import {
    esc as escapeHtml,
    renderPublicSiteNav,
} from "./shared/viewer-utils";

(function () {
    const params = new URLSearchParams(window.location.search);
    const leagueId = params.get("leagueId") || params.get("league_id") || params.get("leagueid") || params.get("id");
    let activeSection = params.get("section") || (params.get("gameDay") ? `game-day-${params.get("gameDay")}` : "auto");
    let activeLeagueRound = params.get("round") || "auto";
    let activeDivisionIndex = Number(params.get("division") || params.get("division_index") || params.get("divisionIndex") || 0) || 0;
    const theme = params.get("theme") === "transparent" ? "transparent" : "full";
    const density = params.get("density") || "auto";
    const compactMode = density === "compact" || params.get("compact") === "1" || params.get("compact") === "true";
    const refreshMs = Math.max(5000, Number(params.get("refresh") || params.get("poll") || 10000));
    const liveMode = String(params.get("live") || "poll").toLowerCase();
    const isViewerOverviewPage = location.pathname.includes("/viewer/overview");
    const isViewerMatchesPage = location.pathname.includes("/viewer/matches");
    const isViewerLadderOverview = location.pathname.includes("/viewer/ladder");
    const isViewerBracketPage = location.pathname.includes("/viewer/bracket");
    const isViewerPublicPage = isViewerOverviewPage || isViewerMatchesPage || isViewerLadderOverview || isViewerBracketPage;
    const isViewerObsSource = isViewerPublicPage && /^(obs|source|card)$/i.test(String(params.get("source") || params.get("view") || ""));
    let savedOverlayStyle = null;
    let customOverlayFonts = [];
    const PUBLIC_LEAGUE_API = leagueId ? `/api/public/leagues/${encodeURIComponent(leagueId)}` : "";
    const PUBLIC_SNAPSHOT_API = leagueId ? `${PUBLIC_LEAGUE_API}/snapshot` : "";
    /** @type {any} */
    let currentSnapshot = null;
    let fontsLoaded = false;
    let fontsLoadingPromise = null;
    let overlayStyleLoadingPromise = null;
    let cachedFonts = [];
    // Prevent duplicate polling loops if this script is loaded/evaluated more than once.
    window.__liveBracketOverlay = window.__liveBracketOverlay || {
        started: false,
        intervals: [],
        lastOverlayStyleRefresh: 0,
    };
    window.__liveBracketOverlay.intervals.forEach(clearInterval);
    window.__liveBracketOverlay.intervals = [];
    const overlayFonts = {
        "Arial": "Arial, Helvetica, sans-serif",
        "Arial Black": "'Arial Black', Arial, sans-serif",
        "Impact": "Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif",
        "Trebuchet MS": "'Trebuchet MS', Arial, sans-serif",
        "Verdana": "Verdana, Geneva, sans-serif",
        "Georgia": "Georgia, serif",
        "Courier New": "'Courier New', monospace",
    };
    const OVERLAY_STYLE_ALIASES = {
        textColor: "text_color",
        bracketBgColor: "bracket_bg_color",
        boColor: "bo_color",
        roundHighlightColor: "round_highlight_color",
        bracketLineColor: "bracket_line_color",
        gameTitleColor: "game_title_color",
        matchTitleColor: "match_title_color",
        cardAccentColor: "card_accent_color",
        cardBgColor: "card_bg_color",
        cardAccentWidth: "card_accent_width",
        bgPattern: "bg_pattern",
        pillPattern: "pill_pattern",
        patternMotion: "pattern_motion",
        pillBgColor: "pill_bg_color",
        pillTextColor: "pill_text_color",
        winnerPillColor: "winner_pill_color",
        textSizeStep: "text_size_step",
        boxRadius: "box_radius",
        matchHubTranslucent: "match_hub_translucent",
        showRoundHighlights: "show_round_highlights",
    };
    function overlayStyleValue(name, fallback = "") {
        const alias = OVERLAY_STYLE_ALIASES[name];
        const value = savedOverlayStyle?.[name] ?? (alias ? savedOverlayStyle?.[alias] : undefined) ?? params.get(name);
        return value ?? fallback;
    }
    function applyOverlayCustomStyle() {
        const root = document.documentElement;
        const font = overlayStyleValue("font", "Arial Black");
        const textColor = overlayStyleValue("textColor", "#ffffff");
        const bracketBgColor = overlayStyleValue("bracketBgColor", "#111733");
        const boColor = overlayStyleValue("boColor", "#54d7ff");
        const roundHighlightColor = overlayStyleValue("roundHighlightColor", "#54d7ff");
        const bracketLineColor = overlayStyleValue("bracketLineColor", "#54d7ff");
        const gameTitleColor = overlayStyleValue("gameTitleColor", "#b8bfd6");
        const matchTitleColor = overlayStyleValue("matchTitleColor", "#b8bfd6");
        const cardAccentColor = overlayStyleValue("cardAccentColor", roundHighlightColor);
        const cardBgColor = overlayStyleValue("cardBgColor", "#030816");
        const cardAccentWidth = overlayStyleValue("cardAccentWidth", "5");
        const bgPattern = overlayStyleValue("bgPattern", "default");
        const pillPattern = overlayStyleValue("pillPattern", "default");
        const pillBgColor = overlayStyleValue("pillBgColor", "#10162d");
        const pillTextColor = overlayStyleValue("pillTextColor", "#ffffff");
        const patternMotion = overlayStyleValue("patternMotion", "moving");
        const matchHubTranslucent = overlayStyleValue("matchHubTranslucent", "0");
        const showRoundHighlights = overlayStyleValue("showRoundHighlights", "1");
        const winnerPillColor = overlayStyleValue("winnerPillColor", "#ffe08a");
        const textSizeStep = Math.max(-2, Math.min(2, Number(overlayStyleValue("textSizeStep", "0")) || 0));
        const boxRadius = Math.max(0, Math.min(24, Number(overlayStyleValue("boxRadius", "0")) || 0));
        if (font && overlayFonts[font])
            root.style.setProperty("--overlay-font", overlayFonts[font]);
        else if (font)
            root.style.setProperty("--overlay-font", `'${font}', Arial, Helvetica, sans-serif`);
        if (textColor)
            root.style.setProperty("--text", textColor);
        if (bracketBgColor) {
            root.style.setProperty("--overlay-bg-color", bracketBgColor);
        }
        if (boColor)
            root.style.setProperty("--bo-color", boColor);
        if (roundHighlightColor)
            root.style.setProperty("--round-highlight", roundHighlightColor);
        if (bracketLineColor)
            root.style.setProperty("--line", bracketLineColor);
        if (gameTitleColor)
            root.style.setProperty("--game-title-color", gameTitleColor);
        if (matchTitleColor)
            root.style.setProperty("--match-title-color", matchTitleColor);
        if (cardAccentColor)
            root.style.setProperty("--card-accent-color", cardAccentColor);
        if (cardBgColor)
            root.style.setProperty("--bracket-card-bg", cardBgColor);
        if (cardAccentWidth !== "")
            root.style.setProperty("--card-accent-width", `${cardAccentWidth}px`);
        if (pillBgColor)
            root.style.setProperty("--pill-bg-color", pillBgColor);
        if (pillTextColor)
            root.style.setProperty("--pill-text-color", pillTextColor);
        if (winnerPillColor)
            root.style.setProperty("--winner", winnerPillColor);
        const fontScale = `clamp(${(0.72 + textSizeStep * 0.07).toFixed(2)}rem, ${(0.85 + textSizeStep * 0.14).toFixed(2)}vw, ${(1 + textSizeStep * 0.12).toFixed(2)}rem)`;
        root.style.setProperty("--font-scale", fontScale);
        root.style.setProperty("--overlay-text-size-step", String(textSizeStep));
        root.style.setProperty("--card-radius", `${boxRadius}px`);
        root.style.setProperty("--overlay-card-radius", `${boxRadius}px`);
        document.body.dataset.cardRadius = String(boxRadius);
        let radiusForceStyle = document.getElementById("overlayRadiusForceStyle");
        if (!radiusForceStyle) {
            radiusForceStyle = document.createElement("style");
            radiusForceStyle.id = "overlayRadiusForceStyle";
            document.head.appendChild(radiusForceStyle);
        }
        radiusForceStyle.textContent = `
    .match-card,
    .bracket-board .board-card-slot .match-card,
    .bracket-board.final-eight-board .board-card-slot .match-card {
      border-radius: ${boxRadius}px !important;
      overflow: hidden !important;
    }
    .bracket-board .board-card-slot {
      border-radius: ${boxRadius}px !important;
    }
  `;
        document.body.dataset.bgPattern = bgPattern || "default";
        document.body.dataset.pillPattern = pillPattern || "default";
        document.body.dataset.patternMotion = patternMotion === "static" ? "static" : "moving";
        document.body.classList.toggle("match-hub-translucent", matchHubTranslucent === "1" || matchHubTranslucent === "true");
        document.body.classList.toggle("hide-round-highlights", showRoundHighlights === "0" || showRoundHighlights === "false");
    }
    async function loadCustomOverlayFonts() {
        if (fontsLoaded)
            return customOverlayFonts;
        if (fontsLoadingPromise)
            return fontsLoadingPromise;
        fontsLoadingPromise = (async () => {
            try {
                const cached = sessionStorage.getItem("overlayFontsCache");
                customOverlayFonts = cached ? JSON.parse(cached) : await fetchJson("/api/leagues/fonts");
                if (!cached) {
                    sessionStorage.setItem("overlayFontsCache", JSON.stringify(customOverlayFonts));
                }
                customOverlayFonts.forEach((font) => {
                    if (!font.family ||
                        !font.url ||
                        document.querySelector(`style[data-font-family="${CSS.escape(font.family)}"]`))
                        return;
                    const style = document.createElement("style");
                    style.dataset.fontFamily = font.family;
                    style.textContent =
                        `@font-face{font-family:'${font.family.replace(/'/g, "\\'")}';` +
                            `src:url('${font.url}') format('truetype');font-display:swap;}`;
                    document.head.appendChild(style);
                });
                fontsLoaded = true;
                return customOverlayFonts;
            }
            catch (error) {
                // Do not retry every refresh tick; this was causing a network flood.
                console.warn("Could not load custom overlay fonts:", error);
                customOverlayFonts = [];
                fontsLoaded = true;
                return customOverlayFonts;
            }
            finally {
                fontsLoadingPromise = null;
            }
        })();
        return fontsLoadingPromise;
    }
    async function refreshOverlayStyle(force = false) {
        if (!leagueId)
            return;
        if (overlayStyleLoadingPromise)
            return overlayStyleLoadingPromise;
        const now = Date.now();
        if (!force && now - window.__liveBracketOverlay.lastOverlayStyleRefresh < 30000) {
            applyOverlayCustomStyle();
            return;
        }
        overlayStyleLoadingPromise = (async () => {
            try {
                await loadCustomOverlayFonts();
                if (currentSnapshot?.overlayStyle) {
                    savedOverlayStyle = currentSnapshot.overlayStyle;
                }
                else {
                    savedOverlayStyle = await fetchJson(`${PUBLIC_LEAGUE_API}/overlay-style`);
                }
                window.__liveBracketOverlay.lastOverlayStyleRefresh = Date.now();
                applyOverlayCustomStyle();
            }
            catch (error) {
                console.warn("Could not refresh overlay style:", error);
                applyOverlayCustomStyle();
            }
            finally {
                overlayStyleLoadingPromise = null;
            }
        })();
        return overlayStyleLoadingPromise;
    }
    document.body.classList.add(`theme-${theme}`);
    document.body.classList.toggle("viewer-round-overview-page", isViewerPublicPage);
    document.body.classList.toggle("viewer-ladder-overview-page", isViewerLadderOverview);
    document.body.classList.toggle("viewer-obs-source-page", isViewerObsSource);
    document.body.classList.toggle("fullpage-bracket-hero", location.pathname.includes("/viewer/bracket") && params.get("watermark") === "1");
    if (compactMode || window.innerHeight <= 760)
        document.body.classList.add("compact");
    applyOverlayCustomStyle();
    const leagueLogo = document.getElementById("leagueLogo");
    const leagueName = document.getElementById("leagueName");
    const leagueMeta = document.getElementById("leagueMeta");
    const overlayTitle = document.getElementById("overlayTitle");
    const overlayKicker = document.getElementById("overlayKicker");
    const sectionTabs = document.getElementById("sectionTabs");
    const bracketStage = document.getElementById("bracketStage");
    const overlayMenu = document.getElementById("overlayMenu");
    const overlayMenuBtn = document.getElementById("overlayMenuBtn");
    const overlayMenuPanel = document.getElementById("overlayMenuPanel");
    function setOverlayMenuOpen(isOpen) {
        if (!overlayMenuPanel || !overlayMenuBtn)
            return;
        overlayMenuPanel.hidden = !isOpen;
        overlayMenuBtn.setAttribute("aria-expanded", String(isOpen));
        overlayMenu?.classList.toggle("open", isOpen);
    }
    overlayMenuBtn?.addEventListener("click", (event) => {
        event.stopPropagation();
        setOverlayMenuOpen(overlayMenuPanel?.hidden !== false);
    });
    document.addEventListener("click", (event) => {
        const target = event.target instanceof Node ? event.target : null;
        if (!target || !overlayMenu?.contains(target))
            setOverlayMenuOpen(false);
    });
    document.addEventListener("keydown", (event) => {
        if (event.key === "Escape")
            setOverlayMenuOpen(false);
    });
    /** @type {(Bracketr.League & Record<string, any>) | null} */
    let currentLeague = null;
    /** @type {Array<Bracketr.Match & Record<string, any>>} */
    let currentMatches = [];
    /** @type {any} */
    let currentStandings = null;
    let panState = { x: 0, y: 0, scale: 1 };
    let isPanning = false;
    let panStart = { x: 0, y: 0 };
    let panOrigin = { x: 0, y: 0 };
    function formatDate(value) {
        if (!value)
            return "";
        const [y, m, d] = String(value).split("-");
        return y && m && d ? `${d}/${m}/${y}` : value;
    }
    function formatLeagueFormat(format) {
        if (format === "single_elim")
            return "Single Elimination";
        if (format === "double_elim")
            return "Double Elimination";
        if (format === "gauntlet")
            return "Gauntlet Bracket";
        if (format === "group_stage")
            return "Group Stage";
        if (format === "final_eight" || format === "final8")
            return "Final Eight";
        if (format === "swiss")
            return "Swiss";
        if (format === "league")
            return "Round Robin";
        return "League";
    }
    function formatViewerHeroFormat(format) {
        return String(format || "")
            .replace(/[_-]+/g, " ")
            .replace(/\s+/g, " ")
            .trim()
            .replace(/\b\w/g, (char) => char.toUpperCase());
    }
    async function fetchJson(url) {
        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok)
            throw new Error(await res.text());
        return res.json();
    }
    async function loadOverlay() {
        if (!leagueId) {
            bracketStage.innerHTML = `<div class="empty-card">Missing leagueId in URL.</div>`;
            return;
        }
        try {
            currentSnapshot = await fetchJson(PUBLIC_SNAPSHOT_API);
        }
        catch (error) {
            currentSnapshot = null;
        }
        if (currentSnapshot?.ok) {
            currentLeague = currentSnapshot.league;
            currentMatches = Array.isArray(currentSnapshot.matches) ? currentSnapshot.matches : [];
            currentStandings = currentSnapshot.standings || null;
        }
        else {
            const [leagues, matches, standingsResult] = await Promise.all([
                fetchJson(PUBLIC_LEAGUE_API),
                fetchJson(`${PUBLIC_LEAGUE_API}/matches`),
                fetchJson(`${PUBLIC_LEAGUE_API}/standings`).catch(() => null),
            ]);
            currentLeague = leagues;
            currentMatches = Array.isArray(matches) ? matches : [];
            currentStandings = standingsResult;
        }
        try {
            const [freshLeague, freshStandings] = await Promise.all([
                fetchJson(PUBLIC_LEAGUE_API),
                fetchJson(`${PUBLIC_LEAGUE_API}/standings`),
            ]);
            currentLeague = freshLeague;
            currentStandings = freshStandings;
        }
        catch (error) {
            console.warn("Failed to refresh standings metadata:", error);
        }
        // The live overlay must reflect admin-side changes immediately.
        // Snapshots can briefly lag after broadcaster/time edits, so always refresh
        // the match list directly before rendering.
        // Only bypass snapshot when explicitly needed.
        if (!currentSnapshot?.ok) {
            try {
                const freshMatches = await fetchJson(`${PUBLIC_LEAGUE_API}/matches`);
                if (Array.isArray(freshMatches))
                    currentMatches = freshMatches;
            }
            catch (error) {
                console.warn("Failed to load matches:", error);
            }
        }
        if (!currentLeague) {
            bracketStage.innerHTML = `<div class="empty-card">League not found.</div>`;
            return;
        }
        renderHeader();
        renderSections();
        renderBracket();
    }
    function renderHeader() {
        leagueLogo.innerHTML = currentLeague.league_logo_path ? `<img src="${escapeHtml(currentLeague.league_logo_path)}" alt="">` : "";
        leagueName.textContent = currentLeague.league_name || "League";
        const meta = [formatLeagueFormat(currentLeague.format)];
        if (currentLeague.tournament_date)
            meta.push(formatDate(currentLeague.tournament_date));
        if (currentLeague.prize_pool)
            meta.push(`Prize Pool: ${currentLeague.prize_pool}`);
        leagueMeta.textContent = meta.join(" • ");
        const title = params.get("title") || currentLeague?.game_title || "LIVE EVENT";
        const gameLogo = currentLeague?.game_logo_path
            ? `<img class="game-title-logo" src="${escapeHtml(currentLeague.game_logo_path)}" alt="" onerror="this.replaceWith(Object.assign(document.createElement('span'),{className:'game-title-logo-fallback',textContent:'RL'}))">`
            : `<span class="game-title-logo-fallback">RL</span>`;
        overlayTitle.innerHTML = `${gameLogo}<span>${escapeHtml(title)}</span>`;
    }
    function isFinalsOnly() {
        return currentMatches.some((m) => Number(m.is_finals) === 1 || String(m.phase || "").includes("finals"));
    }
    function isFinalsRoundIndex(roundIndex) {
        const name = String(getRoundDisplayName(roundIndex) || "").toLowerCase();
        return name.includes("quarter") || name.includes("semi") || name.includes("grand final") || name === "final" || name.includes(" finals");
    }
    function getFinalsRoundIndexes(roundIndexes) {
        return roundIndexes.filter((round) => isFinalsRoundIndex(round));
    }
    function getSections() {
        const sections = [];
        const hasWinner = currentMatches.some((m) => ["winner", "finals_winner"].includes(m.bracket_type));
        const hasLoser = currentMatches.some((m) => ["loser", "finals_loser"].includes(m.bracket_type));
        const hasFinal = currentMatches.some((m) => ["final", "finals_final"].includes(m.bracket_type));
        const hasLeague = currentMatches.some(isLeagueTableMatch);
        const playoffMatches = getPlayoffMatches(currentMatches);
        const hasPlayoffs = playoffMatches.length > 0;
        if (hasLeague) {
            const gameDays = getAvailableGameDays();
            const dateGroups = getLeagueFixtureDateGroups();
            sections.push({ id: "all", label: "Fixtures + Standings" });
            gameDays.forEach((day) => {
                sections.push({ id: `game-day-${day}`, label: `Game Day ${day}`, gameDay: day });
            });
            dateGroups.forEach((group) => {
                sections.push({ id: `date-${group.date}`, label: formatDate(group.date), date: group.date, rounds: group.rounds });
            });
            if (gameDays.length)
                sections.push({ id: "all-game-days", label: "All Game Days" });
            sections.push({ id: "fixtures-overall", label: "All Rounds" });
            sections.push({ id: "fixtures-rounds", label: "Round By Round" });
            sections.push({ id: "standings", label: "Standings" });
            if (hasPlayoffs) {
                sections.push({ id: "playoffs", label: "Playoffs" });
                if (playoffMatches.some((m) => m.bracket_type === "finals_winner"))
                    sections.push({ id: "playoff-winner", label: "Finals Winners" });
                if (playoffMatches.some((m) => m.bracket_type === "finals_loser"))
                    sections.push({ id: "playoff-loser", label: "Finals Losers" });
                if (getPlayoffFinalMatches(playoffMatches).length)
                    sections.push({ id: "playoff-final", label: "Final Overlay" });
                getRoundIndexes(playoffMatches).forEach((round) => {
                    const name = getRoundDisplayNameForMatches(round, playoffMatches) || `Finals Round ${round + 1}`;
                    sections.push({ id: `playoff-round-${round}`, label: formatRoundLabelWithDateForMatches(round, name, playoffMatches) });
                });
            }
            return sections;
        }
        // Large brackets become unreadable when every column is forced onto a 720p/1080p overlay.
        // These broadcast section pills let the caster show clean chunks instead of one crushed view.
        const playable = getRenderableMatches(currentMatches);
        const roundIndexes = getRoundIndexes(playable);
        if (currentLeague.format === "double_elim" || hasLoser) {
            sections.push({ id: "all", label: "Full" });
            if (hasWinner)
                sections.push({ id: "winner", label: "Winners" });
            if (hasLoser)
                sections.push({ id: "loser", label: "Losers" });
            if (hasFinal)
                sections.push({ id: "final", label: "Grand Final" });
        }
        else if (roundIndexes.length) {
            sections.push({ id: "all", label: "Full" });
        }
        if (roundIndexes.length > getMaxReadableColumns()) {
            buildBracketChunks(roundIndexes).forEach((chunk, index) => {
                sections.push({
                    id: `chunk-${index}`,
                    label: formatChunkLabel(chunk),
                    rounds: chunk,
                });
            });
        }
        const finalsRounds = getFinalsRoundIndexes(roundIndexes);
        if (finalsRounds.length >= 2) {
            sections.push({ id: "finals-run", label: "QF + SF + GF", rounds: finalsRounds });
        }
        roundIndexes.forEach((round) => {
            const name = getRoundDisplayName(round) || `Round ${round + 1}`;
            sections.push({ id: `round-${round}`, label: formatRoundLabelWithDate(round, name) });
        });
        if (hasFinal && !sections.some((s) => s.id === "final"))
            sections.push({ id: "final", label: "Grand Final" });
        return sections;
    }
    function getRoundIndexes(matches) {
        return [...new Set(matches.map((m) => Number(m.round_index)).filter((n) => Number.isFinite(n)))]
            .sort((a, b) => a - b);
    }
    function getMaxReadableColumns() {
        if (density === "compact" || window.innerHeight <= 760 || window.innerWidth <= 1400)
            return 3;
        return 4;
    }
    function buildBracketChunks(roundIndexes) {
        const max = getMaxReadableColumns();
        const chunks = [];
        for (let i = 0; i < roundIndexes.length; i += max) {
            chunks.push(roundIndexes.slice(i, i + max));
        }
        return chunks;
    }
    function formatChunkLabel(rounds) {
        if (!rounds.length)
            return "Section";
        if (rounds.length === 1)
            return getRoundDisplayName(rounds[0]) || `Round ${rounds[0] + 1}`;
        const first = getRoundDisplayName(rounds[0]) || `Round ${rounds[0] + 1}`;
        const last = getRoundDisplayName(rounds[rounds.length - 1]) || `Round ${rounds[rounds.length - 1] + 1}`;
        return `${first} - ${last}`;
    }
    function getRoundDisplayName(roundIndex) {
        const match = currentMatches.find((m) => Number(m.round_index) === Number(roundIndex) && m.round_name);
        return match?.round_name || "";
    }
    function getRoundDate(roundIndex, matches = currentMatches) {
        const match = matches.find((m) => Number(m.round_index) === Number(roundIndex) && (m.slot_date || m.match_date || m.date));
        return match ? (match.slot_date || match.match_date || match.date || "") : "";
    }
    function formatRoundLabelWithDate(roundIndex, name) {
        const label = name || getRoundDisplayName(roundIndex) || `Round ${Number(roundIndex) + 1}`;
        const date = getRoundDate(roundIndex);
        return date ? `${label} • ${formatDate(date)}` : label;
    }
    function normalizeOverlayDateKey(value) {
        const raw = String(value || "").trim();
        if (!raw)
            return "";
        const iso = raw.match(/^(\d{4}-\d{2}-\d{2})/);
        if (iso)
            return iso[1];
        const au = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (au)
            return `${au[3]}-${String(au[2]).padStart(2, "0")}-${String(au[1]).padStart(2, "0")}`;
        return raw;
    }
    function getMatchDateKey(match) {
        return normalizeOverlayDateKey(match?.slot_date || match?.match_date || match?.date || getRoundDate(match?.round_index));
    }
    function getLeagueFixtureDateGroups() {
        const groups = new Map();
        getLeagueFixtureMatches(currentMatches).forEach((match) => {
            const dateKey = getMatchDateKey(match);
            if (!dateKey)
                return;
            if (!groups.has(dateKey))
                groups.set(dateKey, new Set());
            groups.get(dateKey).add(Number(match.round_index));
        });
        return [...groups.entries()]
            .sort(([a], [b]) => String(a).localeCompare(String(b)))
            .map(([date, rounds]) => ({
            date,
            rounds: [...rounds].filter(Number.isFinite).sort((a, b) => a - b),
        }));
    }
    function getViewerDivisions() {
        const byIndex = new Map();
        (currentMatches || []).forEach((match) => {
            const isLeagueMatch = isLeagueTableMatch(match);
            if (!isLeagueMatch)
                return;
            const index = Math.max(1, Number(match.division_index || 1) || 1);
            const name = String(match.division_name || `Division ${index}`).trim() || `Division ${index}`;
            if (!byIndex.has(index))
                byIndex.set(index, { index, name });
        });
        return [...byIndex.values()].sort((a, b) => a.index - b.index);
    }
    function getActiveDivisionIndex() {
        const divisions = getViewerDivisions();
        if (!divisions.length)
            return 0;
        if (divisions.some((division) => Number(division.index) === Number(activeDivisionIndex)))
            return Number(activeDivisionIndex);
        activeDivisionIndex = Number(divisions[0].index);
        return activeDivisionIndex;
    }
    function getActiveDivisionName() {
        const active = getActiveDivisionIndex();
        return getViewerDivisions().find((division) => Number(division.index) === Number(active))?.name || "";
    }
    function isMatchInActiveDivision(match) {
        const active = getActiveDivisionIndex();
        if (!active)
            return true;
        return Number(match?.division_index || 1) === Number(active);
    }
    function getActiveDivisionTeamIds(matches = currentMatches) {
        const ids = new Set();
        getLeagueFixtureMatches(matches).forEach((match) => {
            if (match.team1_id)
                ids.add(Number(match.team1_id));
            if (match.team2_id)
                ids.add(Number(match.team2_id));
        });
        return ids;
    }
    function getActiveOverlayDateKey() {
        if (String(activeSection || "").toLowerCase() === "today") {
            const now = new Date();
            const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
            return getLeagueFixtureDateGroups().some((group) => group.date === today) ? today : "";
        }
        return String(activeSection || "").startsWith("date-")
            ? activeSection.replace(/^date-/, "")
            : "";
    }
    function getDateRoundNames(dateKey) {
        const names = [...new Set(getLeagueFixtureMatches(currentMatches)
                .filter((match) => getMatchDateKey(match) === dateKey)
                .map((match) => getRoundDisplayName(match.round_index) || `League Round ${Number(match.round_index) + 1}`))]
            .filter(Boolean);
        return names.join(" + ");
    }
    function compareOverlayDateKeys(a, b) {
        const left = normalizeOverlayDateKey(a);
        const right = normalizeOverlayDateKey(b);
        if (!left || !right)
            return 0;
        return String(left).localeCompare(String(right));
    }
    function getFixtureSchedulerSettings() {
        try {
            return JSON.parse(localStorage.getItem(`bracketr.fixtureScheduler.${leagueId}`) || "{}");
        }
        catch {
            return {};
        }
    }
    function getGameDayAssignments() {
        const settings = getFixtureSchedulerSettings();
        return settings.assignments && typeof settings.assignments === "object" ? settings.assignments : {};
    }
    function getGameDayForMatch(match) {
        const details = getGameDayAssignments()[String(match.id)];
        const day = Number(details?.gameDay);
        return Number.isFinite(day) && day > 0 ? day : null;
    }
    function getAvailableGameDays() {
        const days = [...new Set(getLeagueFixtureMatches(currentMatches).map(getGameDayForMatch).filter(Boolean))]
            .sort((a, b) => a - b);
        return days;
    }
    function getDefaultGameDay() {
        const settings = getFixtureSchedulerSettings();
        const openDay = Number(settings.openGameDay);
        const days = getAvailableGameDays();
        if (days.includes(openDay))
            return openDay;
        const pending = getLeagueFixtureMatches(currentMatches)
            .find((match) => match.status !== "completed" && match.status !== "draw" && match.status !== "bye");
        const pendingDay = pending ? getGameDayForMatch(pending) : null;
        return pendingDay || days[0] || null;
    }
    function getGameDayRoundNames(day) {
        const rounds = [...new Set(getLeagueFixtureMatches(currentMatches)
                .filter((match) => getGameDayForMatch(match) === Number(day))
                .map((match) => getRoundDisplayName(match.round_index) || `League Round ${Number(match.round_index) + 1}`))]
            .filter(Boolean);
        return rounds.join(" + ");
    }
    function renderSections() {
        const sections = getSections();
        sectionTabs.innerHTML = "";
        if (!sections.length)
            return;
        if (activeSection === "auto") {
            const defaultDate = isViewerPublicPage ? getDefaultViewerDateKey() : "";
            const defaultDay = getDefaultGameDay();
            const firstReadableChunk = sections.find((s) => String(s.id).startsWith("chunk-"));
            activeSection = defaultDate ? `date-${defaultDate}` : (defaultDay ? `game-day-${defaultDay}` : (firstReadableChunk ? firstReadableChunk.id : sections[0].id));
        }
        if (!sections.some((s) => s.id === activeSection))
            activeSection = sections[0].id;
        sectionTabs.innerHTML = sections.map((section) => `
    <button class="section-tab ${section.id === activeSection ? "active" : ""}" data-section="${escapeHtml(section.id)}">${escapeHtml(section.label)}</button>
  `).join("");
        sectionTabs.querySelectorAll(".section-tab").forEach((btn) => {
            btn.addEventListener("click", () => {
                activeSection = /** @type {HTMLElement} */ (btn).dataset.section || "auto";
                resetPanState(false);
                const next = new URL(window.location.href);
                next.searchParams.set("section", activeSection);
                window.history.replaceState({}, "", next);
                renderSections();
                renderBracket();
                setOverlayMenuOpen(false);
            });
        });
    }
    function getVisibleMatches() {
        if (activeSection === "standings")
            return [];
        if (activeSection === "playoffs")
            return getPlayoffMatches(currentMatches);
        if (activeSection === "playoff-winner")
            return getPlayoffMatches(currentMatches).filter((m) => m.bracket_type === "finals_winner");
        if (activeSection === "playoff-loser")
            return getPlayoffMatches(currentMatches).filter((m) => m.bracket_type === "finals_loser");
        if (activeSection === "playoff-final")
            return getPlayoffFinalMatches(getPlayoffMatches(currentMatches));
        if (activeSection.startsWith("playoff-round-")) {
            const round = Number(activeSection.replace("playoff-round-", ""));
            return getPlayoffMatches(currentMatches).filter((m) => Number(m.round_index) === round);
        }
        if (activeSection === "fixtures")
            return currentMatches; // legacy link support
        if (activeSection.startsWith("game-day-")) {
            const day = Number(activeSection.replace("game-day-", ""));
            return currentMatches.filter((m) => getGameDayForMatch(m) === day);
        }
        if (activeSection.startsWith("date-")) {
            const dateKey = activeSection.replace(/^date-/, "");
            return currentMatches.filter((m) => getMatchDateKey(m) === dateKey);
        }
        if (activeSection === "all-game-days" || activeSection === "fixtures-overall" || activeSection === "fixtures-rounds")
            return currentMatches;
        if (activeSection.startsWith("round-")) {
            const round = Number(activeSection.replace("round-", ""));
            return currentMatches.filter((m) => Number(m.round_index) === round);
        }
        if (activeSection.startsWith("chunk-") || activeSection === "finals-run") {
            const sections = getSections();
            const section = sections.find((s) => s.id === activeSection);
            const rounds = Array.isArray(section?.rounds) ? section.rounds : [];
            return currentMatches.filter((m) => rounds.includes(Number(m.round_index)));
        }
        if (activeSection === "winner")
            return currentMatches.filter((m) => ["winner", "finals_winner"].includes(m.bracket_type));
        if (activeSection === "loser")
            return currentMatches.filter((m) => ["loser", "finals_loser"].includes(m.bracket_type));
        if (activeSection === "final")
            return currentMatches.filter((m) => ["final", "finals_final"].includes(m.bracket_type));
        return currentMatches;
    }
    function shouldHideOverlayMatch(match) {
        // First-round BYE/TBD games are not real broadcast matches.
        // They only exist so the bracket can push seeded teams forward.
        const isFirstRound = Number(match.round_index) === 0;
        const missingATeam = !match.team1_id || !match.team2_id;
        const isBye = match.status === "bye" || match.win_method === "bye";
        const isPlayoff = String(match.bracket_type || "").startsWith("finals_") || match.phase === "finals" || Number(match.is_finals) === 1;
        return isFirstRound && missingATeam && (isBye || isPlayoff || currentLeague?.format !== "league");
    }
    function getRenderableMatches(matches) {
        return matches.filter((match) => !shouldHideOverlayMatch(match));
    }
    function isLeagueTableFormat() {
        const format = String(currentLeague?.format || "").toLowerCase();
        if (["league", "swiss", "open_ladder"].includes(format))
            return true;
        return currentMatches.some((m) => ["league", "swiss", "open_ladder"].includes(String(m.bracket_type || "").toLowerCase()) ||
            ["league", "swiss", "open_ladder"].includes(String(m.phase || "").toLowerCase()));
    }
    function isOpenLadderFormat() {
        return String(currentLeague?.format || "").trim().toLowerCase() === "open_ladder" ||
            currentMatches.some((match) => ["open_ladder", "ladder"].includes(String(match?.bracket_type || match?.phase || "").trim().toLowerCase()));
    }
    function isLeagueTableMatch(match) {
        return ["league", "swiss", "open_ladder"].includes(String(match?.bracket_type || "").toLowerCase()) ||
            ["league", "swiss", "open_ladder"].includes(String(match?.phase || "").toLowerCase());
    }
    function getBracketViewerMatches(matches = currentMatches) {
        return getRenderableMatches(matches)
            .filter((match) => !isLeagueTableMatch(match))
            .sort(sortMatches);
    }
    function getDefaultViewerDateKey() {
        const groups = getLeagueFixtureDateGroups();
        if (!groups.length)
            return "";
        const pending = getLeagueFixtureMatches(currentMatches).find((match) => !isFinalMatchState(match));
        const pendingDate = pending ? getMatchDateKey(pending) : "";
        return pendingDate || groups[0].date || "";
    }
    function getViewerDateGroups() {
        return getLeagueFixtureDateGroups();
    }
    function getActiveViewerDateKey() {
        const activeDate = getActiveOverlayDateKey();
        return activeDate || getDefaultViewerDateKey();
    }
    function setViewerSection(sectionId) {
        activeSection = sectionId;
        const next = new URL(window.location.href);
        next.searchParams.set("section", activeSection);
        window.history.replaceState({}, "", next);
        renderSections();
        renderBracket();
    }
    function attachViewerRoundControls() {
        bracketStage.querySelectorAll("[data-viewer-section]").forEach((button) => {
            button.addEventListener("click", () => {
                const section = /** @type {HTMLElement} */ (button).dataset.viewerSection;
                if (section)
                    setViewerSection(section);
            });
        });
        bracketStage.querySelectorAll("[data-viewer-match-id]").forEach((card) => {
            card.addEventListener("click", () => {
                const matchId = /** @type {HTMLElement} */ (card).dataset.viewerMatchId || "";
                if (!matchId)
                    return;
                const next = new URL(window.location.href);
                next.searchParams.set("matchId", matchId);
                window.history.replaceState({}, "", next);
                renderBracket();
            });
        });
        bracketStage.querySelector("[data-viewer-match-clear]")?.addEventListener("click", () => {
            const next = new URL(window.location.href);
            next.searchParams.delete("matchId");
            window.history.replaceState({}, "", next);
            renderBracket();
        });
        bracketStage.querySelector("[data-viewer-share]")?.addEventListener("click", () => {
            openViewerShareModal();
        });
        bracketStage.querySelector("[data-viewer-rules]")?.addEventListener("click", () => {
            const rules = String(currentLeague?.rules_text || currentLeague?.rules || "").trim();
            if (!rules)
                return;
            const lines = rules.split(/\r?\n/);
            const sections = [];
            const introLines = [];
            let currentSection = null;
            lines.forEach((line) => {
                const markdownHeading = line.match(/^\s{0,3}(#{1,6})\s+(.+?)\s*#*\s*$/);
                const numberedHeading = line.match(/^\s*(\d+(?:\.\d+)*)\.\s+(.+?)\s*$/);
                if (markdownHeading || numberedHeading) {
                    const headingTitle = markdownHeading
                        ? markdownHeading[2].trim()
                        : `${numberedHeading[1]}. ${numberedHeading[2].trim()}`;
                    const headingLevel = markdownHeading
                        ? markdownHeading[1].length
                        : numberedHeading[1].split(".").length + 1;
                    currentSection = {
                        id: `viewer-rule-section-${sections.length + 1}`,
                        title: headingTitle,
                        level: headingLevel,
                        lines: [],
                    };
                    sections.push(currentSection);
                }
                else if (currentSection) {
                    currentSection.lines.push(line);
                }
                else {
                    introLines.push(line);
                }
            });
            const renderRuleText = (value) => escapeHtml(value.trim()).replace(/\n/g, "<br>");
            const rulesDocument = `
              ${introLines.some((line) => line.trim()) ? `<div class="viewer-rules-intro">${renderRuleText(introLines.join("\n"))}</div>` : ""}
              ${sections.length
                ? sections.map((section) => `
                  <section id="${section.id}" class="viewer-rules-section">
                    <h${Math.min(section.level + 1, 6)}>${escapeHtml(section.title)}</h${Math.min(section.level + 1, 6)}>
                    ${section.lines.some((line) => line.trim()) ? `<div class="viewer-rules-copy">${renderRuleText(section.lines.join("\n"))}</div>` : ""}
                  </section>
                `).join("")
                : `<div class="viewer-rules-copy">${renderRuleText(rules)}</div>`}
            `;
            const existing = document.querySelector("[data-viewer-rules-modal]");
            if (existing)
                existing.remove();
            const modal = document.createElement("div");
            modal.className = "viewer-rules-modal-backdrop";
            modal.dataset.viewerRulesModal = "1";
            modal.innerHTML = `
      <div class="viewer-rules-modal" role="dialog" aria-modal="true" aria-labelledby="viewerRulesTitle">
        <button type="button" data-close-viewer-rules aria-label="Close rules">×</button>
        <p>Rules</p>
        <h2 id="viewerRulesTitle">${escapeHtml(currentLeague?.league_name || "Competition")} Rules</h2>
        <div class="viewer-rules-layout${sections.length ? "" : " viewer-rules-layout-single"}">
          ${sections.length ? `
            <nav class="viewer-rules-index" aria-label="Rules sections">
              <button type="button" class="is-active" data-viewer-rule-target="viewerRulesDocument">
                <span>Full view</span>
                <small>All rules</small>
              </button>
              ${sections.map((section, index) => `
                <button type="button" data-viewer-rule-target="${section.id}">
                  <small>${String(index + 1).padStart(2, "0")}</small>
                  <span>${escapeHtml(section.title)}</span>
                </button>
              `).join("")}
            </nav>
          ` : ""}
          <div id="viewerRulesDocument" class="viewer-rules-document" tabindex="0">
            ${rulesDocument}
          </div>
        </div>
      </div>
    `;
            const rulesDocumentEl = modal.querySelector(".viewer-rules-document");
            const setActiveRuleCard = (targetId, keepVisible = true) => {
                const activeCard = modal.querySelector(`[data-viewer-rule-target="${CSS.escape(targetId)}"]`);
                modal.querySelectorAll("[data-viewer-rule-target]").forEach((item) => item.classList.toggle("is-active", item === activeCard));
                if (keepVisible && activeCard)
                    activeCard.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
            };
            modal.querySelectorAll("[data-viewer-rule-target]").forEach((button) => {
                button.addEventListener("click", () => {
                    const targetId = /** @type {HTMLElement} */ (button).dataset.viewerRuleTarget;
                    const target = targetId ? modal.querySelector(`#${CSS.escape(targetId)}`) : null;
                    if (!target || !rulesDocumentEl)
                        return;
                    setActiveRuleCard(targetId, false);
                    if (target === rulesDocumentEl)
                        rulesDocumentEl.scrollTo({ top: 0, behavior: "smooth" });
                    else
                        target.scrollIntoView({ behavior: "smooth", block: "start" });
                });
            });
            if (rulesDocumentEl && sections.length) {
                const sectionEls = Array.from(modal.querySelectorAll(".viewer-rules-section"));
                let scrollFrame = 0;
                rulesDocumentEl.addEventListener("scroll", () => {
                    if (scrollFrame)
                        return;
                    scrollFrame = requestAnimationFrame(() => {
                        scrollFrame = 0;
                        const readingLine = rulesDocumentEl.scrollTop + 18;
                        const documentTop = rulesDocumentEl.getBoundingClientRect().top;
                        let activeId = "viewerRulesDocument";
                        sectionEls.forEach((section) => {
                            const sectionTop = section.getBoundingClientRect().top - documentTop + rulesDocumentEl.scrollTop;
                            if (sectionTop <= readingLine)
                                activeId = section.id;
                        });
                        setActiveRuleCard(activeId);
                    });
                }, { passive: true });
            }
            modal.addEventListener("click", (event) => {
                const target = event.target instanceof Element ? event.target : null;
                if (target === modal || target?.closest("[data-close-viewer-rules]"))
                    modal.remove();
            });
            document.body.appendChild(modal);
        });
    }
    function viewerShareTitle() {
        const title = currentLeague?.league_name || currentLeague?.name || "Bracketr.gg event";
        const path = String(location.pathname || "");
        if (path.includes("/viewer/matches"))
            return `${title} matches`;
        if (path.includes("/viewer/ladder"))
            return `${title} ${isLeagueTableFormat() ? "standings" : "bracket"}`;
        if (path.includes("/viewer/bracket"))
            return `${title} bracket`;
        return `${title} match centre`;
    }
    function viewerShareText() {
        const title = viewerShareTitle();
        const meta = [
            currentLeague?.game_title || currentLeague?.gameTitle,
            currentLeague?.region,
        ].filter(Boolean).join(" - ");
        return `${title}${meta ? ` - ${meta}` : ""}`;
    }
    function viewerShareTargets(url) {
        const title = viewerShareTitle();
        const text = viewerShareText();
        return [
            ["native", "Device Share", ""],
            ["x", "X", `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`],
            ["facebook", "Facebook", `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`],
            ["whatsapp", "WhatsApp", `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`],
            ["telegram", "Telegram", `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`],
            ["reddit", "Reddit", `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`],
            ["linkedin", "LinkedIn", `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`],
            ["email", "Email", `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`],
        ];
    }
    function openViewerShareModal() {
        const existing = bracketStage.querySelector("[data-viewer-share-modal]");
        if (existing)
            existing.remove();
        const url = window.location.href.split("#")[0];
        const modal = document.createElement("div");
        modal.className = "viewer-rules-modal-backdrop viewer-share-modal-backdrop";
        modal.dataset.viewerShareModal = "1";
        modal.innerHTML = `
      <div class="viewer-rules-modal viewer-share-modal" role="dialog" aria-modal="true" aria-labelledby="viewerShareTitle">
        <button type="button" data-close-viewer-share aria-label="Close share dialog">Ã—</button>
        <p>Share</p>
        <h2 id="viewerShareTitle">${escapeHtml(viewerShareTitle())}</h2>
        <div class="viewer-share-copy">
          <label for="viewerShareUrl">Current section link</label>
          <span>
            <input id="viewerShareUrl" type="text" value="${escapeHtml(url)}" readonly data-viewer-share-url>
            <button type="button" data-copy-viewer-share>Copy</button>
          </span>
        </div>
        <div class="viewer-share-grid">
          ${viewerShareTargets(url).map(([key, label, href]) => key === "native"
            ? `<button type="button" data-native-viewer-share>${escapeHtml(label)}</button>`
            : `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(label)}</a>`).join("")}
        </div>
      </div>
    `;
        const input = modal.querySelector("[data-viewer-share-url]");
        const copyButton = modal.querySelector("[data-copy-viewer-share]");
        const nativeButton = modal.querySelector("[data-native-viewer-share]");
        const close = () => modal.remove();
        modal.addEventListener("click", (event) => {
            const target = event.target instanceof Element ? event.target : null;
            if (target === modal || target?.closest("[data-close-viewer-share]"))
                close();
        });
        copyButton?.addEventListener("click", async () => {
            try {
                await navigator.clipboard.writeText(input?.value || url);
                copyButton.textContent = "Copied";
                setTimeout(() => { copyButton.textContent = "Copy"; }, 1400);
            }
            catch {
                input?.select?.();
            }
        });
        nativeButton?.addEventListener("click", async () => {
            try {
                if (navigator.share)
                    await navigator.share({ title: viewerShareTitle(), text: viewerShareText(), url });
                else if (navigator.clipboard)
                    await navigator.clipboard.writeText(url);
            }
            catch { }
        });
        bracketStage.appendChild(modal);
        input?.select?.();
    }
    function viewerUrl(path, extra = {}) {
        const next = new URL(path, window.location.origin);
        if (leagueId)
            next.searchParams.set("leagueId", leagueId);
        const division = getActiveDivisionIndex();
        if (division)
            next.searchParams.set("division", String(division));
        Object.entries(extra).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== "")
                next.searchParams.set(key, value);
        });
        return `${next.pathname}${next.search}`;
    }
    function renderViewerTopNav(active = "overview") {
        const dateKey = getActiveViewerDateKey();
        const orgHref = orgPublicPath(currentLeague || {});
        const hasRules = Boolean(String(currentLeague?.rules_text || currentLeague?.rules || "").trim());
        const leagueTable = isLeagueTableFormat();
        const ladderLabel = leagueTable ? "Standings" : "Bracket";
        const links = [
            ["hub", "Series Hub", `/viewer?leagueId=${encodeURIComponent(leagueId || "")}`],
            ["overview", "Match Centre", viewerUrl("/viewer/overview", dateKey ? { section: `date-${dateKey}` } : {})],
            ["ladder", ladderLabel, viewerUrl("/viewer/ladder")],
            leagueTable ? ["matches", "Matches", viewerUrl("/viewer/matches", dateKey ? { section: `date-${dateKey}` } : {})] : null,
        ].filter(Boolean);
        return `
    <nav class="viewer-section-nav" aria-label="Viewer sections">
      ${links.map(([key, label, href]) => `<a class="${key === active ? "active" : ""}" href="${escapeHtml(href)}">${escapeHtml(label)}</a>`).join("")}
      ${hasRules ? `<button type="button" data-viewer-rules>Rules</button>` : ""}
      <button type="button" data-viewer-share>Share</button>
      ${orgHref ? `<a href="${escapeHtml(orgHref)}">Org Home</a>` : ""}
    </nav>
  `;
    }
    function slugifyOrgPublicPath(value) {
        return String(value || "")
            .toLowerCase()
            .trim()
            .replace(/&/g, " and ")
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");
    }
    function orgPublicPath(league = {}) {
        const slug = slugifyOrgPublicPath(league.organisation_slug ||
            league.org_slug ||
            league.organization_slug ||
            league.organisation?.slug ||
            league.organization?.slug ||
            league.org?.slug ||
            league.organisation_name ||
            league.org_name ||
            "");
        return slug ? `/${encodeURIComponent(slug)}` : "";
    }
    function renderViewerDivisionNav() {
        const divisions = getViewerDivisions();
        if (divisions.length <= 1)
            return "";
        const active = getActiveDivisionIndex();
        return `
    <nav class="viewer-division-nav" aria-label="Viewer divisions">
      ${divisions.map((division) => {
            const href = viewerUrl(location.pathname, activeSection && activeSection !== "auto" ? { section: activeSection, division: division.index } : { division: division.index });
            return `<a class="${Number(division.index) === Number(active) ? "active" : ""}" href="${escapeHtml(href)}">${escapeHtml(division.name)}</a>`;
        }).join("")}
    </nav>
  `;
    }
    function getViewerHeroMeta() {
        return [
            formatViewerHeroFormat(currentLeague?.format),
            currentLeague?.game_title || "Game",
            currentLeague?.team_size ? `${currentLeague.team_size}v${currentLeague.team_size}` : "",
            currentLeague?.region || "",
        ].filter(Boolean);
    }
    function renderViewerHero(activeNav = "overview") {
        const heroImage = currentLeague?.banner_image_path || currentLeague?.game_banner_path || "";
        const initials = String(currentLeague?.tournament_short_name || currentLeague?.league_name || "L").slice(0, 2).toUpperCase();
        return `
    ${renderPublicSiteNav()}
    <header class="viewer-round-hero" ${heroImage ? `style="--viewer-hero-image:url('${escapeHtml(heroImage)}')"` : ""}>
      <div class="viewer-hero-main">
        ${renderViewerLogo(currentLeague?.league_logo_path, initials, "viewer-league-logo")}
        <div>
          <h1>${escapeHtml(currentLeague?.league_name || "League")}</h1>
          <p>${getViewerHeroMeta().map(escapeHtml).join(" &bull; ")}</p>
          ${currentLeague?.prize_pool ? `<strong class="viewer-prize">${escapeHtml(currentLeague.prize_pool)} Prize Pool</strong>` : ""}
        </div>
      </div>
      ${renderViewerTopNav(activeNav)}
    </header>
  `;
    }
    function renderViewerBracketShell(content) {
        return `
    <section class="viewer-round-page viewer-bracket-page viewer-public-bracket-page">
      ${renderViewerHero("ladder")}
      <div class="viewer-card viewer-public-bracket-card">
        ${content}
      </div>
    </section>
  `;
    }
    function isLiveMatchState(match) {
        return ["active", "in_progress", "live"].includes(String(match?.status || "").toLowerCase());
    }
    function isFinalMatchState(match) {
        const status = String(match?.status || "").toLowerCase();
        return status === "completed" || status === "draw" || status === "bye" || status === "final";
    }
    function isByeMatch(match) {
        return String(match?.status || "").toLowerCase() === "bye" || String(match?.win_method || "").toLowerCase() === "bye";
    }
    function getViewerMatchState(match, firstPendingId) {
        if (isByeMatch(match))
            return "bye";
        if (isLiveMatchState(match))
            return "live";
        if (isFinalMatchState(match))
            return "final";
        if (Number(match?.id) === Number(firstPendingId))
            return "up-next";
        return "upcoming";
    }
    function getViewerRoundLabel(matches) {
        const rounds = [...new Set((matches || [])
                .map((match) => Number(match.round_index))
                .filter(Number.isFinite))]
            .sort((a, b) => a - b);
        if (!rounds.length)
            return "Round";
        if (rounds.length === 1)
            return `Round ${rounds[0] + 1}`;
        return rounds.map((round) => `Round ${round + 1}`).join(" + ");
    }
    function getViewerRoundNames(matches) {
        const names = [...new Set((matches || []).map((match) => match.round_name).filter(Boolean))];
        return names.length ? names.join(" + ") : getViewerRoundLabel(matches);
    }
    function formatViewerLongDate(dateKey) {
        if (!dateKey)
            return "Date TBA";
        const [year, month, day] = String(dateKey).split("-").map(Number);
        if (!year || !month || !day)
            return formatDate(dateKey);
        const date = new Date(Date.UTC(year, month - 1, day));
        return date.toLocaleDateString("en-AU", {
            weekday: "long",
            day: "2-digit",
            month: "long",
            year: "numeric",
            timeZone: "UTC",
        });
    }
    function formatViewerShortDate(dateKey) {
        if (!dateKey)
            return "Date TBA";
        const [year, month, day] = String(dateKey).split("-").map(Number);
        if (!year || !month || !day)
            return formatDate(dateKey);
        return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            timeZone: "UTC",
        });
    }
    function getViewerTeam(match, slot) {
        const fullName = match[`team${slot}_name`];
        const shortName = match[`team${slot}_short_name`];
        const name = String(fullName || shortName || "TBD").trim();
        const logo = match[`team${slot}_logo_path`];
        const id = match[`team${slot}_id`];
        return {
            id,
            name,
            title: fullName || shortName || name,
            logo,
            initials: String(shortName || name || "?").slice(0, 2).toUpperCase(),
            score: Number(match[`team${slot}_score`] ?? 0),
            winner: id && Number(match.winner_id) === Number(id),
        };
    }
    function getViewerByeTeam(match) {
        const teamA = getViewerTeam(match, 1);
        const teamB = getViewerTeam(match, 2);
        if (teamA.id && Number(match.winner_id) === Number(teamA.id))
            return teamA;
        if (teamB.id && Number(match.winner_id) === Number(teamB.id))
            return teamB;
        return teamA.id || teamA.name !== "TBD" ? teamA : teamB;
    }
    function renderViewerLogo(src, initials, className = "viewer-team-logo") {
        return `<span class="${className}">${src ? `<img src="${escapeHtml(src)}" alt="">` : escapeHtml(initials || "?")}</span>`;
    }
    function getViewerCurrentGameLabel(match) {
        if (!isLiveMatchState(match))
            return "";
        try {
            const games = JSON.parse(match.game_scores_json || "[]");
            if (Array.isArray(games) && games.length)
                return `Game ${games.length + 1}`;
        }
        catch { }
        return match.current_game ? `Game ${match.current_game}` : "Live now";
    }
    function renderViewerScore(match, state, teamA, teamB) {
        if (state === "upcoming" || state === "up-next")
            return `<span class="viewer-vs">VS</span>`;
        const aClass = teamA.winner ? "winner" : "";
        const bClass = teamB.winner ? "winner" : "";
        return `
    <span class="viewer-score ${aClass}">${escapeHtml(teamA.score)}</span>
    <span class="viewer-score-sep">${state === "final" ? "-" : "VS"}</span>
    <span class="viewer-score ${bClass}">${escapeHtml(teamB.score)}</span>
  `;
    }
    function getViewerMatchStreamers(match) {
        return Array.isArray(match?.streamers) ? match.streamers.filter(Boolean) : [];
    }
    function viewerStreamerName(streamer) {
        return String(streamer?.streamer_name || streamer?.name || streamer?.handle || streamer?.platform || "").trim();
    }
    function renderViewerStreamerLine(match) {
        const names = getViewerMatchStreamers(match)
            .map(viewerStreamerName)
            .filter(Boolean);
        if (!names.length)
            return "";
        return `<div class="viewer-slot-streamers"><span>Streamer</span><strong>${escapeHtml(names.slice(0, 2).join(", "))}${names.length > 2 ? ` +${escapeHtml(names.length - 2)}` : ""}</strong></div>`;
    }
    function selectedViewerMatchId() {
        return new URLSearchParams(window.location.search).get("matchId") || "";
    }
    function viewerReplayStats(match = {}) {
        return match.replayStats || { uploads: [], games: [], totals: { blue: {}, orange: {}, players: [] }, hasParsedStats: false, mapNames: [] };
    }
    function viewerNum(value, fallback = 0) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
    }
    function formatViewerDuration(seconds = 0) {
        const total = Math.max(0, Number(seconds || 0) || 0);
        if (!total)
            return "";
        const mins = Math.floor(total / 60);
        const secs = Math.floor(total % 60);
        return `${mins}:${String(secs).padStart(2, "0")}`;
    }
    function viewerSafeGames(match) {
        const raw = match?.game_scores_json ?? match?.gameScoresJson ?? match?.game_scores ?? match?.gameScores ?? "[]";
        try {
            if (Array.isArray(raw))
                return raw;
            const parsed = JSON.parse(String(raw || "[]"));
            return Array.isArray(parsed) ? parsed : [];
        }
        catch {
            return [];
        }
    }
    function replayPlayersBySide(stats, side) {
        return (Array.isArray(stats.totals?.players) ? stats.totals.players : [])
            .filter((player) => String(player.team || "").toLowerCase() === side || (side === "orange" ? Number(player.teamNum) === 1 : Number(player.teamNum) === 0));
    }
    function viewerPlayerName(player = {}) {
        return String(player.in_game_name || player.inGameName || player.player_name || player.playerName || player.gamer_tag || player.name || "Player").trim();
    }
    function renderViewerLineup(match, slot, stats) {
        const roster = Array.isArray(match?.[`team${slot}_roster`]) ? match[`team${slot}_roster`] : [];
        const fallbackSide = slot === 1 ? "blue" : "orange";
        const fallbackPlayers = replayPlayersBySide(stats, fallbackSide).map((player) => ({
            player_name: player.name,
            in_game_name: player.name,
            role: `${viewerNum(player.score)} score`,
        }));
        const players = roster.length ? roster : fallbackPlayers;
        const team = getViewerTeam(match, slot);
        return `
      <section class="viewer-lineup-team">
        <div class="viewer-lineup-title">
          ${renderViewerLogo(team.logo, team.initials, "viewer-team-logo viewer-lineup-logo")}
          <div><span>Team Lineup</span><strong>${escapeHtml(team.name)}</strong></div>
        </div>
        ${players.length ? `
          <div class="viewer-lineup-list">
            ${players.map((player) => `
              <div class="viewer-lineup-player">
                <strong>${escapeHtml(viewerPlayerName(player))}</strong>
                <span>${escapeHtml(player.role || player.position || player.primaryId || "")}</span>
              </div>
            `).join("")}
          </div>
        ` : `<div class="viewer-replay-empty">No lineup has been added for this team yet.</div>`}
      </section>`;
    }
    function replayTeamName(match, side) {
        const tracking = match?.replay_tracking || match?.tracking || {};
        const blueId = Number(match?.blue_side_team_id || tracking.blue_side_team_id || tracking.blueTeamId || 0);
        const orangeId = Number(match?.orange_side_team_id || tracking.orange_side_team_id || tracking.orangeTeamId || 0);
        if (side === "blue") {
            if (blueId && Number(match.team2_id || 0) === blueId)
                return getViewerTeam(match, 2).name;
            return getViewerTeam(match, 1).name;
        }
        if (orangeId && Number(match.team1_id || 0) === orangeId)
            return getViewerTeam(match, 1).name;
        return getViewerTeam(match, 2).name;
    }
    function viewerGameScoreRows(match, stats) {
        const replayGames = Array.isArray(stats.games) ? stats.games : [];
        if (replayGames.length) {
            return replayGames.map((game, index) => ({
                game: game.game || index + 1,
                mapName: game.mapName || "",
                durationSeconds: game.durationSeconds || 0,
                leftScore: game.blueScore,
                rightScore: game.orangeScore,
                leftLabel: replayTeamName(match, "blue"),
                rightLabel: replayTeamName(match, "orange"),
            }));
        }
        return viewerSafeGames(match).map((game, index) => ({
            game: game.game || index + 1,
            mapName: game.map || game.mapName || "",
            durationSeconds: game.durationSeconds || game.duration_seconds || 0,
            leftScore: game.team1_score,
            rightScore: game.team2_score,
            leftLabel: getViewerTeam(match, 1).name,
            rightLabel: getViewerTeam(match, 2).name,
        }));
    }
    function renderViewerReplayLeaders(match, stats) {
        const players = Array.isArray(stats.totals?.players) ? stats.totals.players : [];
        const uploads = Array.isArray(stats.uploads) ? stats.uploads : [];
        if (!uploads.length && !players.length)
            return "";
        const topBy = (key) => players
            .filter((player) => viewerNum(player[key]) > 0 || key === "score")
            .sort((a, b) => viewerNum(b[key]) - viewerNum(a[key]) || viewerNum(b.score) - viewerNum(a.score))[0];
        const leaderCard = (label, player, statLabel, key) => `
      <article>
        <span>${escapeHtml(label)}</span>
        <strong>${escapeHtml(player?.name || "TBC")}</strong>
        <small>${escapeHtml(statLabel)}: ${escapeHtml(player ? viewerNum(player[key]) : 0)}${player?.team ? ` &middot; ${escapeHtml(player.team)}` : ""}</small>
      </article>`;
        return `
      <div class="viewer-replay-leaders">
        ${leaderCard("MVP", topBy("score"), "Score", "score")}
        ${leaderCard("Top Scorer", topBy("goals"), "Goals", "goals")}
        ${leaderCard("Defender", topBy("saves"), "Saves", "saves")}
        <article>
          <span>Series</span>
          <strong>${escapeHtml(getViewerTeam(match, 1).score)} - ${escapeHtml(getViewerTeam(match, 2).score)}</strong>
          <small>${escapeHtml(uploads.length)} replay${uploads.length === 1 ? "" : "s"} attached</small>
        </article>
      </div>`;
    }
    function renderViewerReplayDownloads(stats) {
        const uploads = Array.isArray(stats.uploads) ? stats.uploads : [];
        if (!uploads.length)
            return `<div class="viewer-replay-empty">No replay file has been attached to this match yet.</div>`;
        return uploads.map((upload) => `
      <a class="viewer-replay-download" href="${escapeHtml(upload.downloadUrl || upload.storedPath || "#")}" download>
        <span>G${escapeHtml(upload.game || "?")}</span>
        <strong>${escapeHtml(upload.filename || "Replay file")}</strong>
        <small>${escapeHtml(upload.mapName || upload.parsedStatus || "Stored replay")}</small>
      </a>
    `).join("");
    }
    function renderViewerReplayGames(stats) {
        const games = Array.isArray(stats.games) ? stats.games : [];
        if (!games.length)
            return `<div class="viewer-replay-empty">Replay stats will appear here after a replay is parsed.</div>`;
        return games.map((game) => `
      <article class="viewer-replay-game">
        <div>
          <span>Game ${escapeHtml(game.game || "?")}</span>
          <strong>${escapeHtml(game.mapName || "Map TBC")}</strong>
          <small>${escapeHtml(formatViewerDuration(game.durationSeconds) || "Duration TBC")}</small>
        </div>
        <b>${game.blueScore === null || game.blueScore === undefined ? "-" : escapeHtml(game.blueScore)} - ${game.orangeScore === null || game.orangeScore === undefined ? "-" : escapeHtml(game.orangeScore)}</b>
      </article>
    `).join("");
    }
    function renderViewerGameScoreLines(match, stats) {
        const rows = viewerGameScoreRows(match, stats);
        if (!rows.length)
            return `<div class="viewer-replay-empty">No game score lines have been recorded yet.</div>`;
        return rows.map((game) => `
      <article class="viewer-replay-game">
        <div>
          <span>Game ${escapeHtml(game.game || "?")}</span>
          <strong>${escapeHtml(game.leftLabel)} vs ${escapeHtml(game.rightLabel)}</strong>
          <small>${escapeHtml([game.mapName, formatViewerDuration(game.durationSeconds)].filter(Boolean).join(" / ") || "Details TBC")}</small>
        </div>
        <b>${game.leftScore === null || game.leftScore === undefined ? "-" : escapeHtml(game.leftScore)} - ${game.rightScore === null || game.rightScore === undefined ? "-" : escapeHtml(game.rightScore)}</b>
      </article>
    `).join("");
    }
    function renderViewerReplayPlayerTable(stats) {
        const players = Array.isArray(stats.totals?.players) ? stats.totals.players : [];
        if (!players.length)
            return `<div class="viewer-replay-empty">No player box score has been parsed yet.</div>`;
        return `
      <div class="viewer-replay-table">
        <div class="viewer-replay-table-head"><span>Player</span><span>Team</span><span>Score</span><span>G</span><span>A</span><span>Sv</span><span>Sh</span></div>
        ${players.map((player) => `
          <div class="viewer-replay-table-row">
            <strong>${escapeHtml(player.name || "Player")}</strong>
            <span>${escapeHtml(player.team || "-")}</span>
            <span>${escapeHtml(player.score || 0)}</span>
            <span>${escapeHtml(player.goals || 0)}</span>
            <span>${escapeHtml(player.assists || 0)}</span>
            <span>${escapeHtml(player.saves || 0)}</span>
            <span>${escapeHtml(player.shots || 0)}</span>
          </div>
        `).join("")}
      </div>`;
    }
    function renderViewerReplayTeamTotals(match, stats) {
        const blue = stats.totals?.blue || {};
        const orange = stats.totals?.orange || {};
        const blueName = getViewerTeam(match, 1).name;
        const orangeName = getViewerTeam(match, 2).name;
        const stat = (row, key) => escapeHtml(row?.[key] || 0);
        return `
      <div class="viewer-replay-team-totals">
        <article><span>Blue / Team 1</span><strong>${escapeHtml(blueName)}</strong><small>${stat(blue, "shots")} shots · ${stat(blue, "saves")} saves · ${stat(blue, "assists")} assists</small></article>
        <article><span>Orange / Team 2</span><strong>${escapeHtml(orangeName)}</strong><small>${stat(orange, "shots")} shots · ${stat(orange, "saves")} saves · ${stat(orange, "assists")} assists</small></article>
      </div>`;
    }
    function renderViewerMatchDetail(match) {
        if (!match)
            return "";
        if (isByeMatch(match)) {
            const team = getViewerByeTeam(match);
            return `
    <section class="viewer-card viewer-match-detail-card viewer-bye-detail-card">
      <div class="viewer-panel-head">
        <div>
          <h2>${escapeHtml(match.round_name || `Round ${Number(match.round_index || 0) + 1}`)} Bye</h2>
          <p>${escapeHtml(team.name)} advances without a match.</p>
        </div>
      </div>
      <div class="viewer-bye-feature">
        ${renderViewerLogo(team.logo, team.initials, "viewer-team-logo viewer-bye-logo")}
        <div>
          <span>Team with a bye</span>
          <strong>${escapeHtml(team.name)}</strong>
          <small>No match required this round.</small>
        </div>
      </div>
    </section>`;
        }
        const stats = viewerReplayStats(match);
        const teamA = getViewerTeam(match, 1);
        const teamB = getViewerTeam(match, 2);
        const mapNames = Array.isArray(stats.mapNames) ? stats.mapNames : [];
        return `
    <section class="viewer-card viewer-match-detail-card">
      <div class="viewer-panel-head">
        <div>
          <h2>Match ${escapeHtml(match.match_number || match.id)} Stats</h2>
          <p>${escapeHtml(teamA.name)} vs ${escapeHtml(teamB.name)}${mapNames.length ? ` · ${escapeHtml(mapNames.join(", "))}` : ""}</p>
        </div>
        <button type="button" class="viewer-detail-close" data-viewer-match-clear>Close</button>
      </div>
      <div class="viewer-detail-scoreline">
        <strong>${escapeHtml(teamA.name)}</strong>
        <b>${escapeHtml(teamA.score)} - ${escapeHtml(teamB.score)}</b>
        <strong>${escapeHtml(teamB.name)}</strong>
      </div>
      <div class="viewer-lineup-grid">
        ${renderViewerLineup(match, 1, stats)}
        ${renderViewerLineup(match, 2, stats)}
      </div>
      ${renderViewerReplayLeaders(match, stats)}
      ${renderViewerReplayTeamTotals(match, stats)}
      <div class="viewer-replay-grid">
        <section>
          <h3>Game Score Lines</h3>
          ${renderViewerGameScoreLines(match, stats)}
        </section>
        <section>
          <h3>Replay Files</h3>
          ${renderViewerReplayDownloads(stats)}
        </section>
      </div>
      <section class="viewer-replay-players">
        <h3>Player Box Score</h3>
        ${renderViewerReplayPlayerTable(stats)}
      </section>
    </section>`;
    }
    function renderViewerMatchRow(match, firstPendingId) {
        const state = getViewerMatchState(match, firstPendingId);
        if (state === "bye") {
            const team = getViewerByeTeam(match);
            return `
    <article class="viewer-match-row bye" data-viewer-match-id="${escapeHtml(match.id || "")}">
      <div class="viewer-match-time">
        <strong>BYE</strong>
        <span>No Match</span>
      </div>
      <div class="viewer-match-bye-team">
        ${renderViewerLogo(team.logo, team.initials)}
        <strong title="${escapeHtml(team.title)}">${escapeHtml(team.name)}</strong>
        <small>Advances with a bye</small>
      </div>
      <div class="viewer-match-format">
        <strong>Bye</strong>
        <span>${escapeHtml(match.round_name || `Round ${Number(match.round_index || 0) + 1}`)}</span>
      </div>
    </article>
  `;
        }
        const teamA = getViewerTeam(match, 1);
        const teamB = getViewerTeam(match, 2);
        const stateText = state === "up-next" ? "Up Next" : state === "final" ? "Final" : state === "live" ? "Live" : "Upcoming";
        const detail = state === "live" ? getViewerCurrentGameLabel(match) : state === "final" ? "Final" : `Round ${Number(match.round_index || 0) + 1}`;
        return `
    <article class="viewer-match-row ${state}" data-viewer-match-id="${escapeHtml(match.id || "")}">
      <div class="viewer-match-time">
        <strong>${escapeHtml(formatOverlayClock(match.slot_time || match.match_time || match.scheduled_time))}</strong>
        <span>${state === "live" ? `<i></i>` : ""}${escapeHtml(stateText)}</span>
      </div>
      <div class="viewer-match-team team-a ${teamA.winner ? "winner" : ""}">
        ${renderViewerLogo(teamA.logo, teamA.initials)}
        <strong title="${escapeHtml(teamA.title)}">${escapeHtml(teamA.name)}</strong>
      </div>
      <div class="viewer-match-score">${renderViewerScore(match, state, teamA, teamB)}</div>
      <div class="viewer-match-team team-b ${teamB.winner ? "winner" : ""}">
        <strong title="${escapeHtml(teamB.title)}">${escapeHtml(teamB.name)}</strong>
        ${renderViewerLogo(teamB.logo, teamB.initials)}
      </div>
      <div class="viewer-match-format">
        <strong>BO${escapeHtml(match.best_of || currentLeague?.match_series_count || 3)}</strong>
        <span>${escapeHtml(detail)}</span>
      </div>
    </article>
  `;
    }
    function sortViewerScheduleMatches(a, b) {
        const byeA = isByeMatch(a) ? 1 : 0;
        const byeB = isByeMatch(b) ? 1 : 0;
        const timeA = String(a.slot_time || a.match_time || a.scheduled_time || "");
        const timeB = String(b.slot_time || b.match_time || b.scheduled_time || "");
        return Number(a.round_index) - Number(b.round_index) || byeA - byeB || timeA.localeCompare(timeB) || Number(a.match_number) - Number(b.match_number);
    }
    function getViewerTimeSlotKey(match) {
        if (isByeMatch(match))
            return `bye-${Number(match.round_index || 0)}-${Number(match.match_number || match.id || 0)}`;
        return String(match.slot_time || match.match_time || match.scheduled_time || "").slice(0, 5) || `match-${Number(match.match_number || match.id || 0)}`;
    }
    function getViewerTimeSlotState(matches, firstPendingId) {
        if ((matches || []).some(isLiveMatchState))
            return "live";
        if ((matches || []).some((match) => Number(match.id) === Number(firstPendingId)))
            return "up-next";
        if ((matches || []).length && matches.every(isFinalMatchState))
            return "final";
        return "upcoming";
    }
    function renderViewerMatchTile(match, firstPendingId) {
        const state = getViewerMatchState(match, firstPendingId);
        if (state === "bye") {
            const team = getViewerByeTeam(match);
            return `
    <article class="viewer-slot-match bye" data-viewer-match-id="${escapeHtml(match.id || "")}">
      <div class="viewer-slot-round">
        <span>${escapeHtml(match.round_name || `Round ${Number(match.round_index || 0) + 1}`)}</span>
        <strong>BYE</strong>
      </div>
      <div class="viewer-slot-bye">
        ${renderViewerLogo(team.logo, team.initials)}
        <div>
          <strong title="${escapeHtml(team.title)}">${escapeHtml(team.name)}</strong>
          <small>Advances with a bye</small>
        </div>
      </div>
      <div class="viewer-slot-detail">No match required</div>
    </article>
  `;
        }
        const teamA = getViewerTeam(match, 1);
        const teamB = getViewerTeam(match, 2);
        const liveDetail = state === "live" ? getViewerCurrentGameLabel(match) : state === "final" ? "Final" : "";
        const streamerLine = renderViewerStreamerLine(match);
        return `
    <article class="viewer-slot-match ${state}" data-viewer-match-id="${escapeHtml(match.id || "")}">
      <div class="viewer-slot-round">
        <span>${escapeHtml(match.round_name || `Round ${Number(match.round_index || 0) + 1}`)}</span>
        <strong>BO${escapeHtml(match.best_of || currentLeague?.match_series_count || 3)}</strong>
      </div>
      <div class="viewer-slot-versus">
        <div class="viewer-slot-team ${teamA.winner ? "winner" : ""}">
          ${renderViewerLogo(teamA.logo, teamA.initials)}
          <strong title="${escapeHtml(teamA.title)}">${escapeHtml(teamA.name)}</strong>
          ${state === "upcoming" || state === "up-next" ? "" : `<b>${escapeHtml(teamA.score)}</b>`}
        </div>
        <span class="viewer-slot-vs">${state === "final" ? "-" : "VS"}</span>
        <div class="viewer-slot-team right ${teamB.winner ? "winner" : ""}">
          ${state === "upcoming" || state === "up-next" ? "" : `<b>${escapeHtml(teamB.score)}</b>`}
          <strong title="${escapeHtml(teamB.title)}">${escapeHtml(teamB.name)}</strong>
          ${renderViewerLogo(teamB.logo, teamB.initials)}
        </div>
      </div>
      ${streamerLine || (liveDetail ? `<div class="viewer-slot-detail">${escapeHtml(liveDetail)}</div>` : "")}
    </article>
  `;
    }
    function renderViewerTimeSlot(timeKey, matches, firstPendingId) {
        const state = getViewerTimeSlotState(matches, firstPendingId);
        const allByes = matches.length && matches.every(isByeMatch);
        const stateText = allByes ? "Byes" : state === "up-next" ? "Up Next" : state === "final" ? "Final" : state === "live" ? "Live" : "Upcoming";
        const displayTime = timeKey.startsWith("bye-") ? "BYE" : timeKey.startsWith("match-") ? "TBA" : formatOverlayClock(timeKey);
        return `
    <section class="viewer-time-slot ${state}">
      <div class="viewer-match-time viewer-slot-time">
        <strong>${escapeHtml(displayTime)}</strong>
        <span>${state === "live" ? `<i></i>` : ""}${escapeHtml(stateText)}</span>
      </div>
      <div class="viewer-slot-matches">
        ${matches.map((match) => renderViewerMatchTile(match, firstPendingId)).join("")}
      </div>
    </section>
  `;
    }
    function renderViewerTimeSlots(matches, firstPendingId) {
        const groups = new Map();
        matches.slice().sort(sortViewerScheduleMatches).forEach((match) => {
            const key = getViewerTimeSlotKey(match);
            if (!groups.has(key))
                groups.set(key, []);
            groups.get(key).push(match);
        });
        return [...groups.entries()]
            .sort(([a], [b]) => {
            if (a.startsWith("bye-") || b.startsWith("bye-")) {
                if (a.startsWith("bye-") && b.startsWith("bye-"))
                    return a.localeCompare(b);
                return a.startsWith("bye-") ? 1 : -1;
            }
            if (a.startsWith("match-") || b.startsWith("match-"))
                return Number(a.replace("match-", "")) - Number(b.replace("match-", ""));
            return String(a).localeCompare(String(b));
        })
            .map(([timeKey, slotMatches]) => renderViewerTimeSlot(timeKey, slotMatches.sort(sortViewerScheduleMatches), firstPendingId))
            .join("");
    }
    function getRemovedCompetitionTeams() {
        return Array.isArray(currentStandings?.removed_teams) ? currentStandings.removed_teams : [];
    }
    function renderRemovedTeamFooters() {
        return getRemovedCompetitionTeams().map((team) => `
        <div class="standings-removed-team">
          <span class="standings-removed-rank">–</span>
          <span class="team-logo small-logo viewer-ladder-logo">${team.logo_path ? `<img src="${escapeHtml(team.logo_path)}" alt="">` : escapeHtml(String(team.short_name || team.team_name || "?").slice(0, 2).toUpperCase())}</span>
          <span><strong>${escapeHtml(team.team_name || "Removed team")}</strong><small>Removed from league — ${escapeHtml(team.reason || "competition decision")}</small></span>
        </div>
      `).join("");
    }
    function renderViewerLadder() {
        const rows = buildOverlayLeagueLadder(getActiveViewerDateKey());
        if (!rows.length) {
            return `<section class="viewer-card viewer-ladder-card"><div class="viewer-panel-head"><h2>Standings</h2></div><div class="viewer-empty">Standings will appear once results are available.</div></section>`;
        }
        const bottomCount = getViewerBottomCutCount();
        const finalsCut = getViewerFinalsCutConfig();
        const finalsTopCount = getViewerFinalsCutCount();
        const topIndicatorCount = getViewerTopIndicatorCount(finalsCut);
        const topIndicatorLabel = String(finalsCut.topIndicatorLabel || "Top").trim().slice(0, 28) || "Top";
        return `
    <section class="viewer-card viewer-ladder-card">
      <div class="viewer-panel-head">
        <h2>Standings</h2>
        <a href="${escapeHtml(viewerUrl("/viewer/ladder"))}">View Full Standings</a>
      </div>
      <div class="viewer-ladder-table">
        <div class="viewer-ladder-row head">
          <span>#</span><span>Team</span><span>W</span><span>L</span><span>PTS</span><span>DIFF</span>
        </div>
        ${rows.map((row, index) => {
            const rank = index + 1;
            const diff = Number(row.pf || 0) - Number(row.pa || 0);
            const isTop = finalsTopCount > 0 && rank <= finalsTopCount;
            const isTopIndicator = topIndicatorCount > 0 && rank <= topIndicatorCount;
            const isBottom = bottomCount > 0 && rank > rows.length - bottomCount;
            const zone = [
                finalsCut.topEnabled !== false && isTop ? "top-zone" : "",
                finalsCut.topIndicatorEnabled !== false && isTopIndicator ? "has-top-indicator" : "",
                finalsCut.bottomEnabled !== false && isBottom ? "bottom-zone" : "",
            ].filter(Boolean).join(" ");
            const rowStyle = [
                finalsCut.topEnabled !== false && isTop ? `--viewer-ladder-zone-color:${escapeHtml(finalsCut.topColor)}` : "",
                finalsCut.topIndicatorEnabled !== false && isTopIndicator ? `--viewer-ladder-top-indicator-color:${escapeHtml(finalsCut.topIndicatorColor)}` : "",
                finalsCut.bottomEnabled !== false && isBottom ? `--viewer-ladder-zone-color:${escapeHtml(finalsCut.bottomColor)}` : "",
            ].filter(Boolean).join(";");
            return `
            <div class="viewer-ladder-row ${zone}" ${rowStyle ? `style="${rowStyle}"` : ""}>
              <span>${escapeHtml(rank)}</span>
              <span class="viewer-ladder-team" data-top-indicator-label="${escapeHtml(topIndicatorLabel)}">
                ${renderViewerLogo(row.logo, String(row.short || row.name || "?").slice(0, 2).toUpperCase(), "viewer-ladder-logo")}
                <strong>${escapeHtml(row.name)}</strong>
              </span>
              <span>${escapeHtml(row.won)}</span>
              <span>${escapeHtml(row.lost)}</span>
              <span>${escapeHtml(row.points)}</span>
              <span class="${diff > 0 ? "positive" : diff < 0 ? "negative" : ""}">${diff > 0 ? "+" : ""}${escapeHtml(diff)}</span>
            </div>
          `;
        }).join("")}
      </div>
      ${renderRemovedTeamFooters()}
    </section>
  `;
    }
    function renderViewerStatCards() {
        const rows = buildOverlayLeagueLadder(getActiveViewerDateKey());
        const topFor = [...rows].sort((a, b) => Number(b.pf || 0) - Number(a.pf || 0))[0];
        const topStreak = [...rows].sort((a, b) => {
            const aStreak = getWinStreak(a);
            const bStreak = getWinStreak(b);
            return bStreak - aStreak || Number(b.points || 0) - Number(a.points || 0);
        })[0];
        const vods = getViewerVodsForActiveDate();
        return `
    <div class="viewer-stat-grid">
      <article class="viewer-stat-card">
        <span class="viewer-stat-icon">*</span>
        <div>
          <small>Top Scorer</small>
          <strong>${escapeHtml(topFor?.name || "TBA")}</strong>
          <b>${escapeHtml(topFor?.pf || 0)}</b>
          <span>Goals</span>
        </div>
      </article>
      <article class="viewer-stat-card">
        <span class="viewer-stat-icon flame">W</span>
        <div>
          <small>Longest Win Streak</small>
          <strong>${escapeHtml(topStreak?.name || "TBA")}</strong>
          <b>${escapeHtml(topStreak ? getWinStreak(topStreak) : 0)}</b>
          <span>Wins</span>
        </div>
      </article>
      ${renderViewerVodCard(vods)}
    </div>
  `;
    }
    function getViewerVodsForActiveDate() {
        const dateKey = getActiveViewerDateKey();
        const byKey = new Map();
        getLeagueFixtureMatches(currentMatches)
            .filter((match) => !dateKey || getMatchDateKey(match) === dateKey)
            .forEach((match) => {
            (Array.isArray(match.streamers) ? match.streamers : []).forEach((streamer) => {
                const url = String(streamer.vod_url || streamer.vodUrl || "").trim();
                if (!url)
                    return;
                const key = `${streamer.id || streamer.streamer_id || streamer.streamer_name || streamer.gamer_tag || ""}:${url}`;
                if (byKey.has(key))
                    return;
                byKey.set(key, {
                    url,
                    title: streamer.vod_title || streamer.vodTitle || "",
                    streamer: streamer.streamer_name || streamer.gamer_tag || streamer.handle || "Broadcast",
                });
            });
        });
        return [...byKey.values()];
    }
    function renderViewerVodCard(vods = []) {
        return `
      <article class="viewer-stat-card viewer-vod-card">
        <span class="viewer-stat-icon">V</span>
        <div>
          <small>VODs</small>
          ${vods.length
            ? `<div class="viewer-vod-list">${vods.slice(0, 4).map((vod) => `<a href="${escapeHtml(vod.url)}" target="_blank" rel="noopener noreferrer"><strong>${escapeHtml(vod.title || vod.streamer)}</strong><span>${escapeHtml(vod.streamer)}</span></a>`).join("")}</div>`
            : `<strong>TBA</strong><span>Links appear after broadcast</span>`}
        </div>
      </article>`;
    }
    function getWinStreak(row) {
        const form = Array.isArray(row?.form) ? row.form : [];
        let streak = 0;
        for (let index = form.length - 1; index >= 0; index--) {
            if (form[index] !== "W")
                break;
            streak++;
        }
        return streak;
    }
    function getViewerLeagueRounds() {
        const fixtureMatches = getLeagueFixtureMatches(currentMatches);
        return [...new Set(fixtureMatches.map((match) => Number(match.round_index)).filter(Number.isFinite))]
            .sort((a, b) => a - b);
    }
    function getActiveViewerRoundIndex() {
        const rounds = getViewerLeagueRounds();
        const requestedParam = params.get("round");
        if (requestedParam !== null && requestedParam.trim() !== "") {
            const requested = Number(requestedParam);
            if (Number.isFinite(requested) && rounds.includes(requested))
                return requested;
        }
        const roundsWithResults = rounds.filter((round) => getLeagueFixtureMatches(currentMatches)
            .some((match) => Number(match.round_index) === round && isFinalMatchState(match)));
        if (roundsWithResults.length)
            return roundsWithResults[roundsWithResults.length - 1];
        return rounds[0] ?? 0;
    }
    function getViewerMatchesThroughRound(roundIndex = getActiveViewerRoundIndex()) {
        return getLeagueFixtureMatches(currentMatches)
            .filter((match) => Number(match.round_index) <= Number(roundIndex));
    }
    function buildViewerLadderForRound(roundIndex = getActiveViewerRoundIndex()) {
        const table = new Map();
        const removedTeamIds = new Set(getRemovedCompetitionTeams().map((team) => Number(team.old_team_id || 0)).filter(Boolean));
        const activeTeamIds = getActiveDivisionTeamIds(currentMatches);
        (currentLeague?.teams || []).forEach((team) => {
            if (removedTeamIds.has(Number(team.id || 0)))
                return;
            if (activeTeamIds.size && !activeTeamIds.has(Number(team.id)))
                return;
            table.set(Number(team.id), createLadderRow({
                id: team.id,
                name: team.team_name || team.short_name || "Unknown Team",
                short: team.short_name || "",
                logo: team.logo_path || "",
                seed: Number(team.seed || 9999),
            }));
        });
        const matchesThroughRound = getViewerMatchesThroughRound(roundIndex);
        const completedMatchesThroughRound = matchesThroughRound
            .filter((match) => match.team1_id && match.team2_id)
            .filter((match) => !removedTeamIds.has(Number(match.team1_id || 0)) && !removedTeamIds.has(Number(match.team2_id || 0)))
            .filter((match) => isFinalMatchState(match));
        completedMatchesThroughRound.forEach((match) => applyOverlayResult(table, match));
        const rows = [...table.values()].sort((a, b) => sortOverlayLadderRowsForMatches(a, b, completedMatchesThroughRound));
        rows.forEach((row, index) => {
            const rank = index + 1;
            const seed = Number(row.seed || rank);
            const amount = row.played ? Math.abs(seed - rank) : 0;
            row.movementAmount = amount;
            if (!amount)
                row.movement = "same";
            else
                row.movement = seed > rank ? "up" : "down";
        });
        return rows;
    }
    function getViewerFinalsCutConfig() {
        const fallback = {
            cutoffEnabled: true,
            cutoffLabel: "Finals cutoff",
            cutoffColor: "#ffbd4a",
            topIndicatorEnabled: true,
            topIndicatorCount: null,
            topIndicatorLabel: "Top",
            topIndicatorColor: "#24c9ff",
            topEnabled: true,
            topColor: "#1f8f5f",
            bottomEnabled: true,
            bottomColor: "#b93452",
        };
        const normalizeColor = (value, fallbackColor) => /^#[0-9a-f]{6}$/i.test(String(value || "")) ? String(value) : fallbackColor;
        const normalizeCount = (value) => {
            const count = Number(value);
            return Number.isFinite(count) && count >= 0 ? Math.floor(count) : null;
        };
        const normalizeLabel = (value, fallbackLabel) => {
            const label = String(value || "").trim().slice(0, 28);
            return label || fallbackLabel;
        };
        try {
            const raw = currentStandings?.ladder_format || currentLeague?.ladder_format || "";
            const parsed = typeof raw === "string" ? JSON.parse(raw || "{}") : (raw || {});
            const saved = parsed?.finalsCut || parsed?.finals_cut || {};
            return {
                cutoffEnabled: saved.cutoffEnabled !== false,
                cutoffLabel: normalizeLabel(saved.cutoffLabel ?? saved.cutoff_label, fallback.cutoffLabel),
                cutoffColor: normalizeColor(saved.cutoffColor, fallback.cutoffColor),
                topIndicatorEnabled: saved.topIndicatorEnabled !== false,
                topIndicatorCount: normalizeCount(saved.topIndicatorCount ?? saved.top_indicator_count),
                topIndicatorLabel: normalizeLabel(saved.topIndicatorLabel ?? saved.top_indicator_label, fallback.topIndicatorLabel),
                topIndicatorColor: normalizeColor(saved.topIndicatorColor, fallback.topIndicatorColor),
                topEnabled: saved.topEnabled !== false,
                topColor: normalizeColor(saved.topColor, fallback.topColor),
                bottomEnabled: saved.bottomEnabled !== false,
                bottomColor: normalizeColor(saved.bottomColor, fallback.bottomColor),
            };
        }
        catch {
            return fallback;
        }
    }
    function getViewerFinalsCutCount() {
        return Math.max(0, Number(currentLeague?.finals_team_count || currentLeague?.promotion_count || currentStandings?.finals_team_count || 0) || 0);
    }
    function getViewerTopIndicatorCount(finalsCut = getViewerFinalsCutConfig()) {
        const configured = Number(finalsCut?.topIndicatorCount);
        return Number.isFinite(configured) && configured >= 0 ? Math.floor(configured) : getViewerFinalsCutCount();
    }
    function getViewerBottomCutCount() {
        return Math.max(0, Number(currentLeague?.relegation_count || currentStandings?.relegation_count || 0) || 0);
    }
    function renderViewerMovement(row) {
        const direction = String(row?.movement || "same");
        const amount = Number(row?.movementAmount || 0);
        const icon = direction === "up" ? "▲" : direction === "down" ? "▼" : "–";
        const label = direction === "same" || !amount ? "" : `<b>${escapeHtml(amount)}</b>`;
        const title = direction === "up" ? `Up ${amount}` : direction === "down" ? `Down ${amount}` : "No movement";
        return `<span class="viewer-ladder-movement is-${escapeHtml(direction)}" title="${escapeHtml(title)}"><i>${escapeHtml(icon)}</i>${label}</span>`;
    }
    function renderViewerRoundSelect(activeRound) {
        const rounds = getViewerLeagueRounds();
        const total = rounds.length || 1;
        return `
    <label class="viewer-round-select">
      <span>Round ${escapeHtml(Number(activeRound) + 1)} of ${escapeHtml(total)}</span>
      <select data-viewer-ladder-round>
        ${rounds.map((round) => `<option value="${escapeHtml(round)}" ${Number(round) === Number(activeRound) ? "selected" : ""}>Round ${escapeHtml(Number(round) + 1)}</option>`).join("")}
      </select>
    </label>
  `;
    }
    function renderViewerFormBadges(row) {
        const form = Array.isArray(row.form) ? row.form.slice(-2) : [];
        if (!form.length)
            return `<span class="viewer-form-empty">-</span>`;
        return form.map((value) => `<span class="viewer-form-badge ${value === "W" ? "win" : value === "L" || value === "F" ? "loss" : "draw"}">${escapeHtml(value)}</span>`).join("");
    }
    function renderViewerFullLadderTable(rows) {
        const finalsTopCount = getViewerFinalsCutCount();
        const bottomCount = getViewerBottomCutCount();
        const finalsCut = getViewerFinalsCutConfig();
        const topIndicatorCount = getViewerTopIndicatorCount(finalsCut);
        const topIndicatorLabel = String(finalsCut.topIndicatorLabel || "Top").trim().slice(0, 28) || "Top";
        const cutoffLabel = String(finalsCut.cutoffLabel || "Finals cutoff").trim().slice(0, 28) || "Finals cutoff";
        const columns = getPublicLadderColumns({ compact: false });
        const gridTemplate = getLadderColumnGridTemplate(columns, false);
        return `
      <div class="viewer-full-ladder-table">
        <div class="viewer-full-ladder-row head" style="grid-template-columns: ${gridTemplate};">
        ${columns.map((column) => `<span>${escapeHtml(column.label)}</span>`).join("")}
      </div>
      ${rows.map((row, index) => {
            const rank = index + 1;
            const isTop = finalsTopCount > 0 && rank <= finalsTopCount;
            const isTopIndicator = topIndicatorCount > 0 && rank <= topIndicatorCount;
            const isBottom = bottomCount > 0 && rank > rows.length - bottomCount;
            const isCutoff = finalsTopCount > 0 && rank === finalsTopCount && finalsTopCount < rows.length;
            const zone = [
                finalsCut.topEnabled !== false && isTop ? "top-zone" : "",
                finalsCut.topIndicatorEnabled !== false && isTopIndicator ? "has-top-indicator" : "",
                finalsCut.bottomEnabled !== false && isBottom ? "bottom-zone" : "",
                finalsCut.cutoffEnabled !== false && isCutoff ? "has-finals-cutoff" : "",
            ].filter(Boolean).join(" ");
            const rowStyle = [
                finalsCut.topEnabled !== false && isTop ? `--viewer-ladder-zone-color:${escapeHtml(finalsCut.topColor)}` : "",
                finalsCut.topIndicatorEnabled !== false && isTopIndicator ? `--viewer-ladder-top-indicator-color:${escapeHtml(finalsCut.topIndicatorColor)}` : "",
                finalsCut.bottomEnabled !== false && isBottom ? `--viewer-ladder-zone-color:${escapeHtml(finalsCut.bottomColor)}` : "",
                finalsCut.cutoffEnabled !== false && isCutoff ? `--viewer-ladder-cutoff-color:${escapeHtml(finalsCut.cutoffColor)}` : "",
                `grid-template-columns:${gridTemplate}`,
            ].filter(Boolean).join(";");
            return `
          <div class="viewer-full-ladder-row ${zone}" data-cutoff-label="${escapeHtml(cutoffLabel)}" ${rowStyle ? `style="${rowStyle}"` : ""}>
            ${columns.map((column, cellIndex) => {
                const cell = renderLadderCell(column, row, rank, zone, "");
                return column.key === "club" ? cell.replace("<span", `<span data-top-indicator-label="${escapeHtml(topIndicatorLabel)}"`) : cell;
            }).join("")}
          </div>
        `;
        }).join("")}
    </div>
    ${renderRemovedTeamFooters()}
  `;
    }
    function getRoundDateLabel(roundIndex) {
        const match = getLeagueFixtureMatches(currentMatches).find((candidate) => Number(candidate.round_index) === Number(roundIndex));
        const date = match ? getMatchDateKey(match) : "";
        return date ? formatViewerShortDate(date) : "Date TBA";
    }
    function renderViewerLadderSummary(activeRound, rows) {
        const fixtureMatches = getLeagueFixtureMatches(currentMatches);
        const matchesThroughRound = getViewerMatchesThroughRound(activeRound);
        const totalMatches = fixtureMatches.length;
        const playedMatches = matchesThroughRound.filter(isFinalMatchState).length;
        const rounds = getViewerLeagueRounds();
        const completedRounds = rounds.filter((round) => {
            if (Number(round) > Number(activeRound))
                return false;
            const matches = fixtureMatches.filter((match) => Number(match.round_index) === Number(round));
            return matches.length && matches.every(isFinalMatchState);
        }).length;
        const nextRound = rounds.find((round) => Number(round) > Number(activeRound)) ?? rounds.find((round) => Number(round) === Number(activeRound) + 1);
        return `
    <section class="viewer-card viewer-summary-card">
      <div class="viewer-side-head">Standings Summary</div>
      <div class="viewer-summary-grid">
        <article><span>Total Teams</span><strong>${escapeHtml(rows.length)}</strong></article>
        <article><span>Matches Played</span><strong>${escapeHtml(playedMatches)} / ${escapeHtml(totalMatches)}</strong><b>${totalMatches ? ((playedMatches / totalMatches) * 100).toFixed(1) : "0.0"}%</b></article>
        <article><span>Rounds Completed</span><strong>${escapeHtml(completedRounds)} / ${escapeHtml(rounds.length || 1)}</strong></article>
        <article><span>Next Round</span><strong>${nextRound !== undefined ? `Round ${Number(nextRound) + 1}` : "Complete"}</strong><b>${nextRound !== undefined ? escapeHtml(getRoundDateLabel(nextRound)) : ""}</b></article>
      </div>
    </section>
  `;
    }
    function rankRangeLabel(start, end) {
        if (!start || !end || end < start)
            return "";
        return start === end ? `Position ${start}` : `Positions ${start}-${end}`;
    }
    function renderViewerLadderLegend(rows) {
        const total = rows.length;
        const finalsCut = getViewerFinalsCutConfig();
        const finalsTopCount = Math.min(total, getViewerFinalsCutCount());
        const bottomCount = Math.min(total, getViewerBottomCutCount());
        const topIndicatorCount = Math.min(total, getViewerTopIndicatorCount(finalsCut));
        const cutoffLabel = String(finalsCut.cutoffLabel || "Finals cutoff").trim().slice(0, 28) || "Finals cutoff";
        const topLabel = String(finalsCut.topIndicatorLabel || "Top").trim().slice(0, 28) || "Top";
        const rowsOut = [];
        if (finalsCut.topEnabled !== false && finalsTopCount > 0) {
            rowsOut.push({
                kind: "zone",
                color: finalsCut.topColor,
                title: "Finals zone",
                meta: rankRangeLabel(1, finalsTopCount),
                note: "The shaded band on the ladder.",
            });
        }
        if (finalsCut.topIndicatorEnabled !== false && topIndicatorCount > 0 && topIndicatorCount !== finalsTopCount) {
            rowsOut.push({
                kind: "badge",
                color: finalsCut.topIndicatorColor,
                title: `${topLabel} badge`,
                meta: rankRangeLabel(1, topIndicatorCount),
                note: "The small tag shown inside club rows.",
            });
        }
        if (finalsCut.cutoffEnabled !== false && finalsTopCount > 0 && finalsTopCount < total) {
            rowsOut.push({
                kind: "cutoff",
                color: finalsCut.cutoffColor,
                title: cutoffLabel,
                meta: `After position ${finalsTopCount}`,
                note: "The horizontal line across the table.",
            });
        }
        const middleStart = finalsTopCount > 0 ? finalsTopCount + 1 : 1;
        const middleEnd = bottomCount > 0 ? total - bottomCount : total;
        if (middleEnd >= middleStart) {
            rowsOut.push({
                kind: "plain",
                color: "#637187",
                title: finalsTopCount > 0 ? "Outside finals" : "Standard ladder",
                meta: rankRangeLabel(middleStart, middleEnd),
                note: "Rows without a zone band.",
            });
        }
        if (finalsCut.bottomEnabled !== false && bottomCount > 0) {
            rowsOut.push({
                kind: "zone",
                color: finalsCut.bottomColor,
                title: "Bottom zone",
                meta: rankRangeLabel(total - bottomCount + 1, total),
                note: "The lower shaded band on the ladder.",
            });
        }
        return `<div class="viewer-ladder-legend">
          ${rowsOut.map((item) => `<div class="viewer-ladder-legend-item">
            <i class="legend-shape ${escapeHtml(item.kind)}" style="--legend-color:${escapeHtml(item.color)}"></i>
            <span><strong>${escapeHtml(item.title)}</strong><small>${escapeHtml([item.meta, item.note].filter(Boolean).join(" - "))}</small></span>
          </div>`).join("")}
        </div>`;
    }
    function renderViewerLadderSidePanels(activeRound, rows) {
        return `
    <aside class="viewer-ladder-side">
      ${renderViewerLadderSummary(activeRound, rows)}
      <section class="viewer-card viewer-side-card">
        <div class="viewer-side-head">Legend</div>
        ${renderViewerLadderLegend(rows)}
      </section>
      <section class="viewer-card viewer-side-card">
        <div class="viewer-side-head">Point System</div>
        <div class="viewer-points-grid">
          <span>Win <b>${escapeHtml(currentLeague?.points_win ?? 3)} Points</b></span>
          <span>Game Win <b>${escapeHtml(currentLeague?.points_game_win ?? 1)} Point</b></span>
          <span>Draw <b>${escapeHtml(currentLeague?.points_draw ?? 1)} Point</b></span>
          <span>Loss <b>${escapeHtml(currentLeague?.points_loss ?? 0)} Points</b></span>
        </div>
      </section>
      <section class="viewer-card viewer-side-card">
        <div class="viewer-side-head">Tiebreakers</div>
        <ol>
          <li>Head-to-head</li>
          <li>Goal Differential</li>
          <li>Goals For</li>
        </ol>
      </section>
    </aside>
  `;
    }
    function attachViewerLadderControls() {
        attachViewerRoundControls();
        bracketStage.querySelector("[data-viewer-ladder-round]")?.addEventListener("change", (event) => {
            const target = /** @type {HTMLSelectElement} */ (event.target);
            const next = new URL(window.location.href);
            next.searchParams.set("round", target.value);
            window.history.replaceState({}, "", next);
            params.set("round", target.value);
            renderBracket();
        });
    }
    function renderViewerLadderOverview() {
        const activeRound = getActiveViewerRoundIndex();
        const rows = buildViewerLadderForRound(activeRound);
        if (isViewerObsSource) {
            return `
    <section class="viewer-round-page viewer-ladder-page viewer-source-page">
      <section class="viewer-card viewer-ladder-main-card viewer-source-card">
        <div class="viewer-ladder-main-head">
          <h2>Current Standings</h2>
        </div>
        ${rows.length ? renderViewerFullLadderTable(rows) : `<div class="viewer-empty">Standings will appear once results are available.</div>`}
      </section>
    </section>
  `;
        }
        return `
    <section class="viewer-round-page viewer-ladder-page">
      ${renderViewerHero("ladder")}
      <div class="viewer-ladder-layout">
        <section class="viewer-card viewer-ladder-main-card">
          <div class="viewer-ladder-main-head">
            <h2>Standings Through Round ${escapeHtml(Number(activeRound) + 1)}</h2>
            ${renderViewerRoundSelect(activeRound)}
          </div>
          ${rows.length ? renderViewerFullLadderTable(rows) : `<div class="viewer-empty">Standings will appear once results are available.</div>`}
          <footer class="viewer-ladder-updated">Last updated: Live <span aria-hidden="true">&#8635;</span></footer>
        </section>
        ${renderViewerLadderSidePanels(activeRound, rows)}
      </div>
      <div class="viewer-ladder-note">Standings update automatically as matches are completed.</div>
    </section>
  `;
    }
    function getViewerDatePageData() {
        const dateKey = getActiveViewerDateKey();
        const fixtureMatches = getLeagueFixtureMatches(currentMatches)
            .filter((match) => !dateKey || getMatchDateKey(match) === dateKey)
            .sort(sortViewerScheduleMatches);
        const pending = fixtureMatches.find((match) => !isFinalMatchState(match) && !isLiveMatchState(match));
        const bestOf = fixtureMatches.find((match) => match.best_of)?.best_of || currentLeague?.match_series_count || 3;
        const groups = getViewerDateGroups();
        const currentIndex = groups.findIndex((group) => group.date === dateKey);
        return {
            dateKey,
            fixtureMatches,
            firstPendingId: pending?.id || null,
            allComplete: fixtureMatches.length > 0 && fixtureMatches.every(isFinalMatchState),
            bestOf,
            prev: currentIndex > 0 ? groups[currentIndex - 1] : null,
            next: currentIndex >= 0 && currentIndex < groups.length - 1 ? groups[currentIndex + 1] : null,
        };
    }
    function renderViewerDateSelector(data) {
        return `
    <section class="viewer-round-selector">
      <div>
        <strong>${escapeHtml(data.allComplete ? `${getViewerRoundLabel(data.fixtureMatches)} Complete` : getViewerRoundLabel(data.fixtureMatches))}</strong>
        <span>Best of ${escapeHtml(data.bestOf)}</span>
      </div>
      <div class="viewer-date-nav">
        <button type="button" ${data.prev ? `data-viewer-section="date-${escapeHtml(data.prev.date)}"` : "disabled"}>&lsaquo;</button>
        <span>${escapeHtml(formatViewerLongDate(data.dateKey))}</span>
        <button type="button" ${data.next ? `data-viewer-section="date-${escapeHtml(data.next.date)}"` : "disabled"}>&rsaquo;</button>
      </div>
      ${renderViewerDivisionNav()}
    </section>
  `;
    }
    function renderViewerMatchesPanel(data, options = {}) {
        const title = options.title || "Today's Matches";
        const selectedMatch = data.fixtureMatches.find((match) => String(match.id || "") === String(selectedViewerMatchId()))
            || currentMatches.find((match) => String(match.id || "") === String(selectedViewerMatchId()));
        return `
    <section class="viewer-card viewer-matches-card ${options.full ? "viewer-matches-card-full" : ""}">
      <div class="viewer-panel-head">
        <h2>${escapeHtml(title)}</h2>
      </div>
      <div class="viewer-match-list">
        ${data.fixtureMatches.length
            ? renderViewerTimeSlots(data.fixtureMatches, data.firstPendingId)
            : `<div class="viewer-empty">No matches scheduled for this date. Check another round or date.</div>`}
      </div>
    </section>
    ${options.source ? "" : renderViewerMatchDetail(selectedMatch)}
  `;
    }
    function getBracketRoundGroupsForViewer() {
        return groupByRound(getBracketViewerMatches());
    }
    function getActiveBracketRoundIndex(groups = getBracketRoundGroupsForViewer()) {
        const requestedSection = String(activeSection || "");
        const sectionRound = requestedSection.startsWith("round-") ? Number(requestedSection.replace("round-", "")) : NaN;
        if (groups.some((group) => Number(group.roundIndex) === sectionRound))
            return sectionRound;
        const requestedRound = Number(params.get("round"));
        if (groups.some((group) => Number(group.roundIndex) === requestedRound))
            return requestedRound;
        const pendingGroup = groups.find((group) => group.matches.some((match) => !isFinalMatchState(match)));
        return Number(pendingGroup?.roundIndex ?? groups[0]?.roundIndex ?? 0);
    }
    function getBracketViewerData() {
        const groups = getBracketRoundGroupsForViewer();
        const activeRound = getActiveBracketRoundIndex(groups);
        const activeIndex = groups.findIndex((group) => Number(group.roundIndex) === Number(activeRound));
        const activeGroup = groups[activeIndex] || groups[0] || { name: "Round", roundIndex: 0, matches: [] };
        const matches = getBracketViewerMatches();
        const pending = matches.find((match) => !isFinalMatchState(match) && !isLiveMatchState(match));
        const completed = matches.filter(isFinalMatchState).length;
        const live = matches.filter(isLiveMatchState).length;
        const bestOf = activeGroup.matches.find((match) => match.best_of)?.best_of || currentLeague?.match_series_count || 3;
        return {
            groups,
            activeGroup,
            activeRound,
            activeIndex,
            matches,
            firstPendingId: pending?.id || null,
            completed,
            live,
            remaining: Math.max(0, matches.length - completed),
            bestOf,
            prev: activeIndex > 0 ? groups[activeIndex - 1] : null,
            next: activeIndex >= 0 && activeIndex < groups.length - 1 ? groups[activeIndex + 1] : null,
        };
    }
    function isBracketRoundSection() {
        return String(activeSection || "").startsWith("round-");
    }
    function renderViewerBracketSelector(data, options = {}) {
        const singleRoundView = isBracketRoundSection();
        const label = data.activeGroup?.name || `Round ${Number(data.activeRound || 0) + 1}`;
        const complete = data.activeGroup?.matches?.length && data.activeGroup.matches.every(isFinalMatchState);
        const displayLabel = options.allowAll && !singleRoundView ? "All Matches" : label;
        const displaySub = options.allowAll && !singleRoundView ? `${data.groups.length} rounds` : `Best of ${data.bestOf}`;
        return `
    <section class="viewer-round-selector viewer-bracket-selector">
      <div>
        <strong>${escapeHtml(complete && singleRoundView ? `${displayLabel} Complete` : displayLabel)}</strong>
        <span>${escapeHtml(displaySub)}</span>
      </div>
      <div class="viewer-date-nav">
        <button type="button" ${data.prev ? `data-viewer-section="round-${escapeHtml(data.prev.roundIndex)}"` : "disabled"}>&lsaquo;</button>
        <span>${escapeHtml(displayLabel)}</span>
        <button type="button" ${data.next ? `data-viewer-section="round-${escapeHtml(data.next.roundIndex)}"` : "disabled"}>&rsaquo;</button>
      </div>
      <div class="viewer-bracket-selector-stats">
        <span>${escapeHtml(data.completed)} / ${escapeHtml(data.matches.length)} complete</span>
        <span>${escapeHtml(data.remaining)} remaining</span>
      </div>
    </section>
  `;
    }
    function renderViewerBracketRoundFilter(data) {
        const singleRoundView = isBracketRoundSection();
        return `
    <section class="viewer-bracket-round-filter">
      <button type="button" class="${singleRoundView ? "" : "active"}" data-viewer-section="all">All Matches</button>
      ${data.groups.map((group) => `
        <button type="button" class="${singleRoundView && Number(group.roundIndex) === Number(data.activeRound) ? "active" : ""}" data-viewer-section="round-${escapeHtml(group.roundIndex)}">
          ${escapeHtml(group.name || "Round")}
        </button>
      `).join("")}
    </section>
  `;
    }
    function getBracketWinnerMatch() {
        const matches = getBracketViewerMatches();
        const completed = matches
            .filter((match) => isFinalMatchState(match) && match.winner_id)
            .sort((a, b) => {
            const finalA = String(a.bracket_type || "").includes("final") || String(a.round_name || "").toLowerCase().includes("final") ? 1 : 0;
            const finalB = String(b.bracket_type || "").includes("final") || String(b.round_name || "").toLowerCase().includes("final") ? 1 : 0;
            return finalB - finalA || Number(b.round_index || 0) - Number(a.round_index || 0) || Number(b.match_number || 0) - Number(a.match_number || 0);
        });
        const explicitFinal = completed.find((match) => String(match.bracket_type || "").includes("final") ||
            String(match.round_name || "").toLowerCase().includes("final"));
        if (explicitFinal)
            return explicitFinal;
        if (matches.length && matches.every(isFinalMatchState))
            return completed[0] || null;
        return null;
    }
    function getBracketChampionName() {
        const match = getBracketWinnerMatch();
        if (!match?.winner_id)
            return "TBA";
        if (Number(match.winner_id) === Number(match.team1_id))
            return match.team1_name || match.team1_short_name || "TBA";
        if (Number(match.winner_id) === Number(match.team2_id))
            return match.team2_name || match.team2_short_name || "TBA";
        return match.winner_name || match.winner_short_name || "TBA";
    }
    function getBracketTeamRows() {
        const teams = new Map();
        /**
         * @param {any} id
         * @param {any} name
         * @param {any} short
         * @param {any} logo
         * @param {any} [seed]
         */
        const addTeam = (id, name, short, logo, seed = 0) => {
            const key = Number(id);
            if (!key)
                return;
            if (!teams.has(key)) {
                teams.set(key, {
                    id: key,
                    name: name || short || "TBD",
                    short: short || name || "",
                    logo: logo || "",
                    seed: Number(seed || 0),
                    played: 0,
                    won: 0,
                    lost: 0,
                    pf: 0,
                    pa: 0,
                    status: "Waiting",
                    nextMatch: "-",
                });
            }
        };
        (currentLeague?.teams || []).forEach((team) => addTeam(team.id, team.team_name, team.short_name, team.logo_path, team.seed));
        getBracketViewerMatches().forEach((match) => {
            addTeam(match.team1_id, match.team1_name, match.team1_short_name, match.team1_logo_path);
            addTeam(match.team2_id, match.team2_name, match.team2_short_name, match.team2_logo_path);
        });
        const futureIds = new Map();
        const matches = getBracketViewerMatches();
        matches.forEach((match) => {
            if (!isFinalMatchState(match)) {
                [1, 2].forEach((slot) => {
                    const id = Number(match[`team${slot}_id`]);
                    if (id && !futureIds.has(id))
                        futureIds.set(id, match.round_name || `Round ${Number(match.round_index || 0) + 1}`);
                });
                return;
            }
            const team1 = teams.get(Number(match.team1_id));
            const team2 = teams.get(Number(match.team2_id));
            if (!team1 || !team2)
                return;
            const score1 = Number(match.team1_score || 0);
            const score2 = Number(match.team2_score || 0);
            team1.played++;
            team2.played++;
            team1.pf += score1;
            team1.pa += score2;
            team2.pf += score2;
            team2.pa += score1;
            if (Number(match.winner_id) === Number(match.team1_id) || score1 > score2) {
                team1.won++;
                team2.lost++;
            }
            else if (Number(match.winner_id) === Number(match.team2_id) || score2 > score1) {
                team2.won++;
                team1.lost++;
            }
        });
        const championMatch = getBracketWinnerMatch();
        const championId = Number(championMatch?.winner_id || 0);
        const maxLosses = String(currentLeague?.format || "").toLowerCase() === "double_elim" ? 2 : 1;
        teams.forEach((team) => {
            team.nextMatch = futureIds.get(team.id) || "-";
            if (championId && team.id === championId)
                team.status = "Champion";
            else if (futureIds.has(team.id))
                team.status = isLiveMatchState(matches.find((match) => [match.team1_id, match.team2_id].map(Number).includes(team.id))) ? "Live" : "Alive";
            else if (team.lost >= maxLosses || (team.played > 0 && !futureIds.has(team.id)))
                team.status = "Eliminated";
            else
                team.status = "Waiting";
        });
        const statusRank = { Champion: 0, Live: 1, Alive: 2, Waiting: 3, Eliminated: 4 };
        return [...teams.values()].sort((a, b) => (statusRank[a.status] ?? 9) - (statusRank[b.status] ?? 9) ||
            Number(b.won) - Number(a.won) ||
            (Number(b.pf) - Number(b.pa)) - (Number(a.pf) - Number(a.pa)) ||
            Number(a.seed || 999) - Number(b.seed || 999) ||
            String(a.name).localeCompare(String(b.name)));
    }
    function renderBracketStatCards(data) {
        return `
    <div class="viewer-stat-grid viewer-bracket-stats">
      <article class="viewer-stat-card">
        <span class="viewer-stat-icon">#</span>
        <div><small>Champion</small><strong>${escapeHtml(getBracketChampionName())}</strong><b>${escapeHtml(data.completed)}</b><span>Matches Done</span></div>
      </article>
      <article class="viewer-stat-card">
        <span class="viewer-stat-icon flame">VS</span>
        <div><small>Bracket Status</small><strong>${escapeHtml(data.live ? "Live Now" : data.remaining ? "In Progress" : "Complete")}</strong><b>${escapeHtml(data.remaining)}</b><span>Remaining</span></div>
      </article>
    </div>
  `;
    }
    function renderViewerBracketRoundCard(group, firstPendingId) {
        return `
    <section class="viewer-card viewer-bracket-round-card">
      <div class="viewer-panel-head">
        <h2>${escapeHtml(group.name || "Round")}</h2>
        <span>${escapeHtml(group.matches.filter(isFinalMatchState).length)} / ${escapeHtml(group.matches.length)} Complete</span>
      </div>
      <div class="viewer-match-list">
        ${group.matches.length ? renderViewerTimeSlots(group.matches, firstPendingId) : `<div class="viewer-empty">No matches in this round yet.</div>`}
      </div>
    </section>
  `;
    }
    function renderViewerBracketStandingsTable(rows) {
        if (!rows.length)
            return `<div class="viewer-empty">Teams will appear once the bracket is seeded.</div>`;
        return `
    <div class="viewer-bracket-standings-table">
      <div class="viewer-bracket-standings-row head">
        <span>#</span><span>Team</span><span>Status</span><span>W</span><span>L</span><span>Diff</span><span>Next</span>
      </div>
      ${rows.map((row, index) => {
            const diff = Number(row.pf || 0) - Number(row.pa || 0);
            return `
          <div class="viewer-bracket-standings-row is-${escapeHtml(row.status.toLowerCase())}">
            <span>${escapeHtml(index + 1)}</span>
            <span class="viewer-ladder-team">
              ${renderViewerLogo(row.logo, String(row.short || row.name || "?").slice(0, 2).toUpperCase(), "viewer-ladder-logo")}
              <strong>${escapeHtml(row.name)}</strong>
            </span>
            <span><b>${escapeHtml(row.status)}</b></span>
            <span>${escapeHtml(row.won)}</span>
            <span>${escapeHtml(row.lost)}</span>
            <span class="${diff > 0 ? "positive" : diff < 0 ? "negative" : ""}">${diff > 0 ? "+" : ""}${escapeHtml(diff)}</span>
            <span>${escapeHtml(row.nextMatch)}</span>
          </div>
        `;
        }).join("")}
    </div>
  `;
    }
    function renderViewerBracketInfoStrip(data) {
        const teamCount = getBracketTeamRows().length || currentLeague?.teams?.length || 0;
        return `
    <section class="viewer-info-strip viewer-bracket-info-strip">
      <div><strong>Format</strong><span>${escapeHtml(formatLeagueFormat(currentLeague?.format))}</span><span>Best of ${escapeHtml(data.bestOf)}</span></div>
      <div><strong>Teams</strong><span>${escapeHtml(teamCount)} entered</span><span>${escapeHtml(getBracketTeamRows().filter((row) => ["Alive", "Live", "Waiting"].includes(row.status)).length)} active</span></div>
      <div><strong>Progress</strong><span>${escapeHtml(data.completed)} of ${escapeHtml(data.matches.length)} matches complete</span><span>${escapeHtml(data.live)} live now</span></div>
      <div><strong>Prize Pool</strong><b>${escapeHtml(currentLeague?.prize_pool || "-")}</b></div>
    </section>
  `;
    }
    function renderViewerBracketOverview() {
        const data = getBracketViewerData();
        return `
    <section class="viewer-round-page viewer-bracket-page">
      ${renderViewerHero("overview")}
      ${renderViewerBracketSelector(data)}
      <div class="viewer-main-grid viewer-bracket-main-grid">
        ${renderViewerBracketRoundCard(data.activeGroup, data.firstPendingId)}
        <aside class="viewer-side-column">
          ${renderBracketStatCards(data)}
          <section class="viewer-card viewer-bracket-mini-card">
            <div class="viewer-panel-head"><h2>Bracket Progress</h2></div>
            <div class="viewer-bracket-mini-rounds">
              ${data.groups.map((group) => `
                <button type="button" class="${Number(group.roundIndex) === Number(data.activeRound) ? "active" : ""}" data-viewer-section="round-${escapeHtml(group.roundIndex)}">
                  <strong>${escapeHtml(group.name || "Round")}</strong>
                  <span>${escapeHtml(group.matches.filter(isFinalMatchState).length)} / ${escapeHtml(group.matches.length)}</span>
                </button>
              `).join("")}
            </div>
          </section>
        </aside>
      </div>
      ${renderViewerBracketInfoStrip(data)}
    </section>
  `;
    }
    function renderViewerBracketMatchesPage() {
        const data = getBracketViewerData();
        const singleRoundView = isBracketRoundSection();
        const visibleGroups = singleRoundView ? [data.activeGroup].filter((group) => group?.matches?.length) : data.groups;
        return `
    <section class="viewer-round-page viewer-bracket-page viewer-matches-page">
      ${renderViewerHero("matches")}
      ${renderViewerBracketSelector(data, { allowAll: true })}
      ${renderViewerBracketRoundFilter(data)}
      <div class="viewer-bracket-round-list">
        ${visibleGroups.length ? visibleGroups.map((group) => renderViewerBracketRoundCard(group, data.firstPendingId)).join("") : `<section class="viewer-card"><div class="viewer-empty">No matches have been generated yet.</div></section>`}
      </div>
      ${renderViewerBracketInfoStrip(data)}
    </section>
  `;
    }
    function renderViewerBracketStandingsPage() {
        const data = getBracketViewerData();
        const singleRoundView = isBracketRoundSection();
        const visibleGroups = singleRoundView ? [data.activeGroup].filter((group) => group?.matches?.length) : data.groups;
        const progress = data.matches.length ? Math.round((data.completed / data.matches.length) * 100) : 0;
        const championName = getBracketChampionName();
        const bracketStatus = data.live ? "Live now" : data.remaining ? "In progress" : "Complete";
        const bracketMarkup = visibleGroups.length
            ? renderBracketGrid(visibleGroups, ["viewer-public-bracket-grid", singleRoundView ? "sectioned" : ""].filter(Boolean).join(" "))
            : `<div class="viewer-empty">The bracket will appear once matches are generated.</div>`;
        const fullBracketHref = viewerUrl("/viewer/bracket", { section: singleRoundView ? `round-${data.activeRound}` : "all", watermark: "1" });
        return `
    <section class="viewer-round-page viewer-bracket-page viewer-bracket-standings-page">
      ${renderViewerHero("ladder")}
      <div class="viewer-bracket-standings-layout">
        <section class="viewer-card viewer-bracket-canvas-card">
          <div class="viewer-ladder-main-head">
            <div>
              <span class="viewer-bracket-eyebrow">${escapeHtml(singleRoundView ? "Round view" : "Tournament bracket")}</span>
              <h2>${escapeHtml(singleRoundView ? data.activeGroup.name || "Round Bracket" : "The road to the final")}</h2>
            </div>
            <div class="viewer-bracket-progress" style="--bracket-progress:${escapeHtml(progress)}%">
              <span><b>${escapeHtml(progress)}%</b> complete</span>
              <i aria-hidden="true"><em></em></i>
              <small>${escapeHtml(data.completed)} of ${escapeHtml(data.matches.length)} matches</small>
            </div>
          </div>
          <div class="bracket-pan-viewport viewer-public-bracket-viewport" id="bracketPanViewport">
            <div class="bracket-pan-canvas" id="bracketPanCanvas">
              ${bracketMarkup}
            </div>
          </div>
        </section>
        <aside class="viewer-side-column">
          <section class="viewer-card viewer-bracket-outcome-card">
            <div class="viewer-bracket-outcome-top">
              <span class="viewer-bracket-trophy" aria-hidden="true">♛</span>
              <div><small>${escapeHtml(data.remaining ? "Current leader" : "Champion")}</small><strong>${escapeHtml(championName)}</strong></div>
            </div>
            <div class="viewer-bracket-outcome-meta">
              <span><small>Status</small><b class="${data.live ? "is-live" : ""}">${escapeHtml(bracketStatus)}</b></span>
              <span><small>Matches</small><b>${escapeHtml(data.completed)} / ${escapeHtml(data.matches.length)}</b></span>
            </div>
          </section>
          <section class="viewer-card viewer-bracket-mini-card">
            <div class="viewer-panel-head">
              <div><span class="viewer-bracket-eyebrow">Navigate</span><h2>Rounds</h2></div>
              <span>${escapeHtml(data.groups.length)} stages</span>
            </div>
            <div class="viewer-bracket-mini-rounds">
              <button type="button" class="${singleRoundView ? "" : "active"}" data-viewer-section="all">
                <span class="viewer-round-index">All</span>
                <strong>Full Bracket</strong>
                <b>${escapeHtml(data.completed)} / ${escapeHtml(data.matches.length)}</b>
              </button>
              ${data.groups.map((group, index) => `
                <button type="button" class="${singleRoundView && Number(group.roundIndex) === Number(data.activeRound) ? "active" : ""}" data-viewer-section="round-${escapeHtml(group.roundIndex)}">
                  <span class="viewer-round-index">${escapeHtml(String(index + 1).padStart(2, "0"))}</span>
                  <strong>${escapeHtml(group.name || "Round")}</strong>
                  <b>${escapeHtml(group.matches.filter(isFinalMatchState).length)} / ${escapeHtml(group.matches.length)}</b>
                </button>
              `).join("")}
            </div>
            <div class="viewer-bracket-mini-actions">
              <a href="${escapeHtml(fullBracketHref)}" target="_blank" rel="noopener">Open Full Bracket</a>
            </div>
          </section>
          <p class="viewer-bracket-auto-note"><i aria-hidden="true"></i> Live results update this bracket automatically.</p>
        </aside>
      </div>
    </section>
  `;
    }
    function renderViewerMatchesDatePage() {
        const data = getViewerDatePageData();
        if (isViewerObsSource) {
            return `
    <section class="viewer-round-page viewer-matches-page viewer-source-page">
      ${renderViewerMatchesPanel(data, { title: activeSection === "today" ? "Today's Matches" : "All Matches", full: true, source: true })}
    </section>
  `;
        }
        return `
    <section class="viewer-round-page viewer-matches-page">
      ${renderViewerHero("matches")}
      ${renderViewerDateSelector(data)}
      ${renderViewerMatchesPanel(data, { title: "All Matches", full: true })}
      <section class="viewer-info-strip">
        <div><strong>Format</strong><span>${escapeHtml(formatLeagueFormat(currentLeague?.format))}</span><span>Best of ${escapeHtml(data.bestOf)}</span></div>
        <div><strong>Point System</strong><span>Win: ${escapeHtml(currentLeague?.points_win ?? 3)} Points</span><span>Game win: ${escapeHtml(currentLeague?.points_game_win ?? 1)} Point</span><span>Loss: ${escapeHtml(currentLeague?.points_loss ?? 0)} Points</span></div>
        <div><strong>Tiebreakers</strong><span>1. Head-to-head</span><span>2. Goal Differential</span><span>3. Goals For</span></div>
        <div><strong>Prize Pool</strong><b>${escapeHtml(currentLeague?.prize_pool || "-")}</b></div>
      </section>
    </section>
  `;
    }
    function renderViewerRoundOverview(matches) {
        const data = getViewerDatePageData();
        const matchesTitle = isOpenLadderFormat() ? "Upcoming Scheduled Matches" : "Today's Matches";
        if (isViewerObsSource) {
            return `
    <section class="viewer-round-page viewer-source-page">
      ${renderViewerMatchesPanel(data, { title: "Match Centre", full: true, source: true })}
    </section>
  `;
        }
        return `
    <section class="viewer-round-page">
      ${renderViewerHero("overview")}
      ${renderViewerDateSelector(data)}

      <div class="viewer-main-grid">
        ${renderViewerMatchesPanel(data, { title: matchesTitle })}
        <aside class="viewer-side-column">
          ${renderViewerLadder()}
          ${renderViewerStatCards()}
        </aside>
      </div>

      <section class="viewer-info-strip">
        <div><strong>Format</strong><span>${escapeHtml(formatLeagueFormat(currentLeague?.format))}</span><span>Best of ${escapeHtml(data.bestOf)}</span></div>
        <div><strong>Point System</strong><span>Win: ${escapeHtml(currentLeague?.points_win ?? 3)} Points</span><span>Game win: ${escapeHtml(currentLeague?.points_game_win ?? 1)} Point</span><span>Loss: ${escapeHtml(currentLeague?.points_loss ?? 0)} Points</span></div>
        <div><strong>Tiebreakers</strong><span>1. Head-to-head</span><span>2. Goal Differential</span><span>3. Goals For</span></div>
        <div><strong>Prize Pool</strong><b>${escapeHtml(currentLeague?.prize_pool || "-")}</b></div>
      </section>
    </section>
  `;
    }
    function renderBracket() {
        const matches = getRenderableMatches(getVisibleMatches());
        const leagueStyle = isLeagueTableFormat();
        const notifyPublicNavReady = () => document.dispatchEvent(new CustomEvent("bracketr:public-nav-ready"));
        if (leagueStyle && isViewerOverviewPage) {
            bracketStage.innerHTML = renderViewerRoundOverview(matches);
            attachViewerRoundControls();
            notifyPublicNavReady();
            return;
        }
        if (leagueStyle && isViewerMatchesPage) {
            bracketStage.innerHTML = renderViewerMatchesDatePage();
            attachViewerRoundControls();
            notifyPublicNavReady();
            return;
        }
        if (leagueStyle && isViewerLadderOverview) {
            bracketStage.innerHTML = renderViewerLadderOverview();
            attachViewerLadderControls();
            notifyPublicNavReady();
            return;
        }
        if (!leagueStyle && isViewerOverviewPage) {
            bracketStage.innerHTML = renderViewerBracketOverview();
            attachViewerRoundControls();
            notifyPublicNavReady();
            return;
        }
        if (!leagueStyle && isViewerMatchesPage) {
            bracketStage.innerHTML = renderViewerBracketMatchesPage();
            attachViewerRoundControls();
            notifyPublicNavReady();
            return;
        }
        if (!leagueStyle && isViewerLadderOverview) {
            bracketStage.innerHTML = renderViewerBracketStandingsPage();
            attachViewerRoundControls();
            setupBracketPan();
            notifyPublicNavReady();
            return;
        }
        if (leagueStyle && activeSection === "standings") {
            bracketStage.innerHTML = isViewerBracketPage ? renderViewerBracketShell(renderStandingsPanel(false)) : renderStandingsPanel(false);
            if (isViewerBracketPage)
                notifyPublicNavReady();
            return;
        }
        if (!matches.length && !leagueStyle) {
            const empty = `<div class="empty-card">No playable matches to show yet.</div>`;
            bracketStage.innerHTML = isViewerBracketPage ? renderViewerBracketShell(empty) : empty;
            if (isViewerBracketPage)
                notifyPublicNavReady();
            return;
        }
        if (leagueStyle && (activeSection === "all" || activeSection.startsWith("game-day-") || activeSection.startsWith("date-"))) {
            const day = activeSection.startsWith("game-day-") ? Number(activeSection.replace("game-day-", "")) : getDefaultGameDay();
            const dateKey = getActiveOverlayDateKey();
            const title = dateKey ? formatDate(dateKey) : (day ? `Game Day ${day}` : "Current Fixtures");
            const subTitle = dateKey ? getDateRoundNames(dateKey) : (day ? getGameDayRoundNames(day) : "");
            const content = `
      <div class="league-hub">
        <section class="league-fixtures-panel">
          ${renderLeagueFixtureMatrix(matches, title, subTitle)}
        </section>
        ${renderStandingsPanel(true)}
      </div>
    `;
            bracketStage.innerHTML = isViewerBracketPage ? renderViewerBracketShell(content) : content;
            if (isViewerBracketPage)
                notifyPublicNavReady();
            return;
        }
        if (leagueStyle && (activeSection === "fixtures" || activeSection === "all-game-days" || activeSection === "fixtures-overall")) {
            const content = renderLeagueOverallRounds(matches);
            bracketStage.innerHTML = isViewerBracketPage ? renderViewerBracketShell(content) : content;
            if (isViewerBracketPage)
                notifyPublicNavReady();
            return;
        }
        if (leagueStyle && activeSection === "fixtures-rounds") {
            const content = renderLeagueRoundByRound(matches);
            bracketStage.innerHTML = isViewerBracketPage ? renderViewerBracketShell(content) : content;
            attachLeagueRoundTabs();
            if (isViewerBracketPage)
                notifyPublicNavReady();
            return;
        }
        if (leagueStyle && activeSection.startsWith("round-")) {
            const content = `<div class="fixture-list">${matches.map(renderMatchCard).join("")}</div>`;
            bracketStage.innerHTML = isViewerBracketPage ? renderViewerBracketShell(content) : content;
            if (isViewerBracketPage)
                notifyPublicNavReady();
            return;
        }
        const roundGroups = groupByRound(matches);
        const sectionedClass = activeSection.startsWith("chunk-") || activeSection.startsWith("round-") || activeSection.startsWith("playoff-") || activeSection === "final" ? "sectioned" : "";
        // Every tournament bracket view now uses the same pan/zoom shell.
        // Before this, only large full/compact views were draggable, while normal full and section views were stuck.
        const viewportClass = isViewerBracketPage ? "bracket-pan-viewport viewer-public-bracket-viewport" : "bracket-pan-viewport";
        const gridClass = ["pan-grid", sectionedClass, isViewerBracketPage ? "viewer-public-bracket-grid" : ""].filter(Boolean).join(" ");
        const content = `
    <div class="${viewportClass}" id="bracketPanViewport">
      <div class="bracket-pan-canvas" id="bracketPanCanvas">
        ${renderBracketGrid(roundGroups, gridClass)}
      </div>
      ${params.get("watermark") === "1" ? `<img class="bracket-fullpage-watermark" src="/assets/logos/bracketrLogo.png" alt="Bracketr.gg">` : ""}
    </div>
  `;
        bracketStage.innerHTML = isViewerBracketPage ? renderViewerBracketShell(content) : content;
        setupBracketPan();
        if (isViewerBracketPage)
            notifyPublicNavReady();
    }
    function getLeagueFixtureMatches(matches) {
        const source = matches
            .filter(isLeagueTableMatch)
            .filter(isMatchInActiveDivision);
        return withSyntheticViewerByes(source).sort(sortViewerScheduleMatches);
    }
    function viewerTeamId(team = {}) {
        return String(team.id || team.team_id || team.teamId || "").trim();
    }
    function viewerTeamDivisionIndex(team = {}) {
        return Math.max(1, Number(team.division_index || team.divisionIndex || 1) || 1);
    }
    function viewerTeamDivisionName(team = {}) {
        const index = viewerTeamDivisionIndex(team);
        return String(team.division_name || team.divisionName || team.division || team.group_name || team.group || "").trim() || `Division ${index}`;
    }
    function activeViewerDivisionTeams() {
        const teams = Array.isArray(currentLeague?.teams) ? currentLeague.teams : [];
        const active = getActiveDivisionIndex();
        const seen = new Set();
        return teams
            .filter((team) => {
            const id = viewerTeamId(team);
            if (!id || seen.has(id))
                return false;
            seen.add(id);
            return true;
        })
            .filter((team) => !active || viewerTeamDivisionIndex(team) === Number(active))
            .sort((a, b) => Number(a.seed || 9999) - Number(b.seed || 9999) || String(a.team_name || a.short_name || "").localeCompare(String(b.team_name || b.short_name || "")));
    }
    function syntheticViewerByeForTeam(team = {}, roundMatches = [], roundIndex = 0) {
        const first = roundMatches[0] || {};
        const id = viewerTeamId(team);
        const active = getActiveDivisionIndex() || viewerTeamDivisionIndex(team);
        const maxMatchNumber = Math.max(0, ...roundMatches.map((match) => Number(match.match_number || 0) || 0));
        const name = team.team_name || team.name || team.short_name || "Team";
        return {
            id: `viewer-bye-${active}-${roundIndex}-${id || name}`,
            synthetic_bye: true,
            league_id: leagueId,
            round_index: roundIndex,
            round_name: first.round_name || first.roundName || `League Round ${Number(roundIndex) + 1}`,
            match_number: maxMatchNumber + 1,
            team1_id: team.id || team.team_id || null,
            team1_name: name,
            team1_short_name: team.short_name || "",
            team1_logo_path: team.logo_path || team.team_logo_path || team.logo || "",
            team2_id: null,
            team2_name: "",
            winner_id: team.id || team.team_id || null,
            winner_name: name,
            status: "bye",
            win_method: "bye",
            bracket_type: "league",
            phase: "main",
            division_index: active,
            division_name: getActiveDivisionName() || viewerTeamDivisionName(team),
            slot_date: first.slot_date || first.match_date || first.scheduled_at || "",
            slot_time: "",
            streamers: [],
            commentators: [],
        };
    }
    function withSyntheticViewerByes(source = []) {
        const teams = activeViewerDivisionTeams();
        if (teams.length < 3 || teams.length % 2 === 0)
            return source;
        const byRound = new Map();
        source.forEach((match) => {
            if (isByeMatch(match))
                return;
            const round = Number(match.round_index ?? 0);
            if (!Number.isFinite(round))
                return;
            if (!byRound.has(round))
                byRound.set(round, []);
            byRound.get(round).push(match);
        });
        const additions = [];
        byRound.forEach((roundMatches, round) => {
            const explicitBye = source.some((match) => isByeMatch(match) && Number(match.round_index ?? 0) === Number(round));
            if (explicitBye)
                return;
            const playedIds = new Set();
            roundMatches.forEach((match) => {
                [match.team1_id, match.team1Id, match.team2_id, match.team2Id].forEach((value) => {
                    const clean = String(value || "").trim();
                    if (clean)
                        playedIds.add(clean);
                });
            });
            teams
                .filter((team) => {
                const id = viewerTeamId(team);
                return id && !playedIds.has(id);
            })
                .forEach((team) => additions.push(syntheticViewerByeForTeam(team, roundMatches, round)));
        });
        return additions.length ? [...source, ...additions] : source;
    }
    function getPlayoffMatches(matches) {
        return (Array.isArray(matches) ? matches : [])
            .filter((match) => String(match.bracket_type || "").startsWith("finals_") || match.phase === "finals" || Number(match.is_finals) === 1)
            .sort(sortMatches);
    }
    function getPlayoffFinalMatches(matches) {
        const list = getPlayoffMatches(matches);
        const explicitFinals = list.filter((match) => match.bracket_type === "finals_final" || String(match.round_name || "").toLowerCase().includes("grand final"));
        if (explicitFinals.length)
            return explicitFinals.sort(sortMatches);
        const maxRound = Math.max(-1, ...list.map((match) => Number(match.round_index)).filter(Number.isFinite));
        return maxRound >= 0 ? list.filter((match) => Number(match.round_index) === maxRound).sort(sortMatches) : [];
    }
    function getRoundDisplayNameForMatches(roundIndex, matches) {
        const match = (matches || []).find((m) => Number(m.round_index) === Number(roundIndex) && m.round_name);
        return match?.round_name || "";
    }
    function formatRoundLabelWithDateForMatches(roundIndex, name, matches) {
        const label = name || getRoundDisplayNameForMatches(roundIndex, matches) || `Round ${Number(roundIndex) + 1}`;
        const date = getRoundDate(roundIndex, matches);
        return date ? `${label} â€¢ ${formatDate(date)}` : label;
    }
    function getCurrentLeagueRoundIndex(matches) {
        const fixtureMatches = getLeagueFixtureMatches(matches);
        const pending = fixtureMatches.find((match) => match.status !== "completed" && match.status !== "draw" && match.status !== "bye");
        if (pending && Number.isFinite(Number(pending.round_index)))
            return Number(pending.round_index);
        const rounds = getRoundIndexes(fixtureMatches);
        return rounds[0] ?? 0;
    }
    function renderLeagueOverallRounds(matches) {
        const fixtureMatches = getLeagueFixtureMatches(matches);
        const roundGroups = groupByRound(fixtureMatches);
        if (!roundGroups.length)
            return `<div class="empty-card">No fixtures yet.</div>`;
        return `
    <section class="league-rounds-board overall-rounds-board">
      <div class="league-rounds-head">
        <div>
          <div class="panel-heading">All Rounds</div>
          <p>Overall fixture view. Use Round By Round when there are too many matches for one overlay.</p>
        </div>
      </div>
      <div class="league-round-groups">
        ${roundGroups.map((group) => `
          <section class="league-round-group">
            <h2>${escapeHtml(group.name || "Round")}</h2>
            <div class="league-round-card-grid">${group.matches.map(renderMatchCard).join("")}</div>
          </section>
        `).join("")}
      </div>
    </section>
  `;
    }
    function renderLeagueRoundByRound(matches) {
        const fixtureMatches = getLeagueFixtureMatches(matches);
        const roundGroups = groupByRound(fixtureMatches);
        const rounds = getRoundIndexes(fixtureMatches);
        if (!rounds.length)
            return `<div class="empty-card">No fixtures yet.</div>`;
        if (activeLeagueRound === "auto" || !rounds.includes(Number(activeLeagueRound))) {
            activeLeagueRound = String(getCurrentLeagueRoundIndex(fixtureMatches));
        }
        const currentRound = Number(activeLeagueRound);
        const group = roundGroups.find((item) => item.matches.some((match) => Number(match.round_index) === currentRound));
        const visible = group?.matches || [];
        return `
    <section class="league-rounds-board round-by-round-board">
      <div class="league-rounds-head">
        <div>
          <div class="panel-heading">Round By Round</div>
          <p>One clean round at a time for 720p/1080p streams.</p>
        </div>
        <div class="league-round-tabs">
          ${rounds.map((round) => {
            const name = getRoundDisplayName(round) || `Round ${round + 1}`;
            return `<button class="league-round-tab ${Number(activeLeagueRound) === Number(round) ? "active" : ""}" data-league-round="${round}">${escapeHtml(name)}</button>`;
        }).join("")}
        </div>
      </div>
      <section class="league-round-group single-round">
        <h2>${escapeHtml(group?.name || `Round ${currentRound + 1}`)}</h2>
        <div class="league-round-card-grid single-round-grid">${visible.map(renderMatchCard).join("") || '<div class="empty-card small">No fixtures in this round.</div>'}</div>
      </section>
    </section>
  `;
    }
    function formatOverlayClock(timeValue) {
        const raw = String(timeValue || "").trim();
        if (!raw)
            return "TBA";
        const match = raw.match(/^(\d{1,2}):(\d{2})/);
        if (!match)
            return raw;
        const hour = Number(match[1]);
        const minute = match[2];
        if (!Number.isFinite(hour))
            return raw;
        const suffix = hour >= 12 ? "PM" : "AM";
        const twelve = hour % 12 || 12;
        return `${twelve}:${minute} ${suffix}`;
    }
    function leagueMatrixTimeKey(match) {
        return String(match.slot_time || match.match_time || match.scheduled_time || "").slice(0, 5) || `match-${Number(match.match_number || match.id || 0)}`;
    }
    function leagueMatrixTeamData(match, slot) {
        const id = match[`team${slot}_id`];
        const fullName = match[`team${slot}_name`];
        const shortName = match[`team${slot}_short_name`];
        const name = String(fullName || shortName || "TBD").trim();
        const logo = match[`team${slot}_logo_path`];
        const score = match[`team${slot}_score`] ?? 0;
        const isWinner = id && Number(match.winner_id) === Number(id);
        const initials = String(shortName || name || "?").slice(0, 2).toUpperCase();
        return { fullName, shortName, name, logo, score, isWinner, initials };
    }
    function renderLeagueMatrixTeam(match, slot, side) {
        const team = leagueMatrixTeamData(match, slot);
        return `
    <div class="matrix-team-side ${team.isWinner ? "winner" : ""} ${side === "right" ? "right" : "left"}">
      <span class="team-logo matrix-logo">${team.logo ? `<img src="${escapeHtml(team.logo)}" alt="">` : escapeHtml(team.initials)}</span>
      <strong title="${escapeHtml(team.fullName || team.name)}">${escapeHtml(team.name)}</strong>
      <b>${escapeHtml(team.score)}</b>
    </div>
  `;
    }
    function renderMatrixStreamers(streamers) {
        if (!Array.isArray(streamers) || !streamers.length)
            return "";
        return `
    <div class="matrix-stream-row">
      ${streamers.slice(0, 2).map((streamer) => {
            const label = streamer.streamer_name || streamer.handle || streamer.platform || "Stream";
            const initials = String(label).slice(0, 2).toUpperCase();
            return `<span class="matrix-stream-pill"><i>${escapeHtml(initials)}</i>${escapeHtml(label)}</span>`;
        }).join("")}
    </div>
  `;
    }
    function renderLeagueMatrixMatch(match) {
        if (!match)
            return `<div class="matrix-empty"></div>`;
        return `
    <article class="league-matrix-match">
      <div class="matrix-versus-row">
        ${renderLeagueMatrixTeam(match, 1, "left")}
        <span class="matrix-vs">VS</span>
        ${renderLeagueMatrixTeam(match, 2, "right")}
      </div>
      ${renderMatrixStreamers(match.streamers)}
    </article>
  `;
    }
    function renderLeagueFixtureMatrix(matches, title, subTitle = "") {
        const fixtureMatches = getLeagueFixtureMatches(matches);
        if (!fixtureMatches.length) {
            return `
      <div class="panel-heading">${escapeHtml(title || "Current Fixtures")}</div>
      ${subTitle ? `<div class="panel-subheading">${escapeHtml(subTitle)}</div>` : ""}
      <div class="empty-card small">No fixtures for this section yet.</div>
    `;
        }
        const roundGroups = groupByRound(fixtureMatches);
        const timeKeys = [...new Set(fixtureMatches.map(leagueMatrixTimeKey))]
            .sort((a, b) => {
            if (a.startsWith("match-") || b.startsWith("match-"))
                return Number(a.replace("match-", "")) - Number(b.replace("match-", ""));
            return String(a).localeCompare(String(b));
        });
        const roundColumn = roundGroups.length > 3 ? "minmax(260px, 260px)" : "minmax(240px, 1fr)";
        const gridColumns = `minmax(86px, 86px) repeat(${roundGroups.length}, ${roundColumn})`;
        const rowHeight = compactMode || window.innerHeight <= 760 ? "92px" : "104px";
        const gridRows = `30px repeat(${timeKeys.length}, minmax(${rowHeight}, 1fr))`;
        const bestOf = fixtureMatches.find((match) => match.best_of)?.best_of || 1;
        return `
    <div class="matrix-title-row">
      <div class="matrix-date-label">${escapeHtml(title || "Current Fixtures")}</div>
      ${subTitle ? `<div class="matrix-round-pill">${escapeHtml(subTitle)} · BO${escapeHtml(bestOf)}</div>` : ""}
    </div>
    <div class="league-fixture-matrix" style="--fixture-cols:${escapeHtml(gridColumns)};--fixture-rows:${escapeHtml(gridRows)};">
      <div class="matrix-head matrix-time-head">Time</div>
      ${roundGroups.map((group) => `<div class="matrix-head">${escapeHtml(group.name || "Round")}</div>`).join("")}
      ${timeKeys.map((timeKey, index) => {
            const firstMatch = fixtureMatches.find((match) => leagueMatrixTimeKey(match) === timeKey);
            return `
          <div class="matrix-time-cell">
            <small>Match #${escapeHtml(firstMatch?.match_number || index + 1)}</small>
            <strong>${escapeHtml(timeKey.startsWith("match-") ? "TBA" : formatOverlayClock(timeKey))}</strong>
          </div>
          ${roundGroups.map((group) => {
                const cellMatches = group.matches.filter((match) => leagueMatrixTimeKey(match) === timeKey);
                return `
              <div class="matrix-match-cell">
                ${cellMatches.length ? cellMatches.map(renderLeagueMatrixMatch).join("") : `<div class="matrix-empty"></div>`}
              </div>
            `;
            }).join("")}
        `;
        }).join("")}
    </div>
  `;
    }
    function attachLeagueRoundTabs() {
        bracketStage.querySelectorAll("[data-league-round]").forEach((btn) => {
            btn.addEventListener("click", () => {
                activeLeagueRound = /** @type {HTMLElement} */ (btn).dataset.leagueRound || "auto";
                const next = new URL(window.location.href);
                next.searchParams.set("section", "fixtures-rounds");
                next.searchParams.set("round", activeLeagueRound);
                window.history.replaceState({}, "", next);
                renderBracket();
            });
        });
    }
    function resetPanState(apply = true) {
        panState = { x: 0, y: 0, scale: 1 };
        if (apply)
            applyPanTransform();
    }
    function applyPanTransform() {
        const canvas = document.getElementById("bracketPanCanvas");
        if (!canvas)
            return;
        canvas.style.transform = `translate(${panState.x}px, ${panState.y}px) scale(${panState.scale})`;
    }
    function setupBracketPan() {
        const viewport = document.getElementById("bracketPanViewport");
        const canvas = document.getElementById("bracketPanCanvas");
        const resetBtn = document.getElementById("globalResetPanBtn");
        if (!viewport || !canvas)
            return;
        applyPanTransform();
        resetBtn?.addEventListener("click", () => resetPanState(true));
        viewport.addEventListener("pointerdown", (event) => {
            // Do not start bracket dragging when clicking/tapping broadcaster links.
            // The old pointer capture was swallowing the anchor click, so streams would not open.
            const target = event.target instanceof Element ? event.target : null;
            if (target?.closest(".stream-tile"))
                return;
            isPanning = true;
            panStart = { x: event.clientX, y: event.clientY };
            panOrigin = { x: panState.x, y: panState.y };
            viewport.classList.add("is-dragging");
            viewport.setPointerCapture?.(event.pointerId);
        });
        viewport.addEventListener("pointermove", (event) => {
            if (!isPanning)
                return;
            panState.x = panOrigin.x + (event.clientX - panStart.x);
            panState.y = panOrigin.y + (event.clientY - panStart.y);
            applyPanTransform();
        });
        const endPan = (event) => {
            isPanning = false;
            viewport.classList.remove("is-dragging");
            if (event?.pointerId !== undefined)
                viewport.releasePointerCapture?.(event.pointerId);
        };
        viewport.addEventListener("pointerup", endPan);
        viewport.addEventListener("pointercancel", endPan);
        viewport.addEventListener("pointerleave", endPan);
        viewport.addEventListener("wheel", (event) => {
            event.preventDefault();
            const delta = event.deltaY > 0 ? -0.08 : 0.08;
            const nextScale = Math.min(1.45, Math.max(0.55, panState.scale + delta));
            const rect = viewport.getBoundingClientRect();
            const mouseX = event.clientX - rect.left;
            const mouseY = event.clientY - rect.top;
            const scaleRatio = nextScale / panState.scale;
            panState.x = mouseX - (mouseX - panState.x) * scaleRatio;
            panState.y = mouseY - (mouseY - panState.y) * scaleRatio;
            panState.scale = nextScale;
            applyPanTransform();
        }, { passive: false });
        viewport.addEventListener("dblclick", () => resetPanState(true));
    }
    function groupByRound(matches) {
        const map = new Map();
        [...matches].sort(sortMatches).forEach((match) => {
            const key = `${match.bracket_type || "main"}-${match.round_index}`;
            if (!map.has(key))
                map.set(key, { name: buildRoundName(match), matches: [], roundIndex: Number(match.round_index || 0) });
            map.get(key).matches.push(match);
        });
        return [...map.values()].map((group) => {
            const namedGroup = { ...group, name: buildGroupDisplayName(group) };
            if (isFinalEightOverlay()) {
                namedGroup.matches = [...namedGroup.matches].sort(sortFinalEightMatchesForCleanLines);
            }
            return namedGroup;
        });
    }
    function sortFinalEightMatchesForCleanLines(a, b) {
        return finalEightLayoutRank(a) - finalEightLayoutRank(b) || Number(a.match_number || 0) - Number(b.match_number || 0);
    }
    function finalEightLayoutRank(match) {
        const short = finalEightShortRoundName(match.round_name || `Match ${match.match_number || ""}`);
        const fixedOrder = {
            QF1: 10,
            EF1: 20,
            EF2: 30,
            QF2: 40,
            SF1: 20,
            SF2: 40,
            PF1: 14,
            PF2: 36,
            GF: 25,
        };
        if (fixedOrder[short] !== undefined)
            return fixedOrder[short];
        const name = String(match.round_name || "").toLowerCase();
        const num = Number(match.match_number || 0);
        if (name.includes("qualifying"))
            return num === 1 ? 10 : 40;
        if (name.includes("elimination"))
            return num === 1 ? 20 : 30;
        if (name.includes("semi"))
            return num === 1 ? 20 : 40;
        if (name.includes("preliminary"))
            return num === 1 ? 14 : 36;
        if (name.includes("grand"))
            return 25;
        return num || 99;
    }
    function buildGroupDisplayName(group) {
        if (!isFinalEightOverlay())
            return group.name;
        const shorts = group.matches.map((m) => finalEightShortRoundName(m.round_name || ""));
        const names = group.matches.map((m) => String(m.round_name || "").toLowerCase());
        if (shorts.some((n) => n === "GF") || names.some((n) => n.includes("grand")))
            return "Grand Final";
        if (shorts.some((n) => n.startsWith("PF")) || names.some((n) => n.includes("preliminary")))
            return "Preliminary Finals";
        if (shorts.some((n) => n.startsWith("SF")) || names.some((n) => n.includes("semi")))
            return "Semi-Finals";
        if (shorts.some((n) => n.startsWith("QF") || n.startsWith("EF")) || names.some((n) => n.includes("qualifying") || n.includes("elimination")))
            return "Finals Week 1";
        return group.name;
    }
    function sortMatches(a, b) {
        const order = { winner: 1, finals_winner: 1, finals_gauntlet: 1, finals_swiss: 1, finals_group: 1, loser: 2, finals_loser: 2, final: 3, finals_final: 3, league: 4, swiss: 5 };
        return (order[a.bracket_type] || 9) - (order[b.bracket_type] || 9) || Number(a.round_index) - Number(b.round_index) || Number(a.match_number) - Number(b.match_number);
    }
    function buildRoundName(match) {
        const bracketType = String(match.bracket_type || "");
        const roundName = match.round_name || `Round ${Number(match.round_index) + 1}`;
        // Single elimination should read as normal finals names, not Upper Round / Upper Finals.
        if (currentLeague?.format === "single_elim")
            return roundName;
        if (bracketType.includes("loser"))
            return `Lower ${roundName}`;
        if (bracketType.includes("winner") && currentLeague?.format === "double_elim")
            return `Upper ${roundName}`;
        return roundName;
    }
    function renderBracketGrid(roundGroups, extraClass = "") {
        const format = String(currentLeague?.format || currentLeague?.tournament_format || "").toLowerCase();
        const hasDoubleElimRounds = Array.isArray(roundGroups) && roundGroups.some((group) => {
            const name = String(group?.name || "").toLowerCase();
            return name.includes("lower loser") || name.includes("lower losers") || name.includes("loser bracket") || name.includes("grand final");
        });
        const isDoubleElim = ["double_elim", "double_elimination", "double-elim", "double-elimination"].includes(format) || hasDoubleElimRounds;
        if (isDoubleElim) {
            return renderDoubleElimBracketGrid(roundGroups, extraClass);
        }
        return renderStandardBracketGrid(roundGroups, extraClass);
    }
    function renderBoardRoundHeading(group, index, style) {
        const headingDate = getRoundDate(group.roundIndex, group.matches);
        return `
      <div class="board-round-heading" style="${escapeHtml(style)}">
        <span class="board-round-number">${escapeHtml(String(index + 1).padStart(2, "0"))}</span>
        <span class="board-round-copy">
          <strong>${escapeHtml(group.name || "Round")}</strong>
          ${headingDate ? `<small>${escapeHtml(formatDate(headingDate))}</small>` : ""}
        </span>
      </div>
    `;
    }
    function renderDoubleElimBracketGrid(roundGroups, extraClass = "") {
        const visibleGroups = roundGroups.filter((group) => group.matches && group.matches.length);
        if (!visibleGroups.length)
            return renderStandardBracketGrid(roundGroups, extraClass);
        const compactView = window.innerWidth <= 1280 || window.innerHeight <= 760;
        const hasBroadcastedMatches = visibleGroups.some((group) => group.matches.some((match) => Array.isArray(match.streamers) && match.streamers.length > 0));
        const publicViewer = String(extraClass).includes("viewer-public-bracket-grid");
        const publicCompact = publicViewer && window.innerWidth <= 1700;
        const cardW = compactView || publicCompact ? 300 : 340;
        const cardH = publicViewer ? (compactView || publicCompact ? 104 : 116) : (compactView ? 86 : 104);
        const broadcastCardH = compactView ? 112 : 130;
        const gapX = compactView || publicCompact ? 72 : 92;
        const sectionGapY = compactView ? 92 : 120;
        const laneH = compactView ? (hasBroadcastedMatches ? 126 : 110) : (hasBroadcastedMatches ? 152 : 134);
        const headingH = publicViewer ? (compactView ? 42 : 48) : (compactView ? 26 : 30);
        const topPad = headingH + 18;
        const bottomPad = compactView ? 28 : 38;
        const groupKind = (group) => {
            const sample = group.matches.find(Boolean) || {};
            const bt = String(sample.bracket_type || "").toLowerCase();
            const name = String(group.name || sample.round_name || "").toLowerCase();
            if (bt.includes("loser") || name.includes("lower") || name.includes("loser"))
                return "lower";
            if (bt.includes("final") || name.includes("grand final") || name === "final" || name.includes("reset"))
                return "final";
            return "upper";
        };
        const upperGroups = visibleGroups.filter((group) => groupKind(group) === "upper");
        const lowerGroups = visibleGroups.filter((group) => groupKind(group) === "lower");
        const finalGroups = visibleGroups.filter((group) => groupKind(group) === "final");
        if (!upperGroups.length || !lowerGroups.length)
            return renderStandardBracketGrid(roundGroups, extraClass);
        const upperMax = Math.max(1, ...upperGroups.map((group) => group.matches.length));
        const lowerMax = Math.max(1, ...lowerGroups.map((group) => group.matches.length));
        const upperTotalH = Math.max(upperMax * laneH, compactView ? 360 : 430);
        const lowerTotalH = Math.max(lowerMax * laneH, compactView ? 320 : 380);
        const upperTop = 0;
        const lowerTop = topPad + upperTotalH + bottomPad + sectionGapY;
        const finalStartCol = Math.max(upperGroups.length, lowerGroups.length) + 1;
        const columnCount = finalStartCol + Math.max(1, finalGroups.length);
        const boardW = columnCount * cardW + Math.max(0, columnCount - 1) * gapX;
        const boardH = lowerTop + topPad + lowerTotalH + bottomPad;
        const makeLayout = (groups, baseTop, totalH, startCol = 0) => groups.map((group, groupIndex) => {
            const count = Math.max(1, group.matches.length);
            const slotH = totalH / count;
            const x = (startCol + groupIndex) * (cardW + gapX);
            const centres = group.matches.map((match, index) => baseTop + topPad + slotH * (index + 0.5));
            return { ...group, x, baseTop, count, slotH, centres };
        });
        const upperLayout = makeLayout(upperGroups, upperTop, upperTotalH, 0);
        const lowerLayout = makeLayout(lowerGroups, lowerTop, lowerTotalH, 0);
        const finalAreaTop = upperTop;
        const finalAreaH = boardH - bottomPad - topPad;
        const finalLayout = finalGroups.map((group, groupIndex) => {
            const count = Math.max(1, group.matches.length);
            const slotH = finalAreaH / Math.max(2, count + 1);
            const x = (finalStartCol + groupIndex) * (cardW + gapX);
            const centres = group.matches.map((match, index) => finalAreaTop + topPad + slotH * (index + 1));
            return { ...group, x, baseTop: finalAreaTop, count, slotH, centres };
        });
        const layout = [...upperLayout, ...lowerLayout, ...finalLayout];
        const renderSectionLabel = (label, top, widthCols) => `
    <div class="double-elim-section-label" style="left:0;top:${top}px;width:${Math.max(cardW, widthCols * cardW + Math.max(0, widthCols - 1) * gapX)}px;">${escapeHtml(label)}</div>
  `;
        const cards = layout.map((group, groupIndex) => {
            const headingDate = getRoundDate(group.roundIndex, group.matches);
            const headingText = headingDate ? `${group.name} • ${formatDate(headingDate)}` : group.name;
            return `
      ${renderBoardRoundHeading(group, groupIndex, `left:${group.x}px;top:${group.baseTop}px;width:${cardW}px;`)}
      ${group.matches.map((match, index) => {
                const matchHasStreamers = Array.isArray(match.streamers) && match.streamers.length > 0;
                const thisCardH = matchHasStreamers ? broadcastCardH : cardH;
                const y = group.centres[index] - thisCardH / 2;
                const slotClass = matchHasStreamers ? "board-card-slot has-broadcast-card" : "board-card-slot";
                return `<div class="${slotClass}" style="left:${group.x}px;top:${y}px;width:${cardW}px;height:${thisCardH}px;border-radius:var(--overlay-card-radius, var(--card-radius, 0px));overflow:hidden;">${renderMatchCard(match)}</div>`;
            }).join("")}
    `;
        }).join("");
        const connectors = [];
        const matchPositions = new Map();
        layout.forEach((group) => {
            group.matches.forEach((match, index) => {
                matchPositions.set(Number(match.id), {
                    group,
                    match,
                    index,
                    x: group.x,
                    y: group.centres[index],
                    left: group.x,
                    right: group.x + cardW,
                });
            });
        });
        const drawnPairs = new Set();
        const drawConnectorBetweenPositions = (source, target, targetSlot, type, customKey = "") => {
            if (!source || !target)
                return;
            // In double elimination, upper-bracket loser drops can create long orange
            // cross-bracket lines that make the board look like one continuous row.
            // Keep normal winner/lower-bracket progression lines, but do not draw
            // connectors from the winner bracket down into the loser bracket.
            if (type === "L" && groupKind(source.group) === "upper" && groupKind(target.group) === "lower") {
                return;
            }
            const key = customKey || `${source.match?.id || "source"}-${target.match?.id || "target"}-${targetSlot}-${type}`;
            if (drawnPairs.has(key))
                return;
            drawnPairs.add(key);
            const sourceY = source.y;
            const targetY = target.y + (Number(targetSlot) === 2 ? 14 : -14);
            const fromRight = source.right;
            const toLeft = target.left;
            const pathClass = type === "L" ? "loser-path" : "winner-path";
            if (toLeft > fromRight) {
                const midX = fromRight + (toLeft - fromRight) * 0.52;
                const armW = Math.max(8, midX - fromRight);
                const armW2 = Math.max(8, toLeft - midX);
                const minY = Math.min(sourceY, targetY);
                const maxY = Math.max(sourceY, targetY);
                const pillX = fromRight + Math.max(16, (toLeft - fromRight) * 0.28);
                connectors.push(`<span class="conn conn-h ${pathClass}" style="left:${fromRight}px;top:${sourceY}px;width:${armW}px;"></span>`);
                connectors.push(`<span class="conn conn-v ${pathClass}" style="left:${midX}px;top:${minY}px;height:${Math.max(2, maxY - minY)}px;"></span>`);
                connectors.push(`<span class="conn conn-h ${pathClass}" style="left:${midX}px;top:${targetY}px;width:${armW2}px;"></span>`);
                connectors.push(`<span class="conn-pill ${pathClass}" style="left:${pillX}px;top:${sourceY}px;">${type}</span>`);
            }
            else {
                // Cross-bracket drops, mainly upper-bracket losers feeding into the lower bracket.
                const dropX = Math.max(8, fromRight + gapX * 0.38);
                const minY = Math.min(sourceY, targetY);
                const maxY = Math.max(sourceY, targetY);
                connectors.push(`<span class="conn conn-h ${pathClass}" style="left:${fromRight}px;top:${sourceY}px;width:${Math.max(10, dropX - fromRight)}px;"></span>`);
                connectors.push(`<span class="conn conn-v ${pathClass}" style="left:${dropX}px;top:${minY}px;height:${Math.max(2, maxY - minY)}px;"></span>`);
                connectors.push(`<span class="conn conn-h ${pathClass}" style="left:${Math.min(dropX, toLeft - 22)}px;top:${targetY}px;width:${Math.max(10, Math.abs(toLeft - dropX))}px;"></span>`);
                connectors.push(`<span class="conn-pill ${pathClass}" style="left:${dropX + 10}px;top:${sourceY}px;">${type}</span>`);
            }
        };
        visibleGroups.forEach((group) => {
            group.matches.forEach((match) => {
                if (match.winner_to_match_id)
                    drawConnectorBetweenPositions(matchPositions.get(Number(match.id)), matchPositions.get(Number(match.winner_to_match_id)), match.winner_to_slot, "W");
                if (match.loser_to_match_id)
                    drawConnectorBetweenPositions(matchPositions.get(Number(match.id)), matchPositions.get(Number(match.loser_to_match_id)), match.loser_to_slot, "L");
            });
        });
        const cls = ["bracket-board", "aligned-bracket", "double-elim-board", extraClass].filter(Boolean).join(" ");
        return `
    <div class="${escapeHtml(cls)}" style="width:${boardW}px;height:${boardH}px;--card-w:${cardW}px;--card-h:${cardH}px;">
      ${renderSectionLabel("Winner Bracket", upperTop, upperGroups.length)}
      ${renderSectionLabel("Loser Bracket", lowerTop, lowerGroups.length)}
      <div class="bracket-connectors" aria-hidden="true">${connectors.join("")}</div>
      ${cards}
    </div>
  `;
    }
    function renderStandardBracketGrid(roundGroups, extraClass = "") {
        const visibleGroups = roundGroups.filter((group) => group.matches && group.matches.length);
        const maxMatches = Math.max(1, ...visibleGroups.map((group) => group.matches.length));
        const compactView = window.innerWidth <= 1280 || window.innerHeight <= 760;
        const hasBroadcastedMatches = visibleGroups.some((group) => group.matches.some((match) => Array.isArray(match.streamers) && match.streamers.length > 0));
        const finalEight = isFinalEightOverlay();
        const publicViewer = String(extraClass).includes("viewer-public-bracket-grid");
        const publicCompact = publicViewer && window.innerWidth <= 1700;
        const cardW = compactView || publicCompact ? 300 : 340;
        const cardH = finalEight ? (compactView ? 104 : 120) : publicViewer ? (compactView || publicCompact ? 104 : 116) : (compactView ? 86 : 104);
        const broadcastCardH = finalEight ? (compactView ? 128 : 146) : (compactView ? 112 : 130);
        const gapX = compactView || publicCompact ? 72 : 96;
        const laneH = finalEight
            ? (compactView ? (hasBroadcastedMatches ? 150 : 132) : (hasBroadcastedMatches ? 178 : 156))
            : (compactView ? (hasBroadcastedMatches ? 136 : 118) : (hasBroadcastedMatches ? 164 : 142));
        const headingH = publicViewer ? (compactView ? 42 : 48) : (compactView ? 26 : 30);
        const topPad = headingH + 18;
        const bottomPad = compactView ? 34 : 44;
        const totalH = isFinalEightOverlay() ? Math.max(maxMatches * laneH, compactView ? 540 : 620) : Math.max(maxMatches * laneH, 420);
        const groupWidths = visibleGroups.map((group) => {
            if (!publicViewer || finalEight)
                return cardW;
            const longestName = group.matches.reduce((longest, match) => {
                const names = [
                    getDisplayTeamName(match.team1_name, match.team1_short_name),
                    getDisplayTeamName(match.team2_name, match.team2_short_name),
                ].filter(Boolean);
                return Math.max(longest, ...names.map((name) => String(name).length));
            }, 0);
            // Logo, score, winner state and card padding need roughly 118px in
            // addition to the team label. Keep metadata readable at the low end.
            return Math.max(220, Math.min(cardW, Math.ceil(longestName * 7.2 + 118)));
        });
        const boardW = groupWidths.reduce((sum, width) => sum + width, 0) + Math.max(0, visibleGroups.length - 1) * gapX;
        const boardH = topPad + totalH + bottomPad;
        const cls = ["bracket-board", "aligned-bracket", isFinalEightOverlay() ? "final-eight-board" : "", extraClass].filter(Boolean).join(" ");
        const layout = visibleGroups.map((group, roundIndex) => {
            const count = Math.max(1, group.matches.length);
            const slotH = totalH / count;
            const groupCardW = groupWidths[roundIndex];
            const x = groupWidths.slice(0, roundIndex).reduce((sum, width) => sum + width, 0) + roundIndex * gapX;
            let centres = group.matches.map((match, index) => topPad + slotH * (index + 0.5));
            if (isFinalEightOverlay()) {
                const lane = totalH / 6;
                const groupName = String(group.name || "").toLowerCase();
                if (groupName.includes("week 1")) {
                    centres = group.matches.map((match, index) => {
                        const short = finalEightShortRoundName(match.round_name || "");
                        const fixed = { QF1: 0.75, EF1: 2.0, EF2: 4.0, QF2: 5.25 }[short];
                        return topPad + lane * (fixed ?? (index + 1));
                    });
                }
                else if (groupName.includes("semi")) {
                    centres = group.matches.map((match, index) => {
                        const short = finalEightShortRoundName(match.round_name || "");
                        const fixed = { SF1: 2.0, SF2: 4.0 }[short];
                        return topPad + lane * (fixed ?? ((Number(match.match_number || index + 1) === 1) ? 2.0 : 4.0));
                    });
                }
                else if (groupName.includes("preliminary")) {
                    centres = group.matches.map((match, index) => {
                        const short = finalEightShortRoundName(match.round_name || "");
                        const fixed = { PF1: 1.6, PF2: 4.4 }[short];
                        return topPad + lane * (fixed ?? ((Number(match.match_number || index + 1) === 1) ? 1.6 : 4.4));
                    });
                }
                else if (groupName.includes("grand")) {
                    centres = group.matches.map(() => topPad + lane * 3.0);
                }
            }
            return { ...group, x, cardW: groupCardW, count, slotH, centres };
        });
        const cards = layout.map((group, groupIndex) => {
            const headingDate = getRoundDate(group.roundIndex, group.matches);
            const headingText = headingDate ? `${group.name} • ${formatDate(headingDate)}` : group.name;
            return `
    ${renderBoardRoundHeading(group, groupIndex, `left:${group.x}px;top:0;width:${group.cardW}px;`)}
    ${group.matches.map((match, index) => {
                const matchHasStreamers = Array.isArray(match.streamers) && match.streamers.length > 0;
                const thisCardH = matchHasStreamers ? broadcastCardH : cardH;
                const y = group.centres[index] - thisCardH / 2;
                const slotClass = matchHasStreamers ? "board-card-slot has-broadcast-card" : "board-card-slot";
                return `<div class="${slotClass}" style="left:${group.x}px;top:${y}px;width:${group.cardW}px;height:${thisCardH}px;border-radius:var(--overlay-card-radius, var(--card-radius, 0px));overflow:hidden;">${renderMatchCard(match)}</div>`;
            }).join("")}
  `;
        }).join("");
        const connectors = [];
        const matchPositions = new Map();
        layout.forEach((group) => {
            group.matches.forEach((match, index) => {
                matchPositions.set(Number(match.id), {
                    group,
                    match,
                    index,
                    x: group.x,
                    y: group.centres[index],
                    left: group.x,
                    right: group.x + group.cardW,
                });
            });
        });
        const drawnPairs = new Set();
        const drawConnectorBetweenPositions = (source, target, targetSlot, type, customKey = "") => {
            if (!source || !target || source.right >= target.left)
                return;
            const key = customKey || `${source.match?.id || "source"}-${target.match?.id || "target"}-${targetSlot}-${type}`;
            if (drawnPairs.has(key))
                return;
            drawnPairs.add(key);
            const sourceY = source.y;
            const targetY = target.y + (Number(targetSlot) === 2 ? 14 : -14);
            const fromRight = source.right;
            const toLeft = target.left;
            const midX = fromRight + (toLeft - fromRight) * 0.52;
            const armW = Math.max(8, midX - fromRight);
            const armW2 = Math.max(8, toLeft - midX);
            const minY = Math.min(sourceY, targetY);
            const maxY = Math.max(sourceY, targetY);
            const pathClass = type === "L" ? "loser-path" : "winner-path";
            const pillX = fromRight + Math.max(16, (toLeft - fromRight) * 0.28);
            const pillY = sourceY;
            connectors.push(`<span class="conn conn-h ${pathClass}" style="left:${fromRight}px;top:${sourceY}px;width:${armW}px;"></span>`);
            connectors.push(`<span class="conn conn-v ${pathClass}" style="left:${midX}px;top:${minY}px;height:${Math.max(2, maxY - minY)}px;"></span>`);
            connectors.push(`<span class="conn conn-h ${pathClass}" style="left:${midX}px;top:${targetY}px;width:${armW2}px;"></span>`);
            connectors.push(`<span class="conn-pill ${pathClass}" style="left:${pillX}px;top:${pillY}px;">${type}</span>`);
        };
        const pushConnector = (sourceMatch, targetMatchId, targetSlot, type) => {
            const source = matchPositions.get(Number(sourceMatch.id));
            const target = matchPositions.get(Number(targetMatchId));
            drawConnectorBetweenPositions(source, target, targetSlot, type);
        };
        const drawFinalEightConnectors = () => {
            const byShortName = new Map();
            matchPositions.forEach((pos) => {
                const short = finalEightShortRoundName(pos.match.round_name || "");
                if (short)
                    byShortName.set(short, pos);
            });
            const route = (from, to, type, targetSlot) => {
                drawConnectorBetweenPositions(byShortName.get(from), byShortName.get(to), targetSlot, type, `final8-${from}-${to}-${type}-${targetSlot}`);
            };
            // AFL-style Final Eight visual routing.
            // IMPORTANT: QF winners go straight to PF, but the full-view overlay intentionally
            // does NOT draw the long QF winner line across the board. That was the line
            // leaving the W bubble after QF1/QF2 and landing in the wrong place on site.
            // The progression is now shown in the match footer instead: WINNER TO PF1/PF2.
            route("QF1", "SF1", "L", 1);
            route("EF1", "SF1", "W", 2);
            route("EF2", "SF2", "W", 2);
            route("QF2", "SF2", "L", 1);
            route("SF2", "PF1", "W", 2);
            route("SF1", "PF2", "W", 2);
            route("PF1", "GF", "W", 1);
            route("PF2", "GF", "W", 2);
        };
        if (isFinalEightOverlay()) {
            drawFinalEightConnectors();
        }
        else {
            visibleGroups.forEach((group) => {
                group.matches.forEach((match) => {
                    if (match.winner_to_match_id)
                        pushConnector(match, match.winner_to_match_id, match.winner_to_slot, "W");
                    if (match.loser_to_match_id)
                        pushConnector(match, match.loser_to_match_id, match.loser_to_slot, "L");
                });
            });
        }
        // Generated single-elimination brackets can omit persisted advancement IDs.
        // When results already identify the advancing team, connect that match to the
        // next-round card containing the same team. This also handles opening-round
        // byes without incorrectly joining the visible matches by array position.
        if (!connectors.length) {
            for (let roundIndex = 0; roundIndex < layout.length - 1; roundIndex += 1) {
                const from = layout[roundIndex];
                const to = layout[roundIndex + 1];
                from.matches.forEach((sourceMatch) => {
                    const advancingTeamId = Number(sourceMatch.winner_id || 0);
                    if (!advancingTeamId)
                        return;
                    const targetMatch = to.matches.find((candidate) => [candidate.team1_id, candidate.team2_id]
                        .map(Number)
                        .includes(advancingTeamId));
                    if (!targetMatch)
                        return;
                    const targetSlot = Number(targetMatch.team2_id) === advancingTeamId ? 2 : 1;
                    drawConnectorBetweenPositions(matchPositions.get(Number(sourceMatch.id)), matchPositions.get(Number(targetMatch.id)), targetSlot, "W", `team-route-${sourceMatch.id}-${targetMatch.id}`);
                });
            }
        }
        // Older generated brackets may not have explicit advancement links. Keep a soft fallback.
        if (!connectors.length) {
            for (let roundIndex = 0; roundIndex < layout.length - 1; roundIndex += 1) {
                const from = layout[roundIndex];
                const to = layout[roundIndex + 1];
                const fromRight = from.x + from.cardW;
                const toLeft = to.x;
                const midX = fromRight + (toLeft - fromRight) / 2;
                const armW = Math.max(6, midX - fromRight);
                const armW2 = Math.max(6, toLeft - midX);
                for (let nextIndex = 0; nextIndex < to.matches.length; nextIndex += 1) {
                    const startIndex = Math.floor(nextIndex * from.matches.length / to.matches.length);
                    const endIndex = Math.max(startIndex, Math.ceil((nextIndex + 1) * from.matches.length / to.matches.length) - 1);
                    const sourceCentres = from.centres.slice(startIndex, endIndex + 1);
                    if (!sourceCentres.length)
                        continue;
                    const targetY = to.centres[nextIndex];
                    const minY = Math.min(...sourceCentres, targetY);
                    const maxY = Math.max(...sourceCentres, targetY);
                    sourceCentres.forEach((sourceY) => connectors.push(`<span class="conn conn-h winner-path" style="left:${fromRight}px;top:${sourceY}px;width:${armW}px;"></span>`));
                    connectors.push(`<span class="conn conn-v winner-path" style="left:${midX}px;top:${minY}px;height:${Math.max(2, maxY - minY)}px;"></span>`);
                    connectors.push(`<span class="conn conn-h winner-path" style="left:${midX}px;top:${targetY}px;width:${armW2}px;"></span>`);
                }
            }
        }
        return `
    <div class="${escapeHtml(cls)}" style="width:${boardW}px;height:${boardH}px;--card-w:${Math.max(...groupWidths)}px;--card-h:${cardH}px;">
      <div class="bracket-connectors" aria-hidden="true">${connectors.join("")}</div>
      ${cards}
    </div>
  `;
    }
    function renderRoundColumn(group, totalRows) {
        // Kept as a harmless fallback for old cached markup. The active overlay uses renderBracketGrid's board layout.
        const count = Math.max(1, group.matches.length);
        const step = totalRows / count;
        return `
    <section class="round-column" style="--bracket-rows:${totalRows};">
      <div class="round-heading">${escapeHtml((getRoundDate(group.roundIndex, group.matches) ? `${group.name} • ${formatDate(getRoundDate(group.roundIndex, group.matches))}` : group.name))}</div>
      <div class="round-matches" style="--bracket-rows:${totalRows};">
        ${group.matches.map((match, index) => {
            const span = Math.max(3, Math.min(5, Math.floor(step * 0.55)));
            const centre = (index * step) + (step / 2);
            const start = Math.max(1, Math.round(centre - (span / 2)) + 1);
            return renderMatchCard(match, `grid-row:${start} / span ${span};`);
        }).join("")}
      </div>
    </section>
  `;
    }
    const DEFAULT_PUBLIC_LADDER_COLUMNS = [
        { key: "rank", label: "#" },
        { key: "movement", label: "" },
        { key: "club", label: "Club" },
        { key: "played", label: "Played" },
        { key: "points", label: "Points" },
        { key: "percent", label: "Win %" },
        { key: "won", label: "Won" },
        { key: "lost", label: "Lost" },
        { key: "drawn", label: "Drawn" },
        { key: "dq", label: "DQ" },
        { key: "pf", label: "GF" },
        { key: "pa", label: "GA" },
        { key: "diff", label: "Diff" },
        { key: "form", label: "Form" },
        { key: "latest", label: "Latest" },
        { key: "upNext", label: "Up Next" },
    ];
    function getDefaultCompactLadderColumns() {
        const compactKeys = ["rank", "movement", "club", "played", "points", "won", "lost", "pf", "pa", "diff"];
        const defaults = new Map(DEFAULT_PUBLIC_LADDER_COLUMNS.map((column) => [column.key, column]));
        return compactKeys.map((key) => defaults.get(key)).filter(Boolean);
    }
    function normalizePublicLadderColumnKey(key = "") {
        const aliases = {
            winPct: "percent",
            scoreDiff: "diff",
        };
        return aliases[key] || key;
    }
    function getPublicLadderColumns(options = {}) {
        try {
            const raw = currentStandings?.ladder_format || currentLeague?.ladder_format || "";
            if (!String(raw || "").trim() && options.compact)
                return getDefaultCompactLadderColumns();
            const parsed = typeof raw === "string" ? JSON.parse(raw || "{}") : (raw || {});
            const configured = Array.isArray(parsed.columns) ? parsed.columns : [];
            const defaults = new Map(DEFAULT_PUBLIC_LADDER_COLUMNS.map((column) => [column.key, column]));
            const columns = [];
            configured.forEach((column) => {
                const normalizedKey = normalizePublicLadderColumnKey(column.key);
                const base = defaults.get(normalizedKey);
                if (!base)
                    return;
                defaults.delete(normalizedKey);
                if (column.visible === false)
                    return;
                columns.push(base);
            });
            defaults.forEach((column) => columns.push(column));
            return columns;
        }
        catch {
            return options.compact ? getDefaultCompactLadderColumns() : DEFAULT_PUBLIC_LADDER_COLUMNS;
        }
    }
    function getLadderColumnGridTemplate(columns, isSidePanel = false) {
        const widths = {
            rank: isSidePanel ? "34px" : "42px",
            movement: isSidePanel ? "24px" : "34px",
            club: isSidePanel ? "minmax(130px,1.35fr)" : "minmax(220px,2.4fr)",
            played: isSidePanel ? "54px" : "76px",
            points: isSidePanel ? "58px" : "74px",
            percent: isSidePanel ? "58px" : "70px",
            won: isSidePanel ? "46px" : "62px",
            lost: isSidePanel ? "46px" : "62px",
            drawn: isSidePanel ? "52px" : "70px",
            dq: isSidePanel ? "38px" : "54px",
            pf: isSidePanel ? "42px" : "54px",
            pa: isSidePanel ? "42px" : "54px",
            diff: isSidePanel ? "48px" : "62px",
            form: isSidePanel ? "74px" : "100px",
            latest: isSidePanel ? "minmax(88px,1fr)" : "minmax(170px,1.15fr)",
            upNext: isSidePanel ? "minmax(72px,.85fr)" : "minmax(100px,.8fr)",
        };
        return columns.map((column) => widths[column.key] || "minmax(58px,1fr)").join(" ");
    }
    function renderLadderCell(column, row, position, zone, moveIcon) {
        if (column.key === "rank")
            return `<span class="rank">${escapeHtml(position)}</span>`;
        if (column.key === "movement")
            return renderViewerMovement(row);
        if (column.key === "club")
            return `<span class="standings-team viewer-ladder-team"><span class="team-logo small-logo viewer-ladder-logo">${row.logo ? `<img src="${escapeHtml(row.logo)}" alt="">` : escapeHtml(String(row.short || row.name || "?").slice(0, 2).toUpperCase())}</span><strong>${escapeHtml(row.name)}</strong></span>`;
        if (column.key === "played")
            return `<span>${escapeHtml(row.played)}</span>`;
        if (column.key === "points")
            return `<span class="points">${escapeHtml(row.points)}</span>`;
        if (column.key === "percent")
            return `<span>${escapeHtml(getLadderPercent(row).toFixed(1))}</span>`;
        if (column.key === "won")
            return `<span>${escapeHtml(row.won)}</span>`;
        if (column.key === "lost")
            return `<span>${escapeHtml(row.lost)}</span>`;
        if (column.key === "drawn")
            return `<span>${escapeHtml(row.drawn)}</span>`;
        if (column.key === "dq")
            return `<span>${escapeHtml(row.dq || 0)}</span>`;
        if (column.key === "pf")
            return `<span>${escapeHtml(row.pf)}</span>`;
        if (column.key === "pa")
            return `<span>${escapeHtml(row.pa)}</span>`;
        if (column.key === "diff") {
            const diff = Number(row.pf || 0) - Number(row.pa || 0);
            return `<span class="${diff > 0 ? "positive" : diff < 0 ? "negative" : ""}">${diff > 0 ? "+" : ""}${escapeHtml(diff)}</span>`;
        }
        if (column.key === "form")
            return `<span class="form-cell viewer-form-cell">${row.form.slice(-4).map(renderFormDot).join("") || "-"}</span>`;
        if (column.key === "latest")
            return `<span class="latest-cell">${escapeHtml(row.latest || "-")}</span>`;
        if (column.key === "upNext")
            return `<span class="next-cell">${escapeHtml(row.upNext || "-")}</span>`;
        return `<span></span>`;
    }
    function renderStandingsPanel(isSidePanel = false) {
        const rows = buildOverlayLeagueLadder();
        if (!rows.length) {
            return `<section class="standings-panel ${isSidePanel ? "side" : "full"}"><div class="panel-heading">Current Standings</div><div class="empty-card small">No standings data yet.</div></section>`;
        }
        const topCount = Number(currentLeague?.promotion_count || currentLeague?.finals_team_count || currentStandings?.finals_team_count || 0);
        const bottomCount = Number(currentLeague?.relegation_count || currentStandings?.relegation_count || 0);
        const compact = isSidePanel ? "compact-ladder" : "full-ladder";
        const columns = getPublicLadderColumns({ compact: isSidePanel });
        const gridTemplate = getLadderColumnGridTemplate(columns, isSidePanel);
        return `
    <section class="standings-panel ${isSidePanel ? "side" : "full"}">
      <div class="panel-heading">Current Standings</div>
      <div class="standings-table admin-style ${compact}">
        <div class="standings-row head" style="grid-template-columns: ${gridTemplate};">
          ${columns.map((column) => `<span>${escapeHtml(column.label)}</span>`).join("")}
        </div>
        ${rows.map((row, index) => {
            const position = index + 1;
            const zone = topCount > 0 && position <= topCount ? "playoff-zone" : bottomCount > 0 && position > rows.length - bottomCount ? "danger-zone" : "";
            const moveIcon = row.movement === "up" ? "▲" : row.movement === "down" ? "▼" : "–";
            return `
            <div class="standings-row ${zone}" style="grid-template-columns: ${gridTemplate};">
              ${columns.map((column) => renderLadderCell(column, row, position, zone, moveIcon)).join("")}
            </div>
          `;
        }).join("")}
      </div>
      ${renderRemovedTeamFooters()}
      ${isSidePanel ? `
        <div class="ladder-tiebreak-strip">Tiebreak order: Points &gt; Win % &gt; Head-to-head &gt; Score differential &gt; Points for &gt; Seed</div>
        <a class="view-ladder-btn" href="${escapeHtml(viewerUrl("/viewer/ladder"))}">View Full Standings</a>
      ` : ""}
    </section>
  `;
    }
    function buildOverlayLeagueLadder(cutoffDateKey = getActiveOverlayDateKey()) {
        const table = new Map();
        const teams = Array.isArray(currentLeague?.teams) ? currentLeague.teams : [];
        const removedTeamIds = new Set((Array.isArray(currentStandings?.removed_teams) ? currentStandings.removed_teams : []).map((team) => Number(team.old_team_id || 0)).filter(Boolean));
        const activeTeamIds = getActiveDivisionTeamIds(currentMatches);
        teams.forEach((team) => {
            if (activeTeamIds.size && !activeTeamIds.has(Number(team.id)))
                return;
            table.set(Number(team.id), createLadderRow({
                id: team.id,
                name: team.team_name || team.short_name || "Unknown Team",
                short: team.short_name || "",
                logo: team.logo_path || "",
                seed: Number(team.seed || 9999),
            }));
        });
        function ensureFromMatch(teamId, name, short, logo) {
            const id = Number(teamId);
            if (!id)
                return null;
            if (!table.has(id)) {
                table.set(id, createLadderRow({
                    id,
                    name: name || short || "Unknown Team",
                    short: short || "",
                    logo: logo || "",
                    seed: 9999,
                }));
            }
            return table.get(id);
        }
        const fixtureMatches = currentMatches
            .filter(isLeagueTableMatch)
            .filter(isMatchInActiveDivision)
            .filter((match) => match.team1_id && match.team2_id)
            .sort((a, b) => Number(a.round_index) - Number(b.round_index) || Number(a.match_number) - Number(b.match_number));
        const scopedFixtureMatches = cutoffDateKey
            ? fixtureMatches.filter((match) => {
                const matchDate = getMatchDateKey(match);
                return matchDate && compareOverlayDateKeys(matchDate, cutoffDateKey) <= 0;
            })
            : fixtureMatches;
        const completed = scopedFixtureMatches.filter((match) => (match.status === "completed" || match.status === "draw")
            && !removedTeamIds.has(Number(match.team1_id || 0))
            && !removedTeamIds.has(Number(match.team2_id || 0)));
        const latestRound = completed.length ? Math.max(...completed.map((match) => Number(match.round_index || 0))) : null;
        const previousMap = latestRound === null ? new Map() : buildOverlayLadderSnapshot(scopedFixtureMatches, latestRound);
        completed.forEach((match) => applyOverlayResult(table, match));
        scopedFixtureMatches
            .filter((match) => match.status !== "completed" && match.status !== "draw")
            .forEach((match) => {
            const t1 = ensureFromMatch(match.team1_id, match.team1_name, match.team1_short_name, match.team1_logo_path);
            const t2 = ensureFromMatch(match.team2_id, match.team2_name, match.team2_short_name, match.team2_logo_path);
            if (t1 && t2 && t1.upNext === "-")
                t1.upNext = t2.short || t2.name;
            if (t1 && t2 && t2.upNext === "-")
                t2.upNext = t1.short || t1.name;
        });
        const rows = [...table.values()].sort(sortOverlayLadderRows);
        rows.forEach((row, index) => {
            const previousPosition = previousMap.get(Number(row.id));
            const currentPosition = index + 1;
            row.movementAmount = previousPosition && row.played > 0 ? Math.abs(previousPosition - currentPosition) : 0;
            if (!previousPosition || row.played === 0 || !row.movementAmount)
                row.movement = "same";
            else if (previousPosition > currentPosition)
                row.movement = "up";
            else if (previousPosition < currentPosition)
                row.movement = "down";
            else
                row.movement = "same";
        });
        return rows;
    }
    function createLadderRow(base) {
        return {
            id: Number(base.id),
            name: base.name,
            short: base.short,
            logo: base.logo,
            seed: Number(base.seed || 9999),
            played: 0,
            points: 0,
            won: 0,
            lost: 0,
            drawn: 0,
            dq: 0,
            pf: 0,
            pa: 0,
            form: [],
            latest: "-",
            upNext: "-",
            movement: "same",
            movementAmount: 0,
        };
    }
    function buildOverlayLadderSnapshot(matches, beforeRoundIndex) {
        const table = new Map();
        const activeTeamIds = getActiveDivisionTeamIds(matches);
        (currentLeague?.teams || []).forEach((team) => {
            if (activeTeamIds.size && !activeTeamIds.has(Number(team.id)))
                return;
            table.set(Number(team.id), createLadderRow({
                id: team.id,
                name: team.team_name || team.short_name || "Unknown Team",
                short: team.short_name || "",
                logo: team.logo_path || "",
                seed: Number(team.seed || 9999),
            }));
        });
        matches
            .filter((match) => (match.status === "completed" || match.status === "draw") && Number(match.round_index || 0) < Number(beforeRoundIndex))
            .forEach((match) => applyOverlayResult(table, match));
        const positions = new Map();
        [...table.values()].sort(sortOverlayLadderRows).forEach((row, index) => positions.set(Number(row.id), index + 1));
        return positions;
    }
    function parseOverlayGameScores(match) {
        const raw = match?.game_scores_json || match?.game_scores || "";
        if (Array.isArray(raw))
            return raw;
        if (!raw)
            return [];
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        }
        catch {
            return [];
        }
    }
    function getOverlayMatchPointTotals(match) {
        const gameScores = parseOverlayGameScores(match);
        let team1Total = 0;
        let team2Total = 0;
        let hasGameScores = false;
        gameScores.forEach((game) => {
            const team1 = Number(game?.team1_score ?? 0);
            const team2 = Number(game?.team2_score ?? 0);
            if (!Number.isFinite(team1) || !Number.isFinite(team2))
                return;
            if (team1 || team2)
                hasGameScores = true;
            team1Total += team1;
            team2Total += team2;
        });
        if (hasGameScores)
            return { team1: team1Total, team2: team2Total };
        return {
            team1: Number(match.team1_score || 0),
            team2: Number(match.team2_score || 0),
        };
    }
    function getOverlayMatchGameWins(match) {
        const gameScores = parseOverlayGameScores(match);
        let team1Wins = 0;
        let team2Wins = 0;
        let hasGameScores = false;
        gameScores.forEach((game) => {
            const team1 = Number(game?.team1_score ?? 0);
            const team2 = Number(game?.team2_score ?? 0);
            if (!Number.isFinite(team1) || !Number.isFinite(team2))
                return;
            if (team1 || team2)
                hasGameScores = true;
            if (team1 > team2)
                team1Wins += 1;
            if (team2 > team1)
                team2Wins += 1;
        });
        if (hasGameScores)
            return { team1: team1Wins, team2: team2Wins };
        return {
            team1: Number(match.team1_score || 0),
            team2: Number(match.team2_score || 0),
        };
    }
    function applyOverlayResult(table, match) {
        function ensure(teamId, name, short, logo) {
            const id = Number(teamId);
            if (!table.has(id)) {
                table.set(id, createLadderRow({ id, name: name || short || "Unknown Team", short: short || "", logo: logo || "", seed: 9999 }));
            }
            return table.get(id);
        }
        const t1 = ensure(match.team1_id, match.team1_name, match.team1_short_name, match.team1_logo_path);
        const t2 = ensure(match.team2_id, match.team2_name, match.team2_short_name, match.team2_logo_path);
        if (!t1 || !t2)
            return;
        const series = getOverlayMatchGameWins(match);
        const series1 = Number(series.team1 || 0);
        const series2 = Number(series.team2 || 0);
        const pointTotals = getOverlayMatchPointTotals(match);
        const pf1 = Number(pointTotals.team1 || 0);
        const pf2 = Number(pointTotals.team2 || 0);
        const winMethod = String(match.win_method || match.result_method || "").toLowerCase();
        const isDraw = match.status === "draw" || winMethod === "draw" || (!match.winner_id && series1 === series2);
        const isDoubleForfeit = ["double_ff", "double_forfeit", "both_ff", "both_forfeit"].includes(winMethod);
        const isDq = ["dq", "forfeit", "ff", "no_show", "walkover"].includes(winMethod);
        t1.played++;
        t2.played++;
        t1.pf += pf1;
        t1.pa += pf2;
        t2.pf += pf2;
        t2.pa += pf1;
        if (isDoubleForfeit) {
            t1.dq++;
            t2.dq++;
            t1.lost++;
            t2.lost++;
            t1.form.push("F");
            t2.form.push("F");
            t1.latest = `F vs ${t2.short || t2.name}`;
            t2.latest = `F vs ${t1.short || t1.name}`;
        }
        else if (isDq) {
            const dqTeamId = Number(match.dq_team_id || 0);
            const dqRow = dqTeamId === Number(match.team2_id) ? t2 : t1;
            const otherRow = dqTeamId === Number(match.team2_id) ? t1 : t2;
            dqRow.dq++;
            dqRow.lost++;
            otherRow.won++;
            dqRow.points += Number(currentLeague?.points_dq ?? 0);
            otherRow.points += Number(currentLeague?.points_dq_opponent ?? currentLeague?.points_win ?? 3);
            dqRow.form.push("F");
            otherRow.form.push("W");
            dqRow.latest = `F vs ${otherRow.short || otherRow.name}`;
            otherRow.latest = `W by F vs ${dqRow.short || dqRow.name}`;
        }
        else if (isDraw) {
            t1.drawn++;
            t2.drawn++;
            t1.points += Number(currentLeague?.points_draw ?? 1);
            t2.points += Number(currentLeague?.points_draw ?? 1);
            t1.form.push("D");
            t2.form.push("D");
            t1.latest = `D vs ${t2.short || t2.name} ${pf1}-${pf2}`;
            t2.latest = `D vs ${t1.short || t1.name} ${pf2}-${pf1}`;
        }
        else if (Number(match.winner_id) === Number(match.team1_id) || series1 > series2) {
            t1.won++;
            t2.lost++;
            t1.points += Number(currentLeague?.points_win ?? 3) + (series1 * Number(currentLeague?.points_game_win ?? 1));
            t2.points += Number(currentLeague?.points_loss ?? 0) + (series2 * Number(currentLeague?.points_game_win ?? 1));
            t1.form.push("W");
            t2.form.push("L");
            t1.latest = `W vs ${t2.short || t2.name} ${pf1}-${pf2}`;
            t2.latest = `L vs ${t1.short || t1.name} ${pf2}-${pf1}`;
        }
        else {
            t2.won++;
            t1.lost++;
            t2.points += Number(currentLeague?.points_win ?? 3) + (series2 * Number(currentLeague?.points_game_win ?? 1));
            t1.points += Number(currentLeague?.points_loss ?? 0) + (series1 * Number(currentLeague?.points_game_win ?? 1));
            t2.form.push("W");
            t1.form.push("L");
            t2.latest = `W vs ${t1.short || t1.name} ${pf2}-${pf1}`;
            t1.latest = `L vs ${t2.short || t2.name} ${pf1}-${pf2}`;
        }
    }
    function sortOverlayLadderRows(a, b) {
        return b.points - a.points || getLadderPercent(b) - getLadderPercent(a) || -overlayHeadToHeadDelta(a, b) || (b.pf - b.pa) - (a.pf - a.pa) || b.pf - a.pf || a.seed - b.seed || String(a.name).localeCompare(String(b.name));
    }
    function sortOverlayLadderRowsForMatches(a, b, matches = currentMatches) {
        return b.points - a.points || getLadderPercent(b) - getLadderPercent(a) || -overlayHeadToHeadDeltaForMatches(a, b, matches) || (b.pf - b.pa) - (a.pf - a.pa) || b.pf - a.pf || a.seed - b.seed || String(a.name).localeCompare(String(b.name));
    }
    function overlayHeadToHeadDelta(a, b) {
        return overlayHeadToHeadDeltaForMatches(a, b, currentMatches);
    }
    function overlayHeadToHeadDeltaForMatches(a, b, matches = currentMatches) {
        const aId = String(a?.id || "");
        const bId = String(b?.id || "");
        if (!aId || !bId)
            return 0;
        return matches.reduce((delta, match) => {
            if (!isLeagueTableMatch(match) || !isFinalMatchState(match))
                return delta;
            const ids = [String(match.team1_id || ""), String(match.team2_id || "")];
            if (!ids.includes(aId) || !ids.includes(bId))
                return delta;
            const winnerId = String(match.winner_id || "");
            if (winnerId === aId)
                return delta + 1;
            if (winnerId === bId)
                return delta - 1;
            return delta;
        }, 0);
    }
    function getLadderPercent(row) {
        const played = Number(row.played || 0);
        if (!played)
            return 0;
        const wins = Number(row.won || 0);
        const draws = Number(row.drawn || 0);
        return ((wins + (draws * 0.5)) / played) * 100;
    }
    function renderFormDot(value) {
        const cls = value === "W" ? "win" : value === "L" || value === "F" ? "loss" : "draw";
        return `<span class="form-dot ${cls}">${escapeHtml(value)}</span>`;
    }
    function formatSlotTime(timeValue) {
        if (!timeValue)
            return "";
        const [hh, mm] = String(timeValue).split(":");
        if (hh === undefined || mm === undefined)
            return timeValue;
        return `${hh}:${mm}`;
    }
    function isFinalEightOverlay() {
        return String(currentLeague?.format || "") === "final_eight";
    }
    function finalEightShortRoundName(name) {
        const raw = String(name || "").trim();
        if (!isFinalEightOverlay())
            return raw;
        const code = raw.toUpperCase().match(/\b(QF|EF|SF|PF|GF)\s*([12])?\b/);
        if (code)
            return `${code[1]}${code[1] === "GF" ? "" : (code[2] || "")}`;
        const lower = raw.toLowerCase();
        const wordNumber = lower.includes("second") ? "2" : lower.includes("first") ? "1" : "";
        const digitNumber = (raw.match(/(\d+)/) || ["", ""])[1];
        const num = digitNumber || wordNumber;
        if (lower.includes("qualifying"))
            return `QF${num || ""}`;
        if (lower.includes("elimination"))
            return `EF${num || ""}`;
        if (lower.includes("semi"))
            return `SF${num || ""}`;
        if (lower.includes("preliminary"))
            return `PF${num || ""}`;
        if (lower.includes("grand"))
            return "GF";
        return raw;
    }
    function finalEightSourceCode(source, type) {
        const short = finalEightShortRoundName(source?.round_name || "");
        const clean = short || `M${source?.match_number || ""}`;
        return `${type} of ${clean}`;
    }
    /**
     * @param {Bracketr.Match & Record<string, any>} match
     * @param {string} [placementStyle]
     */
    function renderMatchCard(match, placementStyle = "") {
        const title = finalEightShortRoundName(match.round_name || `Match ${match.match_number}`);
        const streamers = Array.isArray(match.streamers) ? match.streamers : [];
        const hasStreamers = streamers.length > 0;
        const liveCardStyle = isViewerPublicPage && !isViewerObsSource
            ? `${placementStyle || ""};border-radius:14px !important;overflow:hidden !important;`
            : `${placementStyle || ""};border-radius:var(--overlay-card-radius, var(--card-radius, 0px)) !important;overflow:hidden !important;`;
        return `
    <article class="match-card ${hasStreamers ? "has-stream-tile" : ""}" style="${escapeHtml(liveCardStyle)}">
      <div class="match-top">
        <span>${escapeHtml(title)} ${match.match_number ? `#${escapeHtml(match.match_number)}` : ""}${match.slot_time ? ` · ${escapeHtml(formatSlotTime(match.slot_time))}` : ""}</span>
        <span class="best-of">BO${escapeHtml(match.best_of || 1)}</span>
      </div>
      ${renderTeam(match, 1)}
      ${renderTeam(match, 2)}
      ${renderFinalEightProgressFooter(match)}
      ${renderStreamers(streamers)}
    </article>
  `;
    }
    function renderFinalEightProgressFooter(match) {
        if (!isFinalEightOverlay())
            return "";
        const short = finalEightShortRoundName(match.round_name || "");
        const winnerText = {
            QF1: "WINNER TO PF1",
            QF2: "WINNER TO PF2",
        }[short];
        if (!winnerText)
            return "";
        return `
    <div class="final8-progress-footer qf-only">
      <span class="advance-detail winner-detail">${escapeHtml(winnerText)}</span>
    </div>
  `;
    }
    function getDisplayTeamName(fullName, shortName) {
        const cleanFull = String(fullName || "").trim();
        const cleanShort = String(shortName || "").trim();
        if (!cleanFull)
            return cleanShort || "TBD";
        // Bracket cards are tight. If the proper name is likely to spill, use the team's short name.
        if (cleanShort && cleanFull.length > 18)
            return cleanShort;
        return cleanFull;
    }
    function renderTeam(match, slot) {
        const id = match[`team${slot}_id`];
        const fullName = match[`team${slot}_name`];
        const shortName = match[`team${slot}_short_name`];
        const rawName = getDisplayTeamName(fullName, shortName);
        const fullTitle = fullName || shortName || rawName;
        const logo = match[`team${slot}_logo_path`];
        const score = match[`team${slot}_score`] ?? 0;
        const isWinner = id && Number(match.winner_id) === Number(id);
        if (!id) {
            const sourceLabel = getEmptySlotSourceLabel(match, slot);
            return `
      <div class="team-row pending-source">
        <span class="team-logo source-mark">${escapeHtml(sourceLabel.type)}</span>
        <span class="team-name">${escapeHtml(sourceLabel.label)}</span>
        <span class="team-score">-</span>
      </div>
    `;
        }
        const name = rawName || "TBD";
        const initials = String(name).slice(0, 2).toUpperCase();
        return `
    <div class="team-row ${isWinner ? "winner" : ""}">
      <span class="team-logo">${logo ? `<img src="${escapeHtml(logo)}" alt="">` : escapeHtml(initials)}</span>
      <span class="team-name" title="${escapeHtml(fullTitle)}">${escapeHtml(name)}</span>
      <span class="team-score">${escapeHtml(score)}</span>
    </div>
  `;
    }
    function getEmptySlotSourceLabel(match, slot) {
        const source = currentMatches.find((candidate) => Number(candidate.winner_to_match_id) === Number(match.id) && Number(candidate.winner_to_slot) === Number(slot));
        if (source) {
            return {
                type: "W",
                label: isFinalEightOverlay() ? finalEightSourceCode(source, "W") : `Winner of ${source.round_name || "Match"} #${source.match_number || ""}`.trim(),
            };
        }
        const loserSource = currentMatches.find((candidate) => Number(candidate.loser_to_match_id) === Number(match.id) && Number(candidate.loser_to_slot) === Number(slot));
        if (loserSource) {
            return {
                type: "L",
                label: isFinalEightOverlay() ? finalEightSourceCode(loserSource, "L") : `Loser of ${loserSource.round_name || "Match"} #${loserSource.match_number || ""}`.trim(),
            };
        }
        // Single-elim brackets generated before route links still have predictable feeding.
        if ((match.bracket_type === "winner" || match.bracket_type === "finals_winner") && Number(match.round_index) > 0) {
            const previousMatchNumber = (Number(match.match_number) - 1) * 2 + Number(slot);
            const previousRound = Number(match.round_index) - 1;
            const previous = currentMatches.find((candidate) => candidate.bracket_type === match.bracket_type &&
                Number(candidate.round_index) === previousRound &&
                Number(candidate.match_number) === previousMatchNumber);
            if (previous) {
                return {
                    type: "W",
                    label: isFinalEightOverlay() ? finalEightSourceCode(previous, "W") : `Winner of ${previous.round_name || "Match"} #${previous.match_number || ""}`.trim(),
                };
            }
        }
        return { type: "TB", label: "TBD" };
    }
    function normaliseStreamUrl(streamer) {
        const rawUrl = String(streamer.stream_url || "").trim();
        const platform = String(streamer.platform || "").toLowerCase();
        if (rawUrl && /^https?:\/\//i.test(rawUrl))
            return rawUrl;
        if (rawUrl && rawUrl.startsWith("//"))
            return `https:${rawUrl}`;
        if (rawUrl && rawUrl.includes("."))
            return `https://${rawUrl.replace(/^\/+/, "")}`;
        // If someone typed only a Twitch/Kick/YouTube handle into the Stream URL field,
        // treat that as the handle instead of leaving the tile unclickable.
        const handleSource = rawUrl || streamer.handle || streamer.streamer_name || "";
        const handle = String(handleSource).trim().replace(/^@/, "").replace(/^\/+/, "");
        if (!handle)
            return "";
        if (platform.includes("youtube"))
            return `https://www.youtube.com/@${handle}`;
        if (platform.includes("kick"))
            return `https://kick.com/${handle}`;
        if (platform.includes("facebook"))
            return `https://www.facebook.com/${handle}`;
        // Default to Twitch because most broadcast channels in this pack are Twitch-style handles.
        return `https://www.twitch.tv/${handle}`;
    }
    function renderStreamers(streamers) {
        if (!streamers.length)
            return `<div class="stream-row empty"><span class="stream-dot"></span><span>Not assigned to stream</span></div>`;
        const tiles = streamers.map((s) => {
            const label = s.streamer_name || s.handle || s.platform || "Stream";
            const url = normaliseStreamUrl(s);
            const logo = s.logo_path || "";
            const initials = String(label).slice(0, 2).toUpperCase();
            const tileInner = `
      <span class="stream-tile-logo">${logo ? `<img src="${escapeHtml(logo)}" alt="">` : escapeHtml(initials)}</span>
      <span class="stream-tile-name">${escapeHtml(label)}</span>
    `;
            return url
                ? `<a class="stream-tile" href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer" data-stream-url="${escapeHtml(url)}" title="Open ${escapeHtml(label)} stream">${tileInner}</a>`
                : `<span class="stream-tile" title="${escapeHtml(label)}">${tileInner}</span>`;
        }).join("");
        return `<div class="stream-boxes" aria-label="Broadcast channels">${tiles}</div>`;
    }
    document.addEventListener("click", (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const link = target?.closest(".stream-tile[data-stream-url]");
        if (!link)
            return;
        const url = link.getAttribute("data-stream-url") || link.getAttribute("href");
        if (!url)
            return;
        // Force open in a real tab/window. This works better in the pan/zoom overlay and OBS browser source.
        event.preventDefault();
        event.stopPropagation();
        window.open(url, "_blank", "noopener,noreferrer");
    });
    async function startOverlay() {
        if (window.__liveBracketOverlay.started) {
            window.__liveBracketOverlay.intervals.forEach(clearInterval);
            window.__liveBracketOverlay.intervals = [];
            if (window.__liveBracketOverlay.eventSource) {
                window.__liveBracketOverlay.eventSource.close();
                window.__liveBracketOverlay.eventSource = null;
            }
        }
        window.__liveBracketOverlay.started = true;
        await refreshOverlayStyle(true);
        await loadOverlay();
        let styleReloading = false;
        let overlayReloading = false;
        async function reloadStyle() {
            if (styleReloading)
                return;
            styleReloading = true;
            try {
                await refreshOverlayStyle(true);
            }
            catch (err) {
                console.error(err);
            }
            finally {
                styleReloading = false;
            }
        }
        async function reloadOverlay() {
            if (overlayReloading)
                return;
            overlayReloading = true;
            try {
                await loadOverlay();
                applyOverlayCustomStyle();
            }
            catch (err) {
                console.error(err);
            }
            finally {
                overlayReloading = false;
            }
        }
        // cPanel/shared hosting does not handle thousands of long-lived SSE connections well.
        // Default to polling the cached public snapshot so scores update without a page refresh
        // while keeping MySQL protected. Use ?live=sse only on infrastructure sized for SSE.
        if (leagueId && refreshMs > 0) {
            const pollId = setInterval(reloadOverlay, refreshMs);
            window.__liveBracketOverlay.intervals.push(pollId);
        }
        if (!leagueId || !window.EventSource || liveMode !== "sse")
            return;
        const events = new EventSource(`/api/public/leagues/${encodeURIComponent(leagueId)}/events`);
        window.__liveBracketOverlay.eventSource = events;
        events.addEventListener("overlay-style-updated", () => { currentSnapshot = null; reloadStyle(); });
        events.addEventListener("match-updated", reloadOverlay);
        events.addEventListener("league-updated", async () => {
            currentSnapshot = null;
            await reloadStyle();
            await reloadOverlay();
        });
        events.onerror = (err) => {
            console.warn("Live overlay event stream disconnected; polling fallback remains active.", err);
        };
    }
    startOverlay().catch((err) => {
        console.error(err);
        bracketStage.innerHTML = `<div class="empty-card">Could not load bracket.</div>`;
    });
})();
//# sourceMappingURL=live-bracket.js.map
